import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCheckbox,
  MDBCol,
  MDBFile,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBRadio,
  MDBRow,
  MDBSpinner,
  MDBSwitch,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
  MDBTextArea,
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UserContext } from '../../../App';
import { captureError } from '../../../utils/sentryHelpers';
import { transformMoney } from '../../../utils/money';
import { getLocalizedText } from '../../../utils/localizedText';
import useDebounce from '../../../utils/debounce';
import {
  BOOTH_PRODUCT_TYPES,
  BOOTH_STATUS,
  boothMatchRuleResult,
  compareBoothNumbers,
  groupAreasByVariant,
  isValidVariantCode,
  matchAreasToProducts,
  normalizeBoothNumber,
  normalizeVariant,
  parseAreasFromJson,
  parsePoints,
  polygonBounds,
  polygonCentroid,
  variantsOf,
} from '../../../utils/boothMap';
import { getBoothAssetUrl } from '../../../utils/boothAssets';
import {
  assignmentMatrix,
  featureRuleLabel,
  featureRuleRows,
  numberRange,
  retargetTemplateNames,
  templateRetargetValues,
  templateSizeChange,
} from '../../../utils/boothOfferAdmin';
import {
  activationBlockers,
  canActivatePlan,
  isUnnamedBoothNumber,
  issueMessageKey,
  parseViewBoxString,
  sortIssues,
  systemStandsFromMap,
  unnamedCount,
} from '../../../utils/hallplanParser';
import { ConfirmModal, ErrorList, FlashToast } from '../../mediathek/MediathekShared';
import {
  useEventBoothPlan,
  errorMessage,
  boothRowPrice,
  isPerSqmProduct,
  isRuleOfferProduct,
  ruleObjectOrNull,
  checkBoothNumber,
  findBoothProductsByNumber,
  findBoothNumberConflicts,
  cleanBoothNumber,
  cleanVariantCode,
  cleanPositionType,
  cleanZone,
  featureKey,
  featureCombinations,
  featureValueText,
  STANDARD_POSITION_TYPES,
  findBoothProductsByVariant,
  toMatchProducts,
  parseAreaAssignmentList,
  areaAssignmentPreview,
  svgVariantChanges,
  BOOTH_SKU_PREFIX,
  BOOTH_NAME_PATTERN_AREA,
  BOOTH_NAME_PATTERN_NUMBER,
  BOOTH_NAME_PATTERN_VARIANT,
  HALL_PLAN_SVG_MAX_BYTES,
  blockerCodes,
  buildBoothProductNames,
  germanText,
  versionActivationState,
} from './hooks/useEventBoothPlan';
import '../../../components/booth-booking/booth-booking.scss';

/**
 * Admin event tab "Hall plan & booths": hall plan image + coordinate space (Event.boothPlan),
 * the hall plan versions (SVG import with check report, draft / activate / delete),
 * the plan areas (BoothPlanArea: import / export / manual), and the booth EventProducts of the
 * event with their live status from getEventBoothMap (booth number, price, stock, availability, options,
 * reservations, creating booth products from unassigned areas), plus the booth EventProducts whose
 * product has no booth number yet (listEventProducts; they are not on the map). Stands of a hall plan
 * report that have no booth product ("Only in the plan") can be turned into booth products from the report.
 *
 * Every section persists immediately (the page Save does not touch boothPlan).
 */

const FEATURE = 'booth-admin';

const STATUS_LABELS = {
  [BOOTH_STATUS.FREE]: 'Free',
  [BOOTH_STATUS.HELD_BY_YOU]: 'Held',
  [BOOTH_STATUS.HELD_BY_OTHER]: 'Held',
  [BOOTH_STATUS.RESERVED_FOR_YOU]: 'Option',
  [BOOTH_STATUS.RESERVED_FOR_OTHER]: 'Option',
  [BOOTH_STATUS.SOLD]: 'Sold',
  [BOOTH_STATUS.UNAVAILABLE]: 'Not available',
};

const HELD_STATUSES = [BOOTH_STATUS.HELD_BY_YOU, BOOTH_STATUS.HELD_BY_OTHER];
const RESERVED_STATUSES = [...HELD_STATUSES, BOOTH_STATUS.RESERVED_FOR_YOU, BOOTH_STATUS.RESERVED_FOR_OTHER];
// booths whose variant / bookable flag should only change after a confirmation
const AFFECTED_STATUSES = [BOOTH_STATUS.SOLD, ...RESERVED_STATUSES];

/** normalized booth number -> booth of the map (physical booths: single booths and booths of variants). */
const boothsByNumber = (boothMap) => {
  const out = new Map();
  (boothMap?.booths || []).forEach((booth) => {
    const key = normalizeBoothNumber(booth?.boothNumber);
    if (key && !out.has(key)) out.set(key, booth);
  });
  return out;
};

/** Booths of the list that are sold, held or optioned (statusByKey: boothsByNumber). */
const affectedBooths = (boothNumbers, statusByKey) => (boothNumbers || [])
  .filter((number) => AFFECTED_STATUSES.includes(statusByKey.get(normalizeBoothNumber(number))?.status));

/** Names of the events a booth product is used in (PRODUCTS_BY_TYPE selects eventProducts.event.name). */
const productEventNames = (product) => (product?.eventProducts?.items || [])
  .filter((item) => item && item.event)
  .map((item) => getLocalizedText(item.event.name))
  .filter(Boolean);

/**
 * t() options of a plural key: `count` plus the English singular / plural defaults. i18next looks up
 * key_one / key_other (Locize, DE and EN); in development the defaults are saved to / updated in the
 * reference language, so EN reads "1 offer" instead of "1 offers" (review 2, C-1).
 */
const plural = (count, one, other, values = {}) => ({
  ...values, count, defaultValue_one: one, defaultValue_other: other,
});

/** Warning of a template whose m² differ from the row's ("Create offers", review 2, N4): form row and result list. */
const SIZE_CHANGE_WARNING = 'Template size {{from}} m² differs from {{to}} m²: quantities equal to the size are scaled, check the others';

/** Comma list, shortened to `max` entries with '…'. */
const shortList = (list, max = 30) => (list.length > max ? `${list.slice(0, max).join(', ')}, …` : list.join(', '));

const formatNumber = (value, language) => (value === null || value === undefined || value === ''
  ? ''
  : Number(value).toLocaleString(language || 'de', { maximumFractionDigits: 2 }));

const positiveOrNull = (value) => {
  const n = value === null || value === undefined || value === '' ? NaN : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const formatDateTime = (iso, language) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(language || 'de', { dateStyle: 'short', timeStyle: 'short' });
};

const readFileText = (file) => (typeof file.text === 'function'
  ? file.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsText(file);
  }));

function StatusChip({ status }) {
  const { t } = useTranslation();
  return (
    <span className={`booth-status-chip booth-status-chip--${status}`}>
      {t(STATUS_LABELS[status] || 'Not available')}
    </span>
  );
}

function SimpleModal({ open, onClose, title, size = 'lg', children, footer, busy = false }) {
  return (
    <MDBModal open={open} onClose={busy ? undefined : onClose} staticBackdrop tabIndex="-1">
      <MDBModalDialog size={size} scrollable centered>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>{title}</MDBModalTitle>
            <MDBBtn className="btn-close" color="none" type="button" onClick={onClose} disabled={busy} />
          </MDBModalHeader>
          <MDBModalBody>{children}</MDBModalBody>
          {footer && <MDBModalFooter>{footer}</MDBModalFooter>}
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

/** Plain (untranslated) messages, e.g. parser errors or server messages. */
function MessageList({ messages, color = 'danger' }) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  return (
    <div className={`alert alert-${color} small mb-3`} role="alert" style={{ maxHeight: '180px', overflowY: 'auto' }}>
      {messages.map((message, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index}>{message}</div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Area preview (SVG)
// ---------------------------------------------------------------------------

// placeholder areas of shapes without booth number ('#<n>'): light grey, dashed, not bookable
const UNNAMED_AREA_STYLE = { fill: 'rgba(173, 181, 189, 0.12)', stroke: '#adb5bd', strokeDasharray: '4 3' };

/** Badge shown instead of the booth number of a placeholder area. */
function UnnamedShapeBadge() {
  const { t } = useTranslation();
  return <MDBBadge color="secondary" light className="fw-normal">{t('unnamed shape')}</MDBBadge>;
}

function AreasPreview({ areas, assignedKeys, plan, planImageUrl }) {
  const { t } = useTranslation();
  const viewBox = useMemo(() => {
    const planBox = parseViewBoxString(plan?.viewBox);
    if (planBox) return { x: planBox[0], y: planBox[1], width: planBox[2], height: planBox[3] };
    if (plan?.width && plan?.height) return { x: 0, y: 0, width: plan.width, height: plan.height };
    if (!areas.length) return null;
    const bounds = areas.map((a) => polygonBounds(a.points));
    const minX = Math.min(...bounds.map((b) => b.minX));
    const minY = Math.min(...bounds.map((b) => b.minY));
    const maxX = Math.max(...bounds.map((b) => b.maxX));
    const maxY = Math.max(...bounds.map((b) => b.maxY));
    const pad = Math.max(maxX - minX, maxY - minY) * 0.03 || 10;
    return { x: minX - pad, y: minY - pad, width: maxX - minX + 2 * pad, height: maxY - minY + 2 * pad };
  }, [areas, plan]);

  if (!viewBox || (!areas.length && !planImageUrl)) return null;
  const fontSize = Math.max(6, Math.min(viewBox.width, viewBox.height) / 70);
  const showImage = planImageUrl && ((plan?.width && plan?.height) || plan?.viewBox);
  // the plan image covers the plan's coordinate space (viewBox of a hall plan version, else 0 0 width height)
  const planBox = parseViewBoxString(plan?.viewBox) || [0, 0, plan?.width, plan?.height];

  return (
    <div className="border rounded mb-3" style={{ background: 'var(--mdb-tertiary-bg, #f8f9fa)' }}>
      <svg
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: '360px' }}
        role="img"
        aria-label={t('Hall plan areas preview')}
      >
        {showImage && (
          <image href={planImageUrl} x={planBox[0]} y={planBox[1]} width={planBox[2]} height={planBox[3]} opacity={0.55} preserveAspectRatio="none" />
        )}
        {areas.map((area) => {
          if (isUnnamedBoothNumber(area.boothNumber)) {
            // placeholder of a shape without booth number: drawn muted, never assigned, no label
            return (
              <polygon
                key={area.id}
                points={area.points}
                fill={UNNAMED_AREA_STYLE.fill}
                stroke={UNNAMED_AREA_STYLE.stroke}
                strokeDasharray={UNNAMED_AREA_STYLE.strokeDasharray}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: 'default' }}
              >
                <title>{t('unnamed shape')}</title>
              </polygon>
            );
          }
          const assigned = assignedKeys.has(normalizeBoothNumber(area.boothNumber));
          const centroid = area.labelX !== null && area.labelX !== undefined && area.labelY !== null && area.labelY !== undefined
            ? { x: area.labelX, y: area.labelY }
            : polygonCentroid(area.points);
          return (
            <g key={area.id}>
              <polygon
                points={area.points}
                className={`booth-area${assigned ? ' booth-area--free' : ''}`}
                style={{ cursor: 'default' }}
              >
                <title>{area.boothNumber}</title>
              </polygon>
              <text x={centroid.x} y={centroid.y} className="booth-map__label" style={{ fontSize: `${area.labelSize || fontSize}px` }}>
                {area.boothNumber}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="d-flex gap-3 small px-3 py-2 border-top">
        <span><span className="booth-legend-swatch booth-legend-swatch--free me-1" />{t('Assigned to a booth')}</span>
        <span><span className="booth-legend-swatch me-1" />{t('No booth in this event')}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Hall plan image
// ---------------------------------------------------------------------------

function PlanImageCard({
  plan, planImageUrl, isLoading, loadError, areasCount, onReadDims, onUpload, onRemove, onSaveDims, busy,
}) {
  const { t, i18n } = useTranslation();
  const fileRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  // new plan file whose coordinate space differs from the stored one while areas exist
  const [pendingUpload, setPendingUpload] = useState(null);

  useEffect(() => {
    setWidth(plan?.width ? String(plan.width) : '');
    setHeight(plan?.height ? String(plan.height) : '');
  }, [plan?.width, plan?.height]);

  const resetFileInput = () => {
    if (fileRef.current) fileRef.current.value = '';
  };

  const startUpload = async (file, options) => {
    setProgress(0);
    await onUpload(file, setProgress, options);
    setProgress(null);
    resetFileInput();
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const dims = await onReadDims(file);
    if (!dims) {
      resetFileInput();
      return;
    }
    const sizeDiffers = areasCount > 0 && plan?.width && plan?.height && dims.width && dims.height
      && (Math.abs(dims.width - plan.width) > 0.5 || Math.abs(dims.height - plan.height) > 0.5);
    if (sizeDiffers) {
      setPendingUpload({ file, dims });
      return;
    }
    await startUpload(file, { dims });
  };

  const resolvePendingUpload = async (keepDimensions) => {
    const pending = pendingUpload;
    setPendingUpload(null);
    if (!pending) return;
    await startUpload(pending.file, { dims: pending.dims, keepDimensions });
  };

  const cancelPendingUpload = () => {
    setPendingUpload(null);
    resetFileInput();
  };

  const dimsChanged = String(plan?.width || '') !== width || String(plan?.height || '') !== height;
  const dimsValid = Number(width) > 0 && Number(height) > 0;

  return (
    <MDBCard className="mb-4">
      <MDBCardBody>
        <MDBCardTitle>{t('Hall plan image')}</MDBCardTitle>
        {loadError && <MessageList messages={[loadError]} />}
        <MDBRow>
          <MDBCol md="6" className="mb-3">
            {isLoading && <MDBSpinner size="sm" />}
            {!isLoading && plan?.image && (
              <>
                {planImageUrl ? (
                  <img
                    src={planImageUrl}
                    alt={t('Hall plan')}
                    className="border rounded d-block mb-2"
                    style={{ maxHeight: '260px', maxWidth: '100%', objectFit: 'contain', background: '#fff' }}
                  />
                ) : (
                  <MDBSpinner size="sm" className="mb-2" />
                )}
                <div className="small text-muted">{plan.image.fileName}</div>
              </>
            )}
            {!isLoading && !plan?.image && <p className="text-muted">{t('No hall plan image uploaded yet.')}</p>}
            <div className="small mt-1">
              {plan?.width && plan?.height
                ? t('Coordinate space: {{width}} × {{height}}', {
                  width: formatNumber(plan.width, i18n.language),
                  height: formatNumber(plan.height, i18n.language),
                })
                : t('No coordinate space set.')}
            </div>
            {plan?.viewBox && (
              <div className="small text-muted">{t('viewBox of the active hall plan version: {{viewBox}}', { viewBox: plan.viewBox })}</div>
            )}
            {plan?.areasUpdatedAt && (
              <div className="small text-muted">
                {t('Areas last changed: {{date}}', { date: formatDateTime(plan.areasUpdatedAt, i18n.language) })}
              </div>
            )}
          </MDBCol>
          <MDBCol md="6">
            <MDBFile
              label={t('Upload hall plan (SVG, PNG or JPG)')}
              accept=".svg,.png,.jpg,.jpeg"
              onChange={handleFile}
              ref={fileRef}
              disabled={busy || progress !== null}
              className="mb-2"
            />
            {progress !== null && (
              <div className="small mb-3">
                <MDBSpinner size="sm" className="me-2" />
                {t('Uploading… {{percent}} %', { percent: progress })}
              </div>
            )}
            <div className="small text-muted mb-3">
              {t('The coordinate space of the areas is read from the SVG viewBox or the image size.')}
            </div>
            <MDBRow className="g-2 align-items-center mb-3">
              <MDBCol size="5">
                <MDBInput label={t('Width')} type="number" min="0" size="sm" value={width} onChange={(e) => setWidth(e.target.value)} />
              </MDBCol>
              <MDBCol size="5">
                <MDBInput label={t('Height')} type="number" min="0" size="sm" value={height} onChange={(e) => setHeight(e.target.value)} />
              </MDBCol>
              <MDBCol size="2">
                <MDBBtn
                  size="sm"
                  type="button"
                  title={t('Save coordinate space')}
                  aria-label={t('Save coordinate space')}
                  disabled={busy || !dimsChanged || !dimsValid}
                  onClick={() => onSaveDims(Number(width), Number(height))}
                >
                  <MDBIcon fas icon="save" />
                </MDBBtn>
              </MDBCol>
            </MDBRow>
            {plan?.image && (
              <MDBBtn color="danger" outline size="sm" type="button" disabled={busy} onClick={() => setConfirmRemove(true)}>
                <MDBIcon fas icon="trash" className="me-2" />
                {t('Remove hall plan')}
              </MDBBtn>
            )}
          </MDBCol>
        </MDBRow>
      </MDBCardBody>
      <ConfirmModal
        open={confirmRemove}
        title={t('Remove hall plan')}
        message={t('The hall plan image and its coordinate space are removed. The areas are kept.')}
        confirmLabel={t('Remove')}
        busy={busy}
        onClose={() => setConfirmRemove(false)}
        onConfirm={async () => {
          await onRemove();
          setConfirmRemove(false);
        }}
      />
      <SimpleModal
        open={Boolean(pendingUpload)}
        onClose={cancelPendingUpload}
        title={t('Coordinate space differs')}
        size="md"
        footer={(
          <>
            <MDBBtn color="secondary" type="button" onClick={cancelPendingUpload}>{t('Cancel')}</MDBBtn>
            <MDBBtn color="secondary" outline type="button" onClick={() => resolvePendingUpload(true)}>
              {t('Keep current coordinate space')}
            </MDBBtn>
            <MDBBtn color="warning" type="button" onClick={() => resolvePendingUpload(false)}>
              {t('Use new coordinate space')}
            </MDBBtn>
          </>
        )}
      >
        {pendingUpload && plan && (
          <div className="alert alert-warning small mb-0">
            {t('The new image has a different coordinate space ({{newWidth}} × {{newHeight}}) than the current hall plan ({{width}} × {{height}}). The {{count}} existing areas were drawn in the current coordinate space and will no longer line up with the plan if it changes.', {
              newWidth: formatNumber(pendingUpload.dims.width, i18n.language),
              newHeight: formatNumber(pendingUpload.dims.height, i18n.language),
              width: formatNumber(plan.width, i18n.language),
              height: formatNumber(plan.height, i18n.language),
              count: areasCount,
            })}
          </div>
        )}
      </SimpleModal>
    </MDBCard>
  );
}

// ---------------------------------------------------------------------------
// 2) Hall plan versions (SVG import)
// ---------------------------------------------------------------------------

// fill / stroke of a stand in the import preview (see classifyStands)
const STAND_STYLES = {
  matched: { fill: 'rgba(25, 135, 84, 0.35)', stroke: '#198754' },
  error: { fill: 'rgba(220, 53, 69, 0.45)', stroke: '#dc3545' },
  warning: { fill: 'rgba(255, 193, 7, 0.5)', stroke: '#b38600' },
  notInSystem: { fill: 'rgba(108, 117, 125, 0.4)', stroke: '#495057' },
  unnamed: UNNAMED_AREA_STYLE,
};
const HIGHLIGHT_COLOR = '#0d6efd';

const LEGEND = [
  ['matched', 'Booth of this event'],
  ['error', 'Error'],
  ['warning', 'Warning'],
  ['notInSystem', 'Not in the booking system'],
  ['unnamed', 'Without booth number'],
];

const EXPORT_CHECKLIST = [
  'Layers "staende" (bookable booths) and "hintergrund" (walls, rooms, furniture); optional "poi".',
  'Object name = booth number as printed on the plan, any scheme (e.g. A3.6, A-12, 1.234, H3/B12, Z3); not empty, not starting with "#".',
  'Exactly one closed shape per booth (rectangle, polygon or path with straight edges).',
  'No duplicate booth numbers and no shapes exported twice.',
  'No booth numbers as text in the background layer.',
  'Strongly recommended: data-size = area of the booth in m² (e.g. data-size="18").',
  'Optional plan code: data-variant (or data-offer) = offer code or alias of the offer in this event (e.g. data-variant="18B"), on every booth sold under that offer.',
  'Optional position type: data-position = row, corner, head or island (or a fair-specific key); without it data-open-sides (1-4) gives row / corner / head / island.',
  'Optional zone: data-zone on the booth or on a parent group (e.g. <g data-zone="V">) for zones or price areas.',
  'Export as SVG with presentation attributes (no CSS), fonts converted to outlines.',
  'Object IDs = layer names, responsive (viewBox only).',
  'File size below 10 MB.',
];

/** Booth numbers an issue refers to ('A1.1 / A1.2' for overlaps). */
const issueRefs = (issue) => (issue && issue.ref
  ? String(issue.ref).split(' / ').map((ref) => ref.trim()).filter(Boolean)
  : []);

const samePoint = (a, b) => Array.isArray(a) && Array.isArray(b)
  && Math.abs(Number(a[0]) - Number(b[0])) < 0.01 && Math.abs(Number(a[1]) - Number(b[1])) < 0.01;

/**
 * Colour class per stand key: unnamed (no booth number) > error > warning (except "not in system")
 * > matched (booth of this event) > notInSystem.
 */
function classifyStands(stands, issues, matchedIds) {
  const errorRefs = new Set();
  const warningRefs = new Set();
  (issues || []).forEach((issue) => {
    issueRefs(issue).forEach((ref) => {
      if (issue.level === 'error') errorRefs.add(ref);
      else if (issue.code !== 'not-in-system') warningRefs.add(ref);
    });
  });
  const classes = new Map();
  stands.forEach((stand) => {
    let cls = 'notInSystem';
    if (!stand.id) cls = 'unnamed';
    else if (errorRefs.has(stand.id)) cls = 'error';
    else if (warningRefs.has(stand.id)) cls = 'warning';
    else if (matchedIds.has(stand.id)) cls = 'matched';
    classes.set(stand.key, cls);
  });
  return classes;
}

/** Report model of a freshly analysed SVG (useEventBoothPlan.analyzeHallPlanFile). */
function reportFromAnalysis(analysis) {
  const { plan, match } = analysis;
  return {
    kind: 'new',
    analysis,
    version: null,
    fileName: analysis.fileName,
    viewBox: Array.isArray(plan.viewBox) && plan.viewBox.length === 4 ? plan.viewBox : null,
    stands: (plan.stands || []).map((stand, index) => ({
      key: `stand-${index}`,
      id: stand.id || null,
      points: (stand.points || []).map((p) => `${p[0]},${p[1]}`).join(' '),
      centroid: stand.centroid,
      labelSize: stand.labelSize,
      areaUnits: stand.areaUnits ?? null,
      bbox: stand.bbox ? { w: stand.bbox.w, h: stand.bbox.h } : null,
      // data-variant (plan code) / data-size / data-position / data-zone of the SVG (only when exported)
      variant: cleanVariantCode(stand.variant),
      size: positiveOrNull(stand.size),
      positionType: cleanPositionType(stand.positionType),
      zone: cleanZone(stand.zone),
      openSides: stand.openSides ?? null,
    })),
    issues: analysis.issues || [],
    counts: analysis.counts || { errors: 0, warnings: 0 },
    summary: plan.summary || {},
    match,
    unitsPerMeter: positiveOrNull(match?.unitsPerMeter),
    compared: Boolean(analysis.hasPrevious),
    previousFileName: analysis.previousVersion?.fileName || '',
    bookedIds: new Set((analysis.systemStands || []).filter((s) => s.hasBookings).map((s) => s.standNo)),
    backgroundSvg: plan.backgroundSvg || null,
    backgroundS3Path: null,
  };
}

/** Report model of a stored version (its issues / matchSummary + its BoothPlanArea rows). */
function reportFromVersion(version, versionAreas, boothMap) {
  const box = parseViewBoxString(version.viewBox)
    || (version.width && version.height ? [0, 0, version.width, version.height] : null);
  const match = version.matchSummary || null;
  return {
    kind: 'stored',
    analysis: null,
    version,
    fileName: version.fileName || '',
    viewBox: box,
    stands: (versionAreas || []).map((area) => {
      const hasLabel = area.labelX !== null && area.labelX !== undefined && area.labelY !== null && area.labelY !== undefined;
      const c = hasLabel ? null : polygonCentroid(area.points);
      // bbox is stored as "x y w h"
      const bbox = parseViewBoxString(area.bbox);
      return {
        key: area.id,
        // placeholder areas of shapes without booth number are drawn as unnamed (dashed grey)
        id: isUnnamedBoothNumber(area.boothNumber) ? null : area.boothNumber,
        points: area.points,
        centroid: hasLabel ? [area.labelX, area.labelY] : [c.x, c.y],
        labelSize: area.labelSize,
        areaUnits: area.areaUnits ?? null,
        bbox: bbox ? { w: bbox[2], h: bbox[3] } : null,
        variant: cleanVariantCode(area.variant),
        size: positiveOrNull(area.size),
        positionType: cleanPositionType(area.positionType),
        zone: cleanZone(area.zone),
        openSides: area.openSides ?? null,
      };
    }),
    issues: sortIssues(version.issues || []),
    counts: { errors: version.errorCount || 0, warnings: version.warningCount || 0 },
    summary: version.summary || {},
    match,
    unitsPerMeter: positiveOrNull(match?.unitsPerMeter ?? version.unitsPerMeter),
    compared: Boolean(match?.compared || match?.previousVersionId),
    previousFileName: match?.previousFileName || '',
    bookedIds: new Set((boothMap?.booths || []).filter((b) => b.status === BOOTH_STATUS.SOLD).map((b) => normalizeBoothNumber(b.boothNumber))),
    backgroundSvg: null,
    backgroundS3Path: version.backgroundFile?.s3Path || null,
  };
}

/**
 * Activation state of a report model: { canActivate, blockers, unnamed, codes }. Every error except
 * 'missing-id' blocks; shapes without a booth number only need a confirmation. A fresh analysis is judged
 * by its issues (parser + match), a stored version by versionActivationState (handles shortened issue lists).
 */
function reportActivation(report) {
  if (!report) {
    return {
      canActivate: false, blockers: [], unnamed: 0, codes: '',
    };
  }
  const state = report.kind === 'stored' && report.version
    ? versionActivationState(report.version)
    : {
      canActivate: canActivatePlan(report.issues),
      blockers: activationBlockers(report.issues),
      unnamed: unnamedCount(report.issues),
    };
  return { ...state, codes: blockerCodes(state.blockers) };
}

const round1 = (value) => Math.round(value * 10) / 10;

/**
 * Rows for the create-booths form from stands of a report (by booth number): m², width and depth are
 * estimated from the plan scale (units per metre; width = bbox width, depth = bbox height), null without a scale.
 */
function planStandRows(report, ids) {
  const upm = report.unitsPerMeter;
  const byId = new Map();
  report.stands.forEach((stand) => {
    if (stand.id && !byId.has(stand.id)) byId.set(stand.id, stand);
  });
  return ids.map((id) => {
    const stand = byId.get(id) || {};
    const units = Number(stand.areaUnits);
    const w = Number(stand.bbox?.w);
    const h = Number(stand.bbox?.h);
    return {
      key: `n:${id}`,
      boothNumber: id,
      area: upm && units > 0 ? round1(units / (upm * upm)) : null,
      width: upm && w > 0 ? round1(w / upm) : null,
      depth: upm && h > 0 ? round1(h / upm) : null,
    };
  });
}

/**
 * Variants of a report (stands grouped by data-variant): [{ variant, count, size, sizeFromSvg, sizes, width,
 * depth, boothNumbers }]. size = most common data-size, else estimated from the median stand and the scale;
 * width / depth = short / long side of the median stand (all stands are rectangles), null without a scale.
 */
function reportVariantGroups(report) {
  const upm = report.unitsPerMeter;
  const standAreas = (report.stands || [])
    .filter((stand) => stand.id)
    .map((stand) => ({ boothNumber: stand.id, variant: stand.variant, size: stand.size, stand }));
  return groupAreasByVariant(standAreas).map((group) => {
    const stands = group.areas.map((a) => a.stand)
      .sort((a, b) => (Number(a.areaUnits) || 0) - (Number(b.areaUnits) || 0));
    const median = stands[Math.floor((stands.length - 1) / 2)] || null;
    const w = Number(median?.bbox?.w);
    const h = Number(median?.bbox?.h);
    const units = Number(median?.areaUnits);
    const hasSides = upm && w > 0 && h > 0;
    return {
      variant: group.variant,
      count: group.count,
      size: group.size ?? (upm && units > 0 ? round1(units / (upm * upm)) : null),
      sizeFromSvg: group.size !== null,
      sizes: group.sizes,
      width: hasSides ? round1(Math.min(w, h) / upm) : null,
      depth: hasSides ? round1(Math.max(w, h) / upm) : null,
      boothNumbers: group.boothNumbers,
    };
  });
}

/** Rows of the create form for variant groups (reportVariantGroups / unassigned areas). */
const variantCreateRows = (groups) => groups.map((group) => ({
  key: `v:${group.variant}`,
  variant: group.variant,
  svgCode: group.variant,
  count: group.count,
  area: group.size,
  width: group.width,
  depth: group.depth,
}));

/**
 * The report re-matched against a (refreshed) booth map: stands of "Only in the plan" that now have a
 * booth product in this event (by number, or by variant code / alias) move to "matched", their
 * "not-in-system" / "variant-without-product" warnings are removed (counts adjusted). Everything else
 * (scale, diff, "Only in the booking system") is kept. For a fresh analysis the analysis is updated too,
 * so "Save as draft" stores the current state. Returns the same object when nothing changed.
 */
function rematchReport(report, boothMap) {
  if (!report?.match || !boothMap) return report;
  // physical booths ({ standNo }) and variant products ({ variant, aliases }) of the event
  const systemStands = systemStandsFromMap(boothMap);
  const systemKeys = new Set(systemStands.map((s) => s.standNo).filter(Boolean));
  // variant code / alias -> variant product entry ({ eventProductId, productId }), first wins as in matchWithSystem
  const variantEntries = new Map();
  systemStands.forEach((s) => {
    // variant product entries only (physical booths carry the variant of their area too)
    if (!s.variant || s.standNo) return;
    [s.variant, ...(Array.isArray(s.aliases) ? s.aliases : [])].forEach((code) => {
      const key = normalizeVariant(code);
      if (key && !variantEntries.has(key)) variantEntries.set(key, s);
    });
  });
  const variantKeys = new Set(variantEntries.keys());
  const variantById = new Map((report.stands || []).filter((s) => s.id).map((s) => [s.id, normalizeVariant(s.variant)]));
  const onlyInPlan = report.match.onlyInPlan || [];
  // plan ids are compared like matchWithSystem does (plan id against the normalized system number)
  const nowMatched = new Set(onlyInPlan.filter((id) => systemKeys.has(id) || variantKeys.has(variantById.get(id))));
  const missingVariants = report.match.variantsWithoutProduct || [];
  const resolvedVariants = missingVariants.filter((entry) => variantKeys.has(normalizeVariant(entry?.code)));
  if (nowMatched.size === 0 && resolvedVariants.length === 0) return report;
  const resolvedIds = (report.stands || [])
    .filter((s) => s.id && !onlyInPlan.includes(s.id) && resolvedVariants.some((entry) => normalizeVariant(entry.code) === variantById.get(s.id)))
    .map((s) => s.id);
  const match = {
    ...report.match,
    matched: [...new Set([...(report.match.matched || []), ...onlyInPlan.filter((id) => nowMatched.has(id)), ...resolvedIds])],
    onlyInPlan: onlyInPlan.filter((id) => !nowMatched.has(id)),
    variantsWithoutProduct: missingVariants.filter((entry) => !resolvedVariants.includes(entry)),
    // resolved codes now have a variant product: listed like matchWithSystem's matchedVariants
    matchedVariants: [
      ...(report.match.matchedVariants || []).filter((entry) => !resolvedVariants.some(
        (resolved) => normalizeVariant(resolved.code) === normalizeVariant(entry?.code),
      )),
      ...resolvedVariants.map((entry) => {
        const product = variantEntries.get(normalizeVariant(entry.code)) || {};
        return { ...entry, eventProductId: product.eventProductId ?? null, productId: product.productId ?? null };
      }),
    ].sort((a, b) => compareBoothNumbers(a.code, b.code)),
  };
  const isResolved = (issue) => issue && (
    (issue.code === 'not-in-system' && nowMatched.has(issue.ref))
    || (issue.code === 'variant-without-product' && variantKeys.has(normalizeVariant(issue.ref))));
  const removed = report.issues.filter(isResolved).length;
  const issues = report.issues.filter((issue) => !isResolved(issue));
  const counts = { errors: report.counts.errors, warnings: Math.max(0, (report.counts.warnings || 0) - removed) };
  return {
    ...report,
    match,
    issues,
    counts,
    rematched: true,
    analysis: report.analysis ? {
      ...report.analysis, match, issues, counts, systemStands,
    } : report.analysis,
  };
}

/** Report download (JSON) of a report model. */
function reportJson(report, eventId) {
  const { version } = report;
  return JSON.stringify({
    eventId,
    fileName: report.fileName,
    version: version ? {
      id: version.id,
      status: version.status,
      createdAt: version.createdAt,
      activatedAt: version.activatedAt,
      uploadedBy: version.uploadedBy,
      notes: version.notes,
    } : null,
    viewBox: report.viewBox,
    summary: report.summary,
    counts: report.counts,
    unitsPerMeter: report.match?.unitsPerMeter ?? null,
    match: report.match,
    issues: report.issues,
  }, null, 2);
}

function downloadTextFile(fileName, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const reportFileName = (report) => `hall-plan-report-${String(report.fileName || 'svg').replace(/\.svg$/i, '').replace(/[^A-Za-z0-9._-]+/g, '_')}.json`;

/** Variant issues carry an English key + params (hallplanParser.ISSUE_MESSAGE_KEYS); the parser port's own messages stay as stored. */
const issueText = (issue, t) => {
  const key = issueMessageKey(issue);
  return key ? t(key.key, key.params) : issue.message;
};

function IssueLevelBadge({ level }) {
  const { t } = useTranslation();
  return level === 'error'
    ? <MDBBadge color="danger">{t('Error')}</MDBBadge>
    : <MDBBadge color="warning" className="text-dark">{t('Warning')}</MDBBadge>;
}

function VersionStatusBadge({ status }) {
  const { t } = useTranslation();
  if (status === 'active') return <MDBBadge color="success">{t('Active')}</MDBBadge>;
  if (status === 'archived') return <MDBBadge color="secondary" light>{t('Archived')}</MDBBadge>;
  return <MDBBadge color="info" light>{t('Draft')}</MDBBadge>;
}

/** Background SVG as <img> (never inlined) with the stands drawn over it in the same viewBox. */
function HallPlanPreview({ report, backgroundUrl, selectedIssue }) {
  const { t } = useTranslation();
  const classes = useMemo(
    () => classifyStands(report.stands, report.issues, new Set(report.match?.matched || [])),
    [report],
  );
  if (!report.viewBox) return null;
  const [minX, minY, width, height] = report.viewBox;
  const refs = new Set(issueRefs(selectedIssue));
  const isHighlighted = (stand) => Boolean(selectedIssue) && (
    (stand.id && refs.has(stand.id)) || (!stand.id && selectedIssue.at && samePoint(stand.centroid, selectedIssue.at))
  );
  const fallbackLabelSize = Math.max(2, Math.min(width, height) / 90);
  const markerRadius = Math.max(width, height) / 60;
  const layerStyle = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' };

  return (
    <div className="border rounded mb-3" style={{ background: '#fff' }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${width} / ${height}`, maxHeight: '65vh' }}>
        {backgroundUrl && (
          <img
            src={backgroundUrl}
            alt={t('Hall plan background')}
            style={{ ...layerStyle, objectFit: 'contain', filter: 'grayscale(1)', opacity: 0.8 }}
          />
        )}
        <svg
          viewBox={`${minX} ${minY} ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={layerStyle}
          role="img"
          aria-label={t('Hall plan preview')}
        >
          {report.stands.map((stand) => {
            const style = STAND_STYLES[classes.get(stand.key)] || STAND_STYLES.notInSystem;
            const highlighted = isHighlighted(stand);
            return (
              <g key={stand.key}>
                <polygon
                  points={stand.points}
                  fill={style.fill}
                  stroke={highlighted ? HIGHLIGHT_COLOR : style.stroke}
                  strokeWidth={highlighted ? 3 : 1}
                  strokeDasharray={highlighted ? undefined : style.strokeDasharray}
                  vectorEffect="non-scaling-stroke"
                >
                  <title>{stand.id || t('Shape without booth number')}</title>
                </polygon>
                {stand.id && Array.isArray(stand.centroid) && (
                  <text
                    x={stand.centroid[0]}
                    y={stand.centroid[1]}
                    fontSize={stand.labelSize || fallbackLabelSize}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ pointerEvents: 'none', fill: '#1d1d1b' }}
                  >
                    {stand.id}
                  </text>
                )}
              </g>
            );
          })}
          {selectedIssue && Array.isArray(selectedIssue.at) && (
            <circle
              cx={selectedIssue.at[0]}
              cy={selectedIssue.at[1]}
              r={markerRadius}
              fill="none"
              stroke={HIGHLIGHT_COLOR}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      </div>
      <div className="d-flex flex-wrap gap-3 small px-3 py-2 border-top">
        {LEGEND.map(([cls, label]) => {
          const style = STAND_STYLES[cls];
          return (
            <span key={cls} className="d-inline-flex align-items-center">
              <span
                className="d-inline-block me-1"
                style={{
                  width: '14px',
                  height: '10px',
                  background: style.fill,
                  border: `1px ${style.strokeDasharray ? 'dashed' : 'solid'} ${style.stroke}`,
                }}
              />
              {t(label)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * "Only in the plan" part of the report: stands with a booth number that have no booth product in this
 * event, selectable to create booth products for them - also for a draft with errors. Nothing is
 * preselected (the admin picks the stands); stands with an error issue of the parser (open path, curve, ...)
 * are marked.
 */
function PlanOnlyStands({ report, disabled, onCreate }) {
  const { t, i18n } = useTranslation();
  // stands with a plan code are created per offer (ReportVariants), not per booth number
  const ids = useMemo(() => {
    const withVariant = new Set((report.stands || []).filter((s) => s.id && s.variant).map((s) => s.id));
    return (report.match?.onlyInPlan || []).filter((id) => !withVariant.has(id));
  }, [report]);
  const errorIds = useMemo(() => new Set((report.issues || [])
    .filter((issue) => issue && issue.level === 'error' && issue.ref)
    .map((issue) => issue.ref)), [report]);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    setSelected(new Set());
  }, [ids]);

  const estimates = useMemo(() => new Map(planStandRows(report, ids).map((row) => [row.boothNumber, row])), [report, ids]);

  if (!report.match) return null;
  const selectedIds = ids.filter((id) => selected.has(id));
  const allSelected = ids.length > 0 && selectedIds.length === ids.length;

  const toggle = (id, checked) => setSelected((prev) => {
    const next = new Set(prev);
    if (checked) next.add(id);
    else next.delete(id);
    return next;
  });

  return (
    <div className="mb-3">
      <h6>{t('Stands only in the plan ({{count}})', { count: ids.length })}</h6>
      {ids.length === 0 ? (
        <p className="small text-muted mb-0">{t('None.')}</p>
      ) : (
        <>
          <div className="small text-muted mb-2">
            {t('These stands have a booth number in the plan but no plan code and no booth product with this number in this event. Create single-booth products for them here (also possible for a draft with errors); existing booth products with the same number are linked instead.')}
          </div>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <MDBCheckbox
              id="hallplan-plan-only-all"
              label={t('Select all')}
              checked={allSelected}
              disabled={disabled}
              onChange={() => setSelected(allSelected ? new Set() : new Set(ids))}
            />
            {onCreate && (
              <MDBBtn
                size="sm"
                type="button"
                disabled={disabled || selectedIds.length === 0}
                onClick={() => onCreate(planStandRows(report, selectedIds))}
              >
                <MDBIcon fas icon="magic" className="me-2" />
                {t('Create booth products for selected stands')}
                {` (${selectedIds.length})`}
              </MDBBtn>
            )}
          </div>
          <div className="border rounded p-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
            <div className="d-flex flex-wrap" style={{ columnGap: '1.5rem', rowGap: '0.25rem' }}>
              {ids.map((id, index) => {
                const estimate = estimates.get(id);
                const label = estimate?.area ? `${id} · ≈ ${formatNumber(estimate.area, i18n.language)} m²` : id;
                return (
                  <span key={id} className="d-inline-flex align-items-center gap-1">
                    <MDBCheckbox
                      id={`hallplan-plan-only-${index}`}
                      label={label}
                      checked={selected.has(id)}
                      disabled={disabled}
                      onChange={(e) => toggle(id, e.target.checked)}
                    />
                    {errorIds.has(id) && (
                      <MDBBadge color="danger" light>{t('Has errors in the report')}</MDBBadge>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * "Offer codes in the plan" part of the report: stands grouped by plan code (SVG data-variant / data-offer)
 * with size, width / depth and the offer of this event carrying the code (offer code or alias). Codes without
 * an offer are selectable (nothing preselected) to create ONE offer per code (CreateBoothsForm with code rows).
 */
function ReportVariants({
  report, eventBoothProducts, disabled, onCreate,
}) {
  const { t, i18n } = useTranslation();
  const groups = useMemo(() => reportVariantGroups(report), [report]);
  const productsByCode = useMemo(() => variantsOf(toMatchProducts(eventBoothProducts)), [eventBoothProducts]);
  const eventProductById = useMemo(
    () => new Map((eventBoothProducts || []).map((ep) => [ep.id, ep])),
    [eventBoothProducts],
  );
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    setSelected(new Set());
  }, [groups]);

  if (groups.length === 0) return null;
  const selectable = groups.filter((group) => !productsByCode.has(group.variant));
  const selectedGroups = selectable.filter((group) => selected.has(group.variant));
  const allSelected = selectable.length > 0 && selectedGroups.length === selectable.length;
  const productLabel = (entry) => {
    const product = eventProductById.get(entry.eventProductId)?.product;
    return product ? [getLocalizedText(product.name), product.sku].filter(Boolean).join(' · ') : entry.productId;
  };

  const toggle = (code, checked) => setSelected((prev) => {
    const next = new Set(prev);
    if (checked) next.add(code);
    else next.delete(code);
    return next;
  });

  return (
    <div className="mb-3">
      <h6>{t('Offer codes in the plan ({{count}} codes)', { count: groups.length })}</h6>
      <div className="small text-muted mb-2">
        {t('Stands with the same plan code (SVG data-variant) are sold under ONE offer per code; the exhibitor picks the booth number on the hall plan. Codes without an offer in this event can be created here.')}
      </div>
      {onCreate && selectable.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
          <MDBCheckbox
            id="hallplan-variants-all"
            label={t('Select all')}
            checked={allSelected}
            disabled={disabled}
            onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.map((group) => group.variant)))}
          />
          <MDBBtn
            size="sm"
            type="button"
            disabled={disabled || selectedGroups.length === 0}
            onClick={() => onCreate(variantCreateRows(selectedGroups))}
          >
            <MDBIcon fas icon="magic" className="me-2" />
            {t('Create offers for selected codes')}
            {` (${selectedGroups.length})`}
          </MDBBtn>
        </div>
      )}
      <div className="border rounded" style={{ maxHeight: '320px', overflowY: 'auto' }}>
        <MDBTable small hover className="mb-0">
          <MDBTableHead>
            <tr>
              <th aria-label={t('Select')} />
              <th>{t('Plan code')}</th>
              <th>{t('Stands')}</th>
              <th>{t('m²')}</th>
              <th>{t('W × D (m)')}</th>
              <th>{t('Offer in this event')}</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {groups.map((group, index) => {
              const products = productsByCode.get(group.variant) || [];
              return (
                <tr key={group.variant}>
                  <td>
                    {products.length === 0 && (
                      <MDBCheckbox
                        id={`hallplan-variant-${index}`}
                        checked={selected.has(group.variant)}
                        disabled={disabled}
                        aria-label={group.variant}
                        onChange={(e) => toggle(group.variant, e.target.checked)}
                      />
                    )}
                  </td>
                  <td className="fw-bold text-nowrap">{group.variant}</td>
                  <td title={group.boothNumbers.join(', ')}>{group.count}</td>
                  <td className="text-nowrap">
                    {group.size !== null ? formatNumber(group.size, i18n.language) : '-'}
                    {group.size !== null && !group.sizeFromSvg && <span className="text-muted"> ≈</span>}
                    {group.sizes.length > 1 && (
                      <MDBBadge color="warning" light className="ms-1" title={group.sizes.join(' / ')}>{t('Different sizes')}</MDBBadge>
                    )}
                  </td>
                  <td className="small text-nowrap">
                    {group.width && group.depth
                      ? `${formatNumber(group.width, i18n.language)} × ${formatNumber(group.depth, i18n.language)}`
                      : '-'}
                  </td>
                  <td className="small">
                    {products.length === 0 && <MDBBadge color="secondary" light>{t('No offer')}</MDBBadge>}
                    {products.length > 0 && productLabel(products[0])}
                    {products.length > 1 && (
                      <MDBBadge color="danger" light className="ms-1" title={products.slice(1).map(productLabel).join(', ')}>
                        {t('{{count}} offers with this code', { count: products.length })}
                      </MDBBadge>
                    )}
                  </td>
                </tr>
              );
            })}
          </MDBTableBody>
        </MDBTable>
      </div>
    </div>
  );
}

/** Translated label of a position type: the standard keys translated, free keys as the plan delivers them. */
const POSITION_LABELS = {
  row: 'Row booth', corner: 'Corner booth', head: 'Head booth', island: 'Island booth',
};

/** Labels of area fields in change lists (re-read, carry-over). */
const FEATURE_FIELD_LABELS = {
  variant: 'Plan code',
  positionType: 'Position type',
  zone: 'Zone',
  openSides: 'Open sides',
  attributes: 'Further attributes',
  bookable: 'Bookable',
};
const positionLabel = (value, t) => {
  const clean = cleanPositionType(value);
  if (!clean) return '';
  return POSITION_LABELS[clean] ? t(POSITION_LABELS[clean]) : clean;
};

/** Short label of an offer (EventProduct of this event): offer code, else name, plus SKU. */
const offerLabelOf = (eventProduct) => {
  const product = eventProduct?.product;
  if (!product) return eventProduct?.id || '';
  return [cleanVariantCode(product.boothVariant) || cleanBoothNumber(product.boothNumber) || getLocalizedText(product.name), product.sku]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Offers of the stands of a report: normalized booth number -> [eventProductId, ...] (primary first).
 * The booth map's offers are used for the live version (booth.offers, else booth.eventProductId); otherwise the
 * stands are matched locally with the same function as the map (matchAreasToProducts: offers when available,
 * else matches).
 */
function reportOffersByNumber(report, boothMap, matchProducts, isLiveReport) {
  const out = new Map();
  if (isLiveReport && Array.isArray(boothMap?.booths)) {
    boothMap.booths.forEach((booth) => {
      const key = normalizeBoothNumber(booth?.boothNumber);
      if (!key || out.has(key)) return;
      const offers = Array.isArray(booth.offers) && booth.offers.length > 0
        ? booth.offers.map((offer) => offer.eventProductId).filter(Boolean)
        : [booth.eventProductId].filter(Boolean);
      out.set(key, offers);
    });
    return out;
  }
  const areas = (report.stands || []).filter((s) => s.id).map((s) => ({
    boothNumber: s.id, variant: s.variant, size: s.size, positionType: s.positionType, zone: s.zone, openSides: s.openSides,
  }));
  const result = matchAreasToProducts(areas, matchProducts);
  if (result.offers instanceof Map) {
    result.offers.forEach((list, key) => {
      out.set(key, (Array.isArray(list) ? list : []).map((offer) => offer?.eventProductId).filter(Boolean));
    });
  }
  result.matches.forEach((match, key) => {
    if (!out.has(key)) out.set(key, [match.eventProductId]);
  });
  return out;
}

/**
 * "Features in the plan" part of the report (booth offering model, Phase 1): the stands grouped by
 * size × position type × zone with their count, 2-3 example booth numbers and the offer(s) in this event,
 * plus the warnings of the offer matching that concern features (area-without-offer, ambiguous-offer-match).
 */
function ReportFeatures({
  report, eventBoothProducts, boothMap, isLiveReport, disabled = false, onCreate = null,
}) {
  const { t, i18n } = useTranslation();
  const combos = useMemo(() => featureCombinations(report.stands), [report]);
  // "Create offer from features": rows picked here become offers with a match rule (size, position type, zone)
  const ruleRowsByKey = useMemo(() => new Map(featureRuleRows(combos).map((row) => [row.key, row])), [combos]);
  const ruleRowOf = (combo) => ruleRowsByKey.get(`r:${String(combo.key).toLowerCase()}`) || null;
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [combos]);
  const matchProducts = useMemo(() => toMatchProducts(eventBoothProducts), [eventBoothProducts]);
  const offersByNumber = useMemo(
    () => reportOffersByNumber(report, boothMap, matchProducts, isLiveReport),
    [report, boothMap, matchProducts, isLiveReport],
  );
  const warnings = useMemo(() => {
    const areas = (report.stands || []).filter((s) => s.id).map((s) => ({
      boothNumber: s.id, variant: s.variant, size: s.size, positionType: s.positionType, zone: s.zone,
    }));
    return matchAreasToProducts(areas, matchProducts).warnings
      .filter((w) => w && (w.code === 'area-without-offer' || w.code === 'ambiguous-offer-match'));
  }, [report, matchProducts]);
  const eventProductById = useMemo(
    () => new Map((eventBoothProducts || []).map((ep) => [ep.id, ep])),
    [eventBoothProducts],
  );
  const hasPosition = (report.stands || []).some((s) => s.positionType);
  const hasZone = (report.stands || []).some((s) => s.zone);
  if (combos.length === 0 || (!hasPosition && !hasZone && combos.every((c) => c.size === null))) return null;

  const offersOf = (combo) => {
    const counts = new Map();
    combo.boothNumbers.forEach((number) => {
      const primary = (offersByNumber.get(normalizeBoothNumber(number)) || [])[0];
      const key = primary || '';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].map(([eventProductId, count]) => ({ eventProductId, count }));
  };
  const ambiguous = warnings.filter((w) => w.code === 'ambiguous-offer-match');
  const withoutOffer = warnings.filter((w) => w.code === 'area-without-offer');
  const selectedRows = combos.filter((combo) => selected.has(combo.key)).map(ruleRowOf).filter(Boolean);
  const toggle = (key, checked) => setSelected((prev) => {
    const next = new Set(prev);
    if (checked) next.add(key);
    else next.delete(key);
    return next;
  });

  return (
    <div className="mb-3">
      <h6>{t('Features in the plan ({{count}} combinations)', { count: combos.length })}</h6>
      <div className="small text-muted mb-2">
        {t('Stands grouped by size, position type (data-position) and zone (data-zone). The offer column shows which offer of this event each group is sold under (plan code first, then the match rules of the offers).')}
      </div>
      {!hasPosition && !hasZone && (
        <div className="small text-muted mb-2">{t('The plan has no data-position or data-zone attributes.')}</div>
      )}
      {onCreate && ruleRowsByKey.size > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
          <MDBBtn
            size="sm"
            type="button"
            outline
            disabled={disabled || selectedRows.length === 0}
            title={t('Creates one offer per selected row with a match rule (size, position type, zone) instead of an offer code.')}
            onClick={() => onCreate(selectedRows)}
          >
            <MDBIcon fas icon="filter" className="me-2" />
            {t('Create offer from features')}
            {` (${selectedRows.length})`}
          </MDBBtn>
          <span className="small text-muted">
            {t('A plan code of a stand still wins over the rule.')}
          </span>
        </div>
      )}
      {(ambiguous.length > 0 || withoutOffer.length > 0) && (
        <div className="alert alert-warning small">
          {withoutOffer.length > 0 && (
            <div>
              {t('Stands with features but without an offer in this event: {{list}}', {
                list: shortList(withoutOffer.map((w) => w.ref)),
              })}
            </div>
          )}
          {ambiguous.length > 0 && (
            <div>
              {t('Several offers match the same features with equal priority (the first one is used): {{list}}', {
                list: ambiguous.map((w) => `${w.ref}: ${(w.eventProductIds || []).map((id) => offerLabelOf(eventProductById.get(id)) || id).join(' / ')}`).join('; '),
              })}
            </div>
          )}
        </div>
      )}
      <div className="border rounded" style={{ maxHeight: '320px', overflowY: 'auto' }}>
        <MDBTable small hover className="mb-0">
          <MDBTableHead>
            <tr>
              {onCreate && <th aria-label={t('Select')} />}
              <th>{t('m²')}</th>
              <th>{t('Position type')}</th>
              <th>{t('Zone')}</th>
              <th>{t('Stands')}</th>
              <th>{t('Examples')}</th>
              <th>{t('Offer in this event')}</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {combos.map((combo, index) => (
              <tr key={combo.key}>
                {onCreate && (
                  <td>
                    {ruleRowOf(combo) && (
                      <MDBCheckbox
                        id={`hallplan-feature-${index}`}
                        checked={selected.has(combo.key)}
                        disabled={disabled}
                        aria-label={ruleRowOf(combo).label}
                        onChange={(e) => toggle(combo.key, e.target.checked)}
                      />
                    )}
                  </td>
                )}
                <td className="text-nowrap">{combo.size !== null ? formatNumber(combo.size, i18n.language) : '-'}</td>
                <td className="text-nowrap">{positionLabel(combo.positionType, t) || <span className="text-muted">-</span>}</td>
                <td className="text-nowrap">{combo.zone || <span className="text-muted">-</span>}</td>
                <td>{combo.count}</td>
                <td className="small text-muted" title={shortList(combo.boothNumbers, 60)}>{combo.boothNumbers.slice(0, 3).join(', ')}</td>
                <td className="small">
                  {offersOf(combo).map(({ eventProductId, count }) => (
                    <div key={eventProductId || '-'}>
                      {eventProductId
                        ? offerLabelOf(eventProductById.get(eventProductId)) || eventProductId
                        : <MDBBadge color="secondary" light>{t('No offer')}</MDBBadge>}
                      {count !== combo.count && <span className="text-muted">{` (${count})`}</span>}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </MDBTableBody>
        </MDBTable>
      </div>
    </div>
  );
}

/** Content of the report modal: summary, issues (click = highlight), diff, preview, system booths without shape. */
function HallPlanReport({
  report, backgroundUrl, onCreateBooths, createDisabled, eventBoothProducts, boothMap = null, activeVersionId = null,
}) {
  const { t, i18n } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [errorsOnly, setErrorsOnly] = useState(false);

  useEffect(() => {
    setSelectedIndex(null);
    setErrorsOnly(false);
  }, [report]);

  const { summary, counts, match } = report;
  const onlyInSystem = match?.onlyInSystem || [];
  const diff = match?.diff || { added: [], removed: [], changed: [] };
  const issueRows = report.issues
    .map((issue, index) => ({ issue, index }))
    .filter(({ issue }) => !errorsOnly || issue.level === 'error');
  const selectedIssue = selectedIndex !== null ? report.issues[selectedIndex] : null;
  const activation = useMemo(() => reportActivation(report), [report]);
  // report of the live version: "This version is live" instead of "can be activated" (review 2, C-4)
  const isLiveReport = report.kind === 'stored' && Boolean(report.version) && report.version.id === activeVersionId;

  const stats = [
    ['Shapes', summary.shapes],
    ['Stands', summary.stands],
    ['With booth number', summary.withId],
    ['Without booth number', activation.unnamed],
    ['Errors', counts.errors],
    ['Warnings', counts.warnings],
    ['Booths of this event', match ? (match.matched || []).length : null],
    ['Only in the plan', match ? (match.onlyInPlan || []).length : null],
    ['Only in the booking system', match ? onlyInSystem.length : null],
    ['Units per metre', match?.unitsPerMeter],
    ['Plan codes', Array.isArray(summary.variants) ? summary.variants.length : (summary.variants ?? null)],
    ['Stands with position type', (report.stands || []).filter((s) => s.id && s.positionType).length || null],
    ['Stands with zone', (report.stands || []).filter((s) => s.id && s.zone).length || null],
  ];

  return (
    <>
      <p className="small text-muted mb-2">
        {report.fileName}
        {report.viewBox && ` · viewBox ${report.viewBox.join(' ')}`}
      </p>
      {!activation.canActivate && (
        <div className="alert alert-danger small">
          {t('{{count}} errors: this version cannot be activated. Correct the Illustrator export and upload the SVG again.', { count: activation.blockers.length })}
          {activation.codes && (
            <div className="mt-1">{t('Blocking issues: {{codes}}', { codes: activation.codes })}</div>
          )}
        </div>
      )}
      {isLiveReport && (
        <div className={`alert small ${activation.unnamed > 0 ? 'alert-warning' : 'alert-success'}`}>
          {t('This version is live ({{errors}} errors, {{warnings}} warnings).', { errors: counts.errors, warnings: counts.warnings })}
          {activation.unnamed > 0 && (
            <div className="mt-1">
              {t('{{count}} shapes have no booth number: they are shown on the hall plan but stay unbookable.', { count: activation.unnamed })}
            </div>
          )}
        </div>
      )}
      {!isLiveReport && activation.canActivate && activation.unnamed > 0 && (
        <div className="alert alert-warning small">
          {t('{{count}} shapes have no booth number: they are shown on the hall plan but stay unbookable. The version can still be activated ({{warnings}} warnings).', {
            count: activation.unnamed,
            warnings: counts.warnings,
          })}
        </div>
      )}
      {!isLiveReport && activation.canActivate && activation.unnamed === 0 && (
        <div className="alert alert-success small">
          {t('No errors: this version can be activated ({{count}} warnings).', { count: counts.warnings })}
        </div>
      )}

      <MDBRow className="g-2 mb-3">
        {stats.map(([label, value]) => (
          <MDBCol key={label} size="6" md="4" lg="3">
            <div className="border rounded px-2 py-1 h-100">
              <div className="small text-muted">{t(label)}</div>
              <div className="fw-bold">{value === null || value === undefined || value === '' ? '-' : formatNumber(value, i18n.language)}</div>
            </div>
          </MDBCol>
        ))}
      </MDBRow>

      <HallPlanPreview report={report} backgroundUrl={backgroundUrl} selectedIssue={selectedIssue} />

      {report.rematched && (
        <div className="small text-muted mb-2">
          {t('The plan was compared with the current booths of this event again.')}
        </div>
      )}
      <ReportVariants
        report={report}
        eventBoothProducts={eventBoothProducts}
        disabled={createDisabled}
        onCreate={onCreateBooths}
      />
      <ReportFeatures
        report={report}
        eventBoothProducts={eventBoothProducts}
        boothMap={boothMap}
        isLiveReport={isLiveReport}
        disabled={createDisabled}
        onCreate={onCreateBooths}
      />
      <PlanOnlyStands report={report} disabled={createDisabled} onCreate={onCreateBooths} />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h6 className="mb-0">{t('Issues ({{count}})', { count: report.issues.length })}</h6>
        <MDBCheckbox
          id="hallplan-report-errors-only"
          label={t('Errors only')}
          checked={errorsOnly}
          onChange={(e) => setErrorsOnly(e.target.checked)}
        />
      </div>
      {report.issues.length === 0 && <p className="small text-muted">{t('No issues.')}</p>}
      {issueRows.length > 0 && (
        <>
          <div className="small text-muted mb-1">{t('Click an issue to highlight the stand in the preview.')}</div>
          <div className="mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <MDBTable small hover className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>{t('Level')}</th>
                  <th>{t('Code')}</th>
                  <th>{t('Booth')}</th>
                  <th>{t('Message')}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {issueRows.map(({ issue, index }) => (
                  <tr
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={selectedIndex === index ? 'table-active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
                  >
                    <td><IssueLevelBadge level={issue.level} /></td>
                    <td className="small text-nowrap"><code>{issue.code}</code></td>
                    <td className="small fw-bold text-nowrap">{issue.ref || '-'}</td>
                    <td className="small">{issueText(issue, t)}</td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          </div>
        </>
      )}

      <h6>{isLiveReport ? t('Changes compared with the version that was active before') : t('Changes compared with the active version')}</h6>
      {report.compared ? (
        <div className="small mb-3">
          <div className="mb-1">
            {t('Compared with {{fileName}}: {{added}} added, {{removed}} removed, {{changed}} changed.', {
              fileName: report.previousFileName || t('the active version'),
              added: diff.added.length,
              removed: diff.removed.length,
              changed: diff.changed.length,
            })}
          </div>
          {diff.added.length > 0 && <div><strong>{t('Added')}:</strong> {diff.added.join(', ')}</div>}
          {diff.removed.length > 0 && <div><strong>{t('Removed')}:</strong> {diff.removed.join(', ')}</div>}
          {diff.changed.length > 0 && <div><strong>{t('Changed')}:</strong> {diff.changed.join(', ')}</div>}
          {(diff.variantChanged || []).length > 0 && (
            <div><strong>{t('Plan code changed')}:</strong> {diff.variantChanged.join(', ')}</div>
          )}
        </div>
      ) : (
        <p className="small text-muted">
          {isLiveReport
            ? t('There was no active version to compare with when this version was uploaded.')
            : t('There was no active version to compare with.')}
        </p>
      )}

      <h6>{t('System booths without a shape in this plan')}</h6>
      {onlyInSystem.length === 0 ? (
        <p className="small text-muted mb-0">{match ? t('None.') : '-'}</p>
      ) : (
        <div className="d-flex flex-wrap gap-1">
          {onlyInSystem.map((boothNumber) => (report.bookedIds.has(boothNumber) ? (
            <MDBBadge key={boothNumber} color="danger" title={t('Booked')}>
              {boothNumber}
              {' · '}
              {t('Booked')}
            </MDBBadge>
          ) : (
            <MDBBadge key={boothNumber} color="secondary" light>{boothNumber}</MDBBadge>
          )))}
        </div>
      )}
    </>
  );
}

/**
 * "Re-read from the SVG" of a stored version: reads data-variant (plan code), data-size, data-position,
 * data-zone, data-open-sides and further data-* attributes of the stored source SVG
 * (useEventBoothPlan.previewVersionVariants), shows the differences per mode ("only fill empty" / "overwrite")
 * and writes the differing areas. For the live version, sold / held / optioned booths whose plan code changes
 * need a confirmation.
 */
function ReReadVariantsModal({
  version, isLive, statusByKey, runAction, actionError, onClose, loadPreview, onApply,
}) {
  const { t, i18n } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('fill');
  const [confirmAffected, setConfirmAffected] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setMode('fill');
    setConfirmAffected(false);
    setResult(null);
    if (!version) return undefined;
    setLoading(true);
    runAction('preview-version-variants', () => loadPreview(version), {
      context: { planVersionId: version.id },
      trackBusy: false,
    }).then((outcome) => {
      if (!cancelled && outcome) setPreview(outcome);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [version, loadPreview, runAction]);

  const changes = useMemo(() => (preview ? svgVariantChanges(preview.rows, mode) : []), [preview, mode]);
  const variantChanges = changes.filter((change) => 'variant' in change.update);
  const affected = isLive ? affectedBooths(variantChanges.map((change) => change.area.boothNumber), statusByKey) : [];
  const busy = loading || applying;
  const sizeText = (value) => (value !== null && value !== undefined ? `${formatNumber(value, i18n.language)} m²` : '');

  const handleApply = async () => {
    setApplying(true);
    const outcome = await onApply(version, changes, preview.variants);
    setApplying(false);
    if (outcome) setResult(outcome);
  };

  return (
    <SimpleModal
      open={Boolean(version)}
      onClose={onClose}
      title={t('Re-read from the SVG')}
      size="lg"
      busy={busy}
      footer={result ? (
        <MDBBtn type="button" onClick={onClose}>{t('Close')}</MDBBtn>
      ) : (
        <>
          <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
          <MDBBtn
            type="button"
            onClick={handleApply}
            disabled={busy || !preview || changes.length === 0 || (affected.length > 0 && !confirmAffected)}
          >
            {applying && <MDBSpinner size="sm" className="me-2" />}
            {t('Update {{count}} areas', plural(changes.length, 'Update {{count}} area', 'Update {{count}} areas'))}
          </MDBBtn>
        </>
      )}
    >
      {version && <p className="small text-muted mb-2">{version.fileName || version.id}</p>}
      {loading && <MDBSpinner size="sm" />}
      {actionError && !busy && !result && <MessageList messages={[actionError]} />}
      {result && (
        <>
          <div className={`alert small ${result.errors.length ? 'alert-warning' : 'alert-success'}`}>
            {t('{{count}} areas updated.', { count: result.updated })}
          </div>
          <MessageList messages={result.errors} />
        </>
      )}
      {preview && !result && (
        <>
          <p className="small">
            {t('The SVG has {{stands}} stands with a plan code ({{codes}} codes) and {{features}} stands with features (position type, zone, open sides); this version has {{areas}} named areas.', {
              stands: preview.standsWithVariant,
              codes: preview.variants.length,
              features: preview.standsWithFeatures || 0,
              areas: preview.rows.length,
            })}
          </p>
          {preview.standsWithVariant === 0 && !preview.standsWithFeatures && (
            <div className="alert alert-info small">{t('The SVG of this version has no data-variant, data-position or data-zone attributes.')}</div>
          )}
          {preview.missingInSvg.length > 0 && (
            <div className="alert alert-warning small">
              {t('Areas without a stand in the SVG (kept unchanged): {{list}}', { list: shortList(preview.missingInSvg) })}
            </div>
          )}
          <div className="d-flex flex-wrap gap-3 mb-2">
            <MDBRadio
              name="hallplan-reread-mode"
              id="hallplan-reread-fill"
              label={t('Only fill empty values')}
              checked={mode === 'fill'}
              disabled={busy}
              onChange={() => setMode('fill')}
            />
            <MDBRadio
              name="hallplan-reread-mode"
              id="hallplan-reread-overwrite"
              label={t('Overwrite differing values')}
              checked={mode === 'overwrite'}
              disabled={busy}
              onChange={() => setMode('overwrite')}
            />
          </div>
          <p className="small fw-bold mb-1">{t('{{count}} differences', { count: changes.length })}</p>
          {affected.length > 0 && (
            <div className="alert alert-danger small">
              {t('These booths are sold, held or optioned and change their plan code: {{list}}. Bookings and reservations keep their booth; the booth may move to another offer.', {
                list: shortList(affected),
              })}
              <MDBCheckbox
                id="hallplan-reread-confirm"
                className="mt-2"
                label={t('Change them anyway')}
                checked={confirmAffected}
                onChange={(e) => setConfirmAffected(e.target.checked)}
              />
            </div>
          )}
          {changes.length > 0 && (
            <div className="border rounded p-2 small" style={{ maxHeight: '260px', overflowY: 'auto' }}>
              {changes.slice(0, 300).map((change) => (
                <div key={change.area.id}>
                  <strong>{change.area.boothNumber}</strong>
                  {': '}
                  {'variant' in change.update && `${change.current || '-'} → ${change.next || '-'}`}
                  {'size' in change.update && `${'variant' in change.update ? ' · ' : ''}${sizeText(change.currentSize) || '-'} → ${sizeText(change.nextSize)}`}
                  {(change.features || []).map((feature, index) => (
                    <span key={feature.field}>
                      {(index > 0 || 'variant' in change.update || 'size' in change.update) ? ' · ' : ''}
                      {`${t(FEATURE_FIELD_LABELS[feature.field] || feature.field)}: ${featureValueText(feature.field, feature.current) || '-'} → ${featureValueText(feature.field, feature.next)}`}
                    </span>
                  ))}
                </div>
              ))}
              {changes.length > 300 && <div className="text-muted">…</div>}
            </div>
          )}
        </>
      )}
    </SimpleModal>
  );
}

/** Identity of a carry-over correction (one field of one area of the new version). */
const correctionId = (correction) => `${correction.areaId}:${correction.field}`;

/** Display value of a carry-over correction value (bookable: yes / no). */
const correctionValueText = (field, value, t) => {
  if (field === 'bookable') return value === false ? t('Not bookable') : t('Bookable');
  if (field === 'positionType') return positionLabel(value, t) || '-';
  return featureValueText(field, value) || '-';
};

/**
 * Activation of a hall plan version with the carry-over dialog (decision 14.10.2026 "Dialog mit Auswahl"):
 * the corrections of the live version that the new version lacks (booths taken off sale, hand-set plan codes,
 * position type / zone) are listed with checkboxes (default: all) and written onto the new version's areas
 * before it becomes live. Without a live version (or without corrections) it is a plain confirmation.
 */
function ActivateVersionModal({
  activation, hasLiveVersion = true, actionError, onChangeSelection, onClose, onConfirm,
}) {
  const { t } = useTranslation();
  const version = activation?.version || null;
  const state = useMemo(() => (version ? versionActivationState(version) : null), [version]);
  const corrections = activation?.corrections || [];
  const selected = activation?.selected || new Set();
  const busy = Boolean(activation?.loading || activation?.running);
  const allSelected = corrections.length > 0 && corrections.every((c) => selected.has(correctionId(c)));
  const selectedCount = corrections.filter((c) => selected.has(correctionId(c))).length;

  const toggle = (correction, checked) => {
    const next = new Set(selected);
    if (checked) next.add(correctionId(correction));
    else next.delete(correctionId(correction));
    onChangeSelection(next);
  };

  return (
    <SimpleModal
      open={Boolean(activation)}
      onClose={onClose}
      title={t('Activate hall plan version')}
      size={corrections.length > 0 ? 'lg' : 'md'}
      busy={busy}
      footer={(
        <>
          <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
          <MDBBtn color="success" type="button" onClick={onConfirm} disabled={busy}>
            {activation?.running && <MDBSpinner size="sm" className="me-2" />}
            {selectedCount > 0
              ? t('Activate and take over {{count}} corrections', plural(
                selectedCount,
                'Activate and take over {{count}} correction',
                'Activate and take over {{count}} corrections',
              ))
              : t('Activate')}
          </MDBBtn>
        </>
      )}
    >
      {version && (
        <>
          <p className="mb-2">
            {/* the sentence about archiving only when a version is live (review 2, C-4) */}
            {hasLiveVersion
              ? t('Activate {{fileName}}? The booking map switches to its background and areas immediately; the current version is archived.', {
                fileName: version.fileName || version.id,
              })
              : t('Activate {{fileName}}? The booking map switches to its background and areas immediately.', {
                fileName: version.fileName || version.id,
              })}
          </p>
          {state?.unnamed > 0 && (
            <p className="mb-2">
              {t('{{count}} shapes have no booth number and stay unbookable on the hall plan. Activate this version anyway?', {
                count: state.unnamed,
              })}
            </p>
          )}
          {actionError && !busy && <MessageList messages={[actionError]} />}
          {activation.loading && (
            <div className="small mb-2">
              <MDBSpinner size="sm" className="me-2" />
              {t('Comparing with the live version…')}
            </div>
          )}
          {!activation.loading && activation.previewFailed && (
            <div className="alert alert-warning small">
              {t('The corrections of the live version could not be read. The version can still be activated without taking them over.')}
            </div>
          )}
          {!activation.loading && activation.liveVersion && corrections.length === 0 && !activation.previewFailed && (
            <div className="small text-muted">
              {t('The live version has no corrections that this version would lose.')}
            </div>
          )}
          {!activation.loading && corrections.length > 0 && (
            <>
              <div className="alert alert-info small">
                {t('The live version {{fileName}} has {{count}} manual corrections that this version does not have (booths taken off sale, plan codes set by hand, position type or zone). Select the ones to take over:', {
                  fileName: activation.liveVersion?.fileName || activation.liveVersion?.id || '',
                  count: corrections.length,
                })}
              </div>
              <MDBCheckbox
                id="hallplan-carry-over-all"
                label={t('Select all')}
                checked={allSelected}
                disabled={busy}
                onChange={() => onChangeSelection(allSelected ? new Set() : new Set(corrections.map(correctionId)))}
              />
              <div className="border rounded mt-2" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                <MDBTable small hover className="mb-0">
                  <MDBTableHead>
                    <tr>
                      <th aria-label={t('Select')} />
                      <th>{t('Booth')}</th>
                      <th>{t('Field')}</th>
                      <th>{t('Live version')}</th>
                      <th>{t('This version')}</th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {corrections.map((correction) => (
                      <tr key={correctionId(correction)}>
                        <td>
                          <MDBCheckbox
                            id={`hallplan-carry-over-${correctionId(correction)}`}
                            checked={selected.has(correctionId(correction))}
                            disabled={busy}
                            aria-label={`${correction.boothNumber} ${t(FEATURE_FIELD_LABELS[correction.field] || correction.field)}`}
                            onChange={(e) => toggle(correction, e.target.checked)}
                          />
                        </td>
                        <td className="fw-bold text-nowrap">{correction.boothNumber}</td>
                        <td className="small">{t(FEATURE_FIELD_LABELS[correction.field] || correction.field)}</td>
                        <td className="small">{correctionValueText(correction.field, correction.next, t)}</td>
                        <td className="small text-muted">{correctionValueText(correction.field, correction.current, t)}</td>
                      </tr>
                    ))}
                  </MDBTableBody>
                </MDBTable>
              </div>
            </>
          )}
        </>
      )}
    </SimpleModal>
  );
}

function VersionsCard({
  eventId, versions, activeVersionId, liveAreas = null, boothMap, isLoading, loadError, busy, actionError, clearActionError, runAction,
  notify, uploadedBy, eventProductIds, productsLoading, eventBoothProducts, ...actions
}) {
  const { t, i18n } = useTranslation();
  // error / warning counts of the live version as its report shows them now (stored counts date from the upload;
  // e.g. "plan code without offer" warnings disappear once the offers exist) - review 2, C-4
  const liveCounts = useMemo(() => {
    const liveVersion = (versions || []).find((v) => v.id === activeVersionId) || null;
    // without the live areas (still loading) the stored counts are shown, labelled "at upload"
    if (!liveVersion || !boothMap || !Array.isArray(liveAreas) || liveAreas.length === 0) return null;
    return rematchReport(reportFromVersion(liveVersion, liveAreas, boothMap), boothMap).counts;
  }, [versions, activeVersionId, liveAreas, boothMap]);
  const fileRef = useRef(null);
  const [fileError, setFileError] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [report, setReport] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [saveProgress, setSaveProgress] = useState(null);
  const [saveResult, setSaveResult] = useState(null);
  // activation with carry-over dialog: { version, savedAreaCount, fromSave, loading, liveVersion, corrections, selected }
  const [activation, setActivation] = useState(null);
  // "Save and activate" of a report with shapes without booth number waits for this confirmation
  const [confirmUnnamedSave, setConfirmUnnamedSave] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  // rows of the inline "create booth products" form of the report (null = report view)
  const [createRows, setCreateRows] = useState(null);
  const [creating, setCreating] = useState(false);
  // version whose variants are re-read from its stored SVG
  const [rereadTarget, setRereadTarget] = useState(null);
  const statusByKey = useMemo(() => boothsByNumber(boothMap), [boothMap]);

  // preview background: object URL of the sanitized background SVG (new upload) or the signed URL of the stored file
  // (depends on the two sources only, so a re-matched report keeps its background)
  const reportBackgroundSvg = report?.backgroundSvg || null;
  const reportBackgroundS3Path = report?.backgroundS3Path || null;
  useEffect(() => {
    if (reportBackgroundSvg) {
      const url = URL.createObjectURL(new Blob([reportBackgroundSvg], { type: 'image/svg+xml' }));
      setBackgroundUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    let cancelled = false;
    setBackgroundUrl('');
    if (reportBackgroundS3Path) {
      getBoothAssetUrl(reportBackgroundS3Path)
        .then((url) => {
          if (!cancelled) setBackgroundUrl(url);
        })
        .catch((error) => {
          captureError(error, {
            feature: FEATURE,
            operation: 'get-hall-plan-version-background-url',
            consoleMessage: 'Error loading the background URL of a hall plan version',
            context: { eventId, s3Path: reportBackgroundS3Path },
          });
        });
    }
    return () => {
      cancelled = true;
    };
  }, [reportBackgroundSvg, reportBackgroundS3Path, eventId]);

  const saving = saveProgress !== null && !saveResult;
  const reportBusy = saving || creating;
  const reportActivationState = useMemo(() => reportActivation(report), [report]);

  /**
   * Opens the activation dialog of a version: reads the manual corrections of the live version that this
   * version would lose (useEventBoothPlan.previewCarryOver) for the carry-over selection (default: all).
   * fromSave: the version was just saved with "Save and activate" while another version is live; without
   * corrections it is activated right away.
   */
  const startActivation = async (version, { savedAreaCount = null, versionAreas = null, fromSave = false } = {}) => {
    clearActionError();
    setActivation({
      version, savedAreaCount, fromSave, loading: true, liveVersion: null, corrections: [], selected: new Set(), previewFailed: false,
    });
    const preview = await runAction('preview-carry-over', () => actions.previewCarryOver(version, { versionAreas }), {
      context: { planVersionId: version.id },
      trackBusy: false,
    });
    const corrections = preview?.corrections || [];
    if (fromSave && preview && corrections.length === 0) {
      // nothing to take over: activate like a plain "Save and activate"
      await confirmActivation({ version, savedAreaCount, fromSave, corrections: [], selected: new Set() });
      return;
    }
    setActivation((prev) => (prev && prev.version.id === version.id ? {
      ...prev,
      loading: false,
      previewFailed: !preview,
      liveVersion: preview?.liveVersion || null,
      corrections,
      selected: new Set(corrections.map(correctionId)),
    } : prev));
  };

  const confirmActivation = async (state = activation) => {
    if (!state) return;
    const carryOver = (state.corrections || []).filter((c) => state.selected.has(correctionId(c)));
    setActivation((prev) => (prev ? { ...prev, running: true } : prev));
    const outcome = await runAction(
      'activate-hall-plan-version',
      () => actions.activateVersion(state.version, { carryOver, savedAreaCount: state.savedAreaCount }),
      {
        success: carryOver.length > 0 ? 'Hall plan version activated; corrections taken over.' : 'Hall plan version activated.',
        context: { planVersionId: state.version.id, carryOver: carryOver.length, fromSave: Boolean(state.fromSave) },
      },
    );
    if (outcome === undefined) {
      // the dialog stays open with the error (actionError); the version stays a draft
      setActivation((prev) => (prev ? {
        ...prev, running: false, loading: false, corrections: state.corrections || [], selected: state.selected,
      } : { ...state, running: false, loading: false }));
      return;
    }
    if (state.fromSave) {
      setSaveResult((prev) => (prev ? { ...prev, activated: true, pendingActivation: null } : prev));
    }
    setActivation(null);
  };

  const closeActivation = () => {
    if (activation?.running) return;
    setActivation(null);
  };

  const closeReport = () => {
    if (reportBusy) return;
    setReport(null);
    setConfirmUnnamedSave(false);
    setSaveProgress(null);
    setSaveResult(null);
    setCreateRows(null);
  };

  const openCreateForm = (rows) => {
    clearActionError(); // the form shows actionError: start without a stale one
    // the admin picked these rows in the report: they start selected (the form itself preselects nothing)
    setCreateRows(rows.map((row) => ({ ...row, selected: true })));
  };

  const closeCreateForm = () => {
    if (creating) return;
    setCreateRows(null);
  };

  const createBoothsFromStands = async (options) => {
    const outcome = await runAction('create-booths-from-plan-stands', () => actions.createBoothProducts(options), {
      context: {
        count: options.rows.length,
        templateId: options.templateId,
        planVersionId: report?.version?.id || null,
        fileName: report?.fileName || null,
      },
    });
    // the report reflects the new booths (stands move from "Only in the plan" to the booths of this event)
    if (outcome?.boothMap) setReport((prev) => rematchReport(prev, outcome.boothMap));
    // the reads are eventually consistent: match once more with a map reloaded after a short delay
    // (the hook drops the delayed reload when the event changes or the tab unmounts: resolves to null)
    if (outcome && actions.reloadBoothsDelayed) {
      actions.reloadBoothsDelayed().then((delayedMap) => {
        if (delayedMap) setReport((prev) => rematchReport(prev, delayedMap));
      });
    }
    return outcome;
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setFileError(null);
    if (!/\.svg$/i.test(file.name || '')) {
      setFileError(t('Please choose an SVG file.'));
      return;
    }
    if (file.size > HALL_PLAN_SVG_MAX_BYTES) {
      setFileError(t('The file is larger than 10 MB.'));
      return;
    }
    clearActionError();
    setAnalyzing(true);
    const analysis = await runAction('analyze-hall-plan-svg', () => actions.analyzeHallPlanFile(file), {
      context: { fileName: file.name, size: file.size },
      trackBusy: false,
    });
    setAnalyzing(false);
    if (!analysis) return;
    setSaveProgress(null);
    setSaveResult(null);
    setCreateRows(null);
    setReport(reportFromAnalysis(analysis));
  };

  const openStoredReport = async (version) => {
    clearActionError();
    setOpeningId(version.id);
    const versionAreas = await runAction('load-hall-plan-version-areas', () => actions.loadVersionAreas(version.id), {
      context: { planVersionId: version.id },
      trackBusy: false,
    });
    setOpeningId(null);
    if (!versionAreas) return;
    setSaveProgress(null);
    setSaveResult(null);
    setCreateRows(null);
    // the stored match lists date from the upload: booths created since then count as matched
    setReport(rematchReport(reportFromVersion(version, versionAreas, boothMap), boothMap));
  };

  const handleSave = async (activate) => {
    if (!report?.analysis) return;
    clearActionError();
    setSaveProgress({ phase: 'upload', percent: 0 });
    const outcome = await runAction(
      activate ? 'save-activate-hall-plan-version' : 'save-hall-plan-version',
      () => actions.saveHallPlanVersion({
        analysis: report.analysis,
        uploadedBy,
        activate,
        onProgress: setSaveProgress,
      }),
      { context: { fileName: report.fileName, activate } },
    );
    if (!outcome) {
      setSaveProgress(null);
      return;
    }
    setSaveResult(outcome);
    if (outcome.pendingActivation) {
      // another version is live: carry-over dialog first (activates right away when there is nothing to take over)
      await startActivation(outcome.pendingActivation.version, {
        savedAreaCount: outcome.pendingActivation.savedAreaCount,
        versionAreas: outcome.pendingActivation.areas,
        fromSave: true,
      });
      return;
    }
    if (outcome.errors.length === 0) {
      notify(outcome.activated ? 'Hall plan version saved and activated.' : 'Hall plan version saved as draft.');
    }
  };

  const list = versions || [];
  const canSave = Boolean(report?.analysis && report.viewBox && report.analysis.plan?.backgroundSvg);

  let reportFooter = null;
  // the inline create form brings its own buttons
  if (report && !createRows) {
    const downloadButton = (
      <MDBBtn
        color="secondary"
        outline
        type="button"
        disabled={saving}
        onClick={() => downloadTextFile(reportFileName(report), reportJson(report, eventId), 'application/json')}
      >
        <MDBIcon fas icon="download" className="me-2" />
        {t('Download report (JSON)')}
      </MDBBtn>
    );
    reportFooter = report.kind === 'new' && !saveResult ? (
      <>
        {downloadButton}
        <MDBBtn color="secondary" type="button" onClick={closeReport} disabled={saving}>{t('Cancel')}</MDBBtn>
        <MDBBtn color="secondary" type="button" onClick={() => handleSave(false)} disabled={saving || busy || !canSave}>
          {saving && <MDBSpinner size="sm" className="me-2" />}
          {t('Save as draft')}
        </MDBBtn>
        <MDBBtn
          type="button"
          onClick={() => {
            // shapes without booth number stay unbookable: ask first
            if (reportActivationState.unnamed > 0) setConfirmUnnamedSave(true);
            else handleSave(true);
          }}
          disabled={saving || busy || !canSave || !reportActivationState.canActivate}
          title={reportActivationState.canActivate
            ? undefined
            : `${t('Versions with errors cannot be activated.')} ${t('Blocking issues: {{codes}}', { codes: reportActivationState.codes })}`}
        >
          {saving && <MDBSpinner size="sm" className="me-2" />}
          {t('Save and activate')}
        </MDBBtn>
      </>
    ) : (
      <>
        {downloadButton}
        <MDBBtn type="button" onClick={closeReport}>{t('Close')}</MDBBtn>
      </>
    );
  }

  let progressText = '';
  if (saving && saveProgress.phase === 'upload') {
    progressText = t('Uploading the SVG files… {{percent}} %', { percent: saveProgress.percent || 0 });
  } else if (saving && saveProgress.phase === 'areas') {
    progressText = t('Saving areas: {{done}} of {{total}}', { done: saveProgress.done, total: saveProgress.total });
  }

  return (
    <MDBCard className="mb-4">
      <MDBCardBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <MDBCardTitle className="mb-1">{t('Hall plan versions (SVG import)')}</MDBCardTitle>
            <div className="small text-muted">
              {t('Upload the Illustrator export (one SVG per hall plan version). The plan provides only booth numbers and shapes; size, price, status and exhibitor come from the booth products.')}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <MDBFile
              size="sm"
              label={t('Upload hall plan SVG (max. 10 MB)')}
              accept=".svg"
              ref={fileRef}
              onChange={handleFile}
              disabled={busy || analyzing}
            />
            {analyzing && (
              <span className="small">
                <MDBSpinner size="sm" className="me-2" />
                {t('Checking the SVG…')}
              </span>
            )}
            <MDBBtn
              size="sm"
              color="secondary"
              type="button"
              title={t('Refresh')}
              aria-label={t('Refresh')}
              onClick={() => actions.loadVersions()}
              disabled={isLoading}
            >
              {isLoading ? <MDBSpinner size="sm" /> : <MDBIcon fas icon="sync-alt" />}
            </MDBBtn>
          </div>
        </div>

        {loadError && <MessageList messages={[loadError]} />}
        {fileError && <MessageList messages={[fileError]} />}

        <details className="small mb-3">
          <summary className="mb-1">{t('Export checklist (Illustrator)')}</summary>
          <ul className="mb-0">
            {EXPORT_CHECKLIST.map((item) => <li key={item}>{t(item)}</li>)}
          </ul>
        </details>

        {isLoading && !versions && <MDBSpinner size="sm" />}
        {versions && list.length === 0 && <p className="small text-muted mb-0">{t('No hall plan versions uploaded yet.')}</p>}
        {list.length > 0 && (
          <div className="table-responsive">
            <MDBTable small hover align="middle" className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>{t('Uploaded')}</th>
                  <th>{t('File')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Stands / with number')}</th>
                  <th>{t('Errors / warnings')}</th>
                  <th>{t('Units per metre')}</th>
                  <th aria-label={t('Actions')} />
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {list.map((version) => {
                  const live = version.id === activeVersionId;
                  // the live version is Event.boothPlan.activeVersionId; a stale 'active' status is shown as archived
                  let status = version.status === 'active' ? 'archived' : version.status;
                  if (live) status = 'active';
                  const hasErrors = (version.errorCount || 0) > 0;
                  // 'missing-id' errors (shapes without booth number) do not block the activation
                  const versionState = versionActivationState(version);
                  const blocked = !versionState.canActivate;
                  return (
                    <tr key={version.id}>
                      <td className="small text-nowrap">{formatDateTime(version.createdAt, i18n.language)}</td>
                      <td>
                        <div className="small fw-bold">{version.fileName || '-'}</div>
                        {version.uploadedBy && <div className="small text-muted">{version.uploadedBy}</div>}
                        {version.notes && <div className="small text-warning">{version.notes}</div>}
                      </td>
                      <td>
                        <VersionStatusBadge status={status} />
                        {live && version.activatedAt && (
                          <div className="small text-muted">{formatDateTime(version.activatedAt, i18n.language)}</div>
                        )}
                      </td>
                      <td className="small">
                        {formatNumber(version.summary?.stands ?? version.standsCount, i18n.language) || '-'}
                        {' / '}
                        {formatNumber(version.summary?.withId, i18n.language) || '-'}
                      </td>
                      <td className="small">
                        {live && liveCounts ? (
                          <>
                            <span className={liveCounts.errors ? 'text-danger fw-bold' : ''}>{liveCounts.errors}</span>
                            {' / '}
                            <span className={liveCounts.warnings ? 'text-warning' : ''}>{liveCounts.warnings}</span>
                            {(liveCounts.errors !== (version.errorCount || 0) || liveCounts.warnings !== (version.warningCount || 0)) && (
                              <div className="text-muted">
                                {t('at upload: {{errors}} / {{warnings}}', { errors: version.errorCount || 0, warnings: version.warningCount || 0 })}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <span className={hasErrors ? 'text-danger fw-bold' : ''}>{version.errorCount || 0}</span>
                            {' / '}
                            <span className={version.warningCount ? 'text-warning' : ''}>{version.warningCount || 0}</span>
                            <div className="text-muted">{t('at upload')}</div>
                          </>
                        )}
                        {versionState.unnamed > 0 && (
                          <div className="text-muted">{t('{{count}} without booth number', { count: versionState.unnamed })}</div>
                        )}
                      </td>
                      <td className="small">{formatNumber(version.unitsPerMeter, i18n.language) || '-'}</td>
                      <td className="text-end text-nowrap">
                        <MDBBtn
                          color="link"
                          size="sm"
                          type="button"
                          title={t('Report')}
                          aria-label={t('Report')}
                          disabled={Boolean(openingId)}
                          onClick={() => openStoredReport(version)}
                        >
                          {openingId === version.id ? <MDBSpinner size="sm" /> : <MDBIcon fas icon="clipboard-list" />}
                        </MDBBtn>
                        {version.sourceFile?.s3Path && actions.previewVersionVariants && (
                          <MDBBtn
                            color="link"
                            size="sm"
                            type="button"
                            title={t('Re-read from the SVG')}
                            aria-label={t('Re-read from the SVG')}
                            disabled={busy}
                            onClick={() => {
                              clearActionError();
                              setRereadTarget(version);
                            }}
                          >
                            <MDBIcon fas icon="tags" />
                          </MDBBtn>
                        )}
                        <MDBBtn
                          color="link"
                          size="sm"
                          type="button"
                          title={t('Download report (JSON)')}
                          aria-label={t('Download report (JSON)')}
                          onClick={() => downloadTextFile(
                            reportFileName({ fileName: version.fileName }),
                            reportJson(reportFromVersion(version, [], boothMap), eventId),
                            'application/json',
                          )}
                        >
                          <MDBIcon fas icon="download" />
                        </MDBBtn>
                        {!live && (
                          <MDBBtn
                            color="link"
                            size="sm"
                            type="button"
                            className={blocked ? 'text-muted' : 'text-success'}
                            title={blocked
                              ? `${t('Versions with errors cannot be activated.')} ${t('Blocking issues: {{codes}}', { codes: blockerCodes(versionState.blockers) })}`
                              : t('Activate')}
                            aria-label={t('Activate')}
                            disabled={busy || blocked || Boolean(activation)}
                            onClick={() => startActivation(version)}
                          >
                            <MDBIcon fas icon="check-circle" />
                          </MDBBtn>
                        )}
                        {live && (
                          <MDBBtn
                            color="link"
                            size="sm"
                            type="button"
                            className="text-warning"
                            title={t('Deactivate (take the hall plan offline)')}
                            aria-label={t('Deactivate (take the hall plan offline)')}
                            disabled={busy}
                            onClick={() => {
                              clearActionError();
                              setDeactivateTarget(version);
                            }}
                          >
                            <MDBIcon fas icon="eye-slash" />
                          </MDBBtn>
                        )}
                        <MDBBtn
                          color="link"
                          size="sm"
                          type="button"
                          className="text-danger"
                          title={live ? t('Delete active version') : t('Delete version')}
                          aria-label={live ? t('Delete active version') : t('Delete version')}
                          disabled={busy}
                          onClick={() => {
                            clearActionError();
                            setDeleteTarget(version);
                          }}
                        >
                          <MDBIcon fas icon="trash" />
                        </MDBBtn>
                      </td>
                    </tr>
                  );
                })}
              </MDBTableBody>
            </MDBTable>
          </div>
        )}
      </MDBCardBody>

      <SimpleModal
        open={Boolean(report)}
        onClose={closeReport}
        title={report?.kind === 'new' ? t('Check hall plan SVG') : t('Hall plan version report')}
        size="xl"
        busy={reportBusy}
        footer={reportFooter}
      >
        {report && createRows && (
          <CreateBoothsForm
            active
            sourceRows={createRows}
            description={t('Creates one offer (booth product, not visible in the shop) for every selected plan code, covering all booths with this code, or one single-booth product per selected stand, and adds it to this event. The template of a row donates image, PDF and 3D model (copied), included package, description, invoice name, price model, buyer groups, category, industries and - when the row has none - its price.')}
            hint={report.unitsPerMeter
              ? t('m² and dimensions are estimated from the scale of the plan ({{unitsPerMeter}} units per metre). Check them before creating.', {
                unitsPerMeter: formatNumber(report.unitsPerMeter, i18n.language),
              })
              : t('The plan has no scale yet (no booth of this event with m² was matched): enter the m² per row where the name or the price needs them.')}
            eventProductIds={eventProductIds}
            loadBoothProducts={actions.loadBoothProducts}
            productsLoading={productsLoading}
            actionError={actionError}
            onCreate={createBoothsFromStands}
            onClose={closeCreateForm}
            onBusyChange={setCreating}
            renderFrame={({ body, footer }) => (
              <>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <h6 className="mb-0">{t('Create offers')}</h6>
                  <span className="small text-muted">{report.fileName}</span>
                </div>
                {body}
                <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">{footer}</div>
              </>
            )}
          />
        )}
        {report && !createRows && (
          <>
            {actionError && !saving && <MessageList messages={[actionError]} />}
            {report.kind === 'new' && !canSave && (
              <div className="alert alert-danger small">{t('The SVG could not be read, so it cannot be saved.')}</div>
            )}
            {saving && (
              <div className="small mb-3">
                <MDBSpinner size="sm" className="me-2" />
                {progressText}
              </div>
            )}
            {saveResult && (
              <>
                <div className={`alert small ${saveResult.errors.length ? 'alert-warning' : 'alert-success'}`}>
                  {t('Version saved: {{created}} of {{total}} areas written.', { created: saveResult.created, total: saveResult.total })}
                  {' '}
                  {saveResult.activated ? t('The version is active.') : t('The version is a draft.')}
                  {!saveResult.activated && saveResult.pendingActivation && !activation && (
                    <div className="mt-1">{t('It was not activated yet: activate it in the versions list (the corrections of the live version can be taken over there).')}</div>
                  )}
                </div>
                <MessageList messages={saveResult.errors} color="warning" />
              </>
            )}
            <HallPlanReport
              report={report}
              backgroundUrl={backgroundUrl}
              createDisabled={busy || saving}
              onCreateBooths={actions.createBoothProducts ? openCreateForm : null}
              eventBoothProducts={eventBoothProducts}
              boothMap={boothMap}
              activeVersionId={activeVersionId}
            />
          </>
        )}
      </SimpleModal>

      <ReReadVariantsModal
        version={rereadTarget}
        isLive={Boolean(rereadTarget) && rereadTarget.id === activeVersionId}
        statusByKey={statusByKey}
        runAction={runAction}
        actionError={actionError}
        onClose={() => setRereadTarget(null)}
        loadPreview={actions.previewVersionVariants}
        onApply={(version, changes, variants) => runAction(
          'apply-version-variants',
          () => actions.applyVersionVariants(version, changes, { variants }),
          { context: { planVersionId: version.id, count: changes.length } },
        )}
      />
      <ConfirmModal
        open={confirmUnnamedSave && Boolean(report)}
        title={t('Save and activate')}
        message={t('{{count}} shapes have no booth number and stay unbookable on the hall plan. Activate this version anyway?', {
          count: reportActivationState.unnamed,
        })}
        confirmLabel={t('Activate')}
        busy={saving}
        onClose={() => setConfirmUnnamedSave(false)}
        onConfirm={() => {
          setConfirmUnnamedSave(false);
          handleSave(true);
        }}
      />
      <ActivateVersionModal
        activation={activation}
        hasLiveVersion={Boolean(activeVersionId) && activeVersionId !== activation?.version?.id}
        actionError={actionError}
        onChangeSelection={(selected) => setActivation((prev) => (prev ? { ...prev, selected } : prev))}
        onClose={closeActivation}
        onConfirm={() => confirmActivation()}
      />
      <ConfirmModal
        open={Boolean(deactivateTarget)}
        title={t('Deactivate hall plan version')}
        message={deactivateTarget
          ? t('Take the hall plan version {{fileName}} offline? Exhibitors see no hall plan until another version is activated. The areas and files of the version are kept and it can be activated again.', { fileName: deactivateTarget.fileName || deactivateTarget.id })
          : ''}
        confirmLabel={t('Deactivate')}
        busy={busy}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={async () => {
          const version = deactivateTarget;
          await runAction('deactivate-hall-plan-version', () => actions.deactivateVersion(version), {
            success: 'Hall plan version deactivated.',
            context: { planVersionId: version.id },
          });
          setDeactivateTarget(null);
        }}
      />
      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={t('Delete version')}
        message={deleteTarget
          ? (deleteTarget.id === activeVersionId
            ? t('Delete the ACTIVE hall plan version {{fileName}} with its areas and files? The hall plan goes offline for exhibitors until another version is activated.', { fileName: deleteTarget.fileName || deleteTarget.id })
            : t('Delete the hall plan version {{fileName}} with its areas and files?', { fileName: deleteTarget.fileName || deleteTarget.id }))
          : ''}
        busy={busy}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          const version = deleteTarget;
          await runAction('delete-hall-plan-version', () => actions.deleteVersion(version), {
            success: 'Hall plan version deleted.',
            context: { planVersionId: version.id },
          });
          setDeleteTarget(null);
        }}
      />
    </MDBCard>
  );
}

// ---------------------------------------------------------------------------
// 3) Hall plan areas
// ---------------------------------------------------------------------------

function ImportAreasModal({ open, parsed, fileName, plan, boothKeys, activeVersionId, actionError, onClose, onImport }) {
  const { t, i18n } = useTranslation();
  const [replace, setReplace] = useState(true);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setReplace(true);
      setProgress(null);
      setResult(null);
    }
  }, [open]);

  if (!parsed) return null;
  const matched = parsed.areas.filter((a) => boothKeys.has(normalizeBoothNumber(a.boothNumber))).length;
  const busy = progress !== null && !result;
  const sizeDiffers = parsed.width && parsed.height && plan?.width && plan?.height
    && (Math.abs(parsed.width - plan.width) > 0.5 || Math.abs(parsed.height - plan.height) > 0.5);

  const handleImport = async () => {
    setProgress({ done: 0, total: parsed.areas.length });
    const outcome = await onImport(parsed, {
      replace,
      onProgress: (done, total) => setProgress({ done, total }),
    });
    if (outcome) setResult(outcome);
    else setProgress(null);
  };

  const footer = result ? (
    <MDBBtn type="button" onClick={onClose}>{t('Close')}</MDBBtn>
  ) : (
    <>
      <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
      <MDBBtn type="button" onClick={handleImport} disabled={busy || parsed.areas.length === 0}>
        {busy && <MDBSpinner size="sm" className="me-2" />}
        {t('Import {{count}} areas', { count: parsed.areas.length })}
      </MDBBtn>
    </>
  );

  return (
    <SimpleModal open={open} onClose={onClose} title={t('Import areas')} size="lg" footer={footer} busy={busy}>
      <p className="small text-muted mb-2">{fileName}</p>
      <p>
        {t('{{count}} areas found, {{matched}} of them match a booth of this event.', { count: parsed.areas.length, matched })}
      </p>
      {parsed.width && parsed.height && (
        <p className="small">
          {t('Coordinate space of the file: {{width}} × {{height}}', {
            width: formatNumber(parsed.width, i18n.language),
            height: formatNumber(parsed.height, i18n.language),
          })}
        </p>
      )}
      {sizeDiffers && (
        <div className="alert alert-warning small">
          {t('The coordinate space of the file differs from the hall plan ({{width}} × {{height}}). The areas may not line up with the plan image.', {
            width: formatNumber(plan.width, i18n.language),
            height: formatNumber(plan.height, i18n.language),
          })}
        </div>
      )}
      {activeVersionId && !result && (
        <div className="alert alert-warning small">
          {t('A hall plan version is active. The import writes legacy areas without a version and deactivates the version: the booking map then shows the imported areas.')}
        </div>
      )}
      {actionError && !busy && !result && <MessageList messages={[actionError]} />}
      <MessageList messages={parsed.errors} color="warning" />
      {!result && (
        <MDBCheckbox
          id="hallplan-import-replace"
          label={t('Replace existing areas')}
          checked={replace}
          disabled={busy}
          onChange={(e) => setReplace(e.target.checked)}
          className="mb-3"
        />
      )}
      {busy && (
        <div className="small mb-3">
          <MDBSpinner size="sm" className="me-2" />
          {t('{{done}} of {{total}}', { done: progress.done, total: progress.total })}
        </div>
      )}
      {result && (
        <>
          <div className="alert alert-success small">
            {t('Import finished: {{created}} created, {{updated}} updated, {{deleted}} deleted.', result)}
          </div>
          <MessageList messages={result.errors} />
        </>
      )}
      {!result && parsed.areas.length > 0 && (
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          <MDBTable small hover className="mb-0">
            <MDBTableHead>
              <tr>
                <th>{t('Booth number')}</th>
                <th>{t('Points')}</th>
                <th>{t('m²')}</th>
                <th>{t('Booth in this event')}</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {parsed.areas.map((area) => {
                const isMatched = boothKeys.has(normalizeBoothNumber(area.boothNumber));
                return (
                  <tr key={area.boothNumber}>
                    <td className="fw-bold">{area.boothNumber}</td>
                    <td>{parsePoints(area.points).length}</td>
                    <td>{formatNumber(area.area, i18n.language)}</td>
                    <td>
                      {isMatched
                        ? <MDBBadge color="success" light>{t('Yes')}</MDBBadge>
                        : <MDBBadge color="secondary" light>{t('No')}</MDBBadge>}
                    </td>
                  </tr>
                );
              })}
            </MDBTableBody>
          </MDBTable>
        </div>
      )}
    </SimpleModal>
  );
}

function AddAreaModal({ open, existingKeys, actionError, onClose, onCreate }) {
  const { t } = useTranslation();
  const [boothNumber, setBoothNumber] = useState('');
  const [points, setPoints] = useState('');
  const [validation, setValidation] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setBoothNumber('');
      setPoints('');
      setValidation([]);
      setBusy(false);
    }
  }, [open]);

  const handleSave = async () => {
    const problems = [];
    if (!boothNumber.trim()) problems.push('Please enter a booth number.');
    else if (isUnnamedBoothNumber(boothNumber)) problems.push('Booth numbers must not start with "#" (reserved for unnamed shapes).');
    else if (existingKeys.has(normalizeBoothNumber(boothNumber))) problems.push('An area with this booth number already exists.');
    if (parsePoints(points).length < 3) problems.push('Please enter at least 3 points as "x,y x,y x,y".');
    setValidation(problems);
    if (problems.length) return;
    setBusy(true);
    const ok = await onCreate({ boothNumber: boothNumber.trim(), points: points.trim().replace(/\s+/g, ' ') });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <SimpleModal
      open={open}
      onClose={onClose}
      title={t('Add area manually')}
      size="md"
      busy={busy}
      footer={(
        <>
          <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
          <MDBBtn type="button" onClick={handleSave} disabled={busy}>
            {busy && <MDBSpinner size="sm" className="me-2" />}
            {t('Save')}
          </MDBBtn>
        </>
      )}
    >
      <ErrorList errors={validation} />
      {actionError && !busy && <MessageList messages={[actionError]} />}
      <MDBInput label={t('Booth number')} value={boothNumber} onChange={(e) => setBoothNumber(e.target.value)} className="mb-3" />
      <MDBTextArea
        label={t('Points (x,y x,y …) in the hall plan coordinate space')}
        rows={4}
        value={points}
        onChange={(e) => setPoints(e.target.value)}
      />
    </SimpleModal>
  );
}

/**
 * "Assign from list": textarea in the Excel layout with a preview against the live areas (unknown numbers,
 * areas not in the list, codes with listed / found counts, the changed areas); sold / held / optioned booths
 * whose plan code changes need a confirmation. Two layouts (parseAreaAssignmentList):
 * - code<TAB or ;>count?<TAB or ;>N1, N2, … per line (one plan code per line), or
 * - a header row naming the columns, e.g. "Nummer<TAB>Code<TAB>Position<TAB>Zone" (one booth per line; the
 *   optional columns Position and Zone set the position type / zone, empty cells change nothing).
 * Writes only the areas whose plan code / features differ.
 */
function AssignVariantsModal({
  open, areas, statusByKey, actionError, onClose, onApply,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [confirmAffected, setConfirmAffected] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const debouncedText = useDebounce(text, 300);

  useEffect(() => {
    if (!open) return;
    setText('');
    setConfirmAffected(false);
    setApplying(false);
    setResult(null);
  }, [open]);

  const preview = useMemo(
    () => (debouncedText.trim() ? areaAssignmentPreview(parseAreaAssignmentList(debouncedText), areas) : null),
    [debouncedText, areas],
  );
  const affected = preview ? affectedBooths(preview.changes.map((change) => change.area.boothNumber), statusByKey) : [];
  const pending = text !== debouncedText;
  const updateCount = preview ? preview.updates.length : 0;

  const handleApply = async () => {
    setApplying(true);
    const outcome = await onApply(preview.updates);
    setApplying(false);
    if (outcome) setResult(outcome);
  };

  return (
    <SimpleModal
      open={open}
      onClose={onClose}
      title={t('Assign from list')}
      size="lg"
      busy={applying}
      footer={result ? (
        <MDBBtn type="button" onClick={onClose}>{t('Close')}</MDBBtn>
      ) : (
        <>
          <MDBBtn color="secondary" type="button" onClick={onClose} disabled={applying}>{t('Cancel')}</MDBBtn>
          <MDBBtn
            type="button"
            onClick={handleApply}
            disabled={applying || pending || !preview || updateCount === 0 || (affected.length > 0 && !confirmAffected)}
          >
            {applying && <MDBSpinner size="sm" className="me-2" />}
            {t('Update {{count}} areas', plural(updateCount, 'Update {{count}} area', 'Update {{count}} areas'))}
          </MDBBtn>
        </>
      )}
    >
      {actionError && !applying && !result && <MessageList messages={[actionError]} />}
      {result && (
        <>
          <div className={`alert small ${result.errors.length ? 'alert-warning' : 'alert-success'}`}>
            {t('{{count}} areas updated.', { count: result.updated })}
          </div>
          <MessageList messages={result.errors} />
        </>
      )}
      {!result && (
        <>
          <p className="small text-muted mb-1">
            {t('One plan code per line, copied from the Excel overview: code, optional count, then the booth numbers separated by commas (columns separated by tab or ";"). Only the listed areas of the live hall plan are changed.')}
          </p>
          <p className="small text-muted">
            {t('With position type and zone: start with a header row naming the columns, e.g. "Number, Code, Position, Zone" (one booth per line; Code, Position and Zone are optional, empty cells change nothing). Position: row, corner, head, island or a fair-specific key.')}
          </p>
          <MDBTextArea
            label={t('List')}
            rows={8}
            value={text}
            disabled={applying}
            onChange={(e) => setText(e.target.value)}
            className="mb-3 font-monospace"
          />
          {preview && (
            <>
              <MessageList messages={preview.errors} color="warning" />
              <p className="small fw-bold mb-2">
                {t('{{codes}} plan codes; {{changes}} areas get another plan code.', {
                  codes: preview.codes.length,
                  changes: preview.changes.length,
                })}
                {preview.hasFeatures && (
                  <>
                    {' '}
                    {t('{{count}} position type / zone changes.', { count: preview.featureChanges.length })}
                  </>
                )}
              </p>
              {preview.unknownNumbers.length > 0 && (
                <div className="alert alert-warning small">
                  {t('Booth numbers without an area on the hall plan (ignored): {{list}}', { list: shortList(preview.unknownNumbers) })}
                </div>
              )}
              {preview.notListed.length > 0 && (
                <div className="alert alert-secondary small">
                  {t('{{count}} areas are not in the list and keep their plan code: {{list}}', {
                    count: preview.notListed.length,
                    list: shortList(preview.notListed),
                  })}
                  {preview.notListedWithoutVariant.length > 0 && (
                    <div className="mt-1">
                      {t('{{count}} of them have no plan code.', { count: preview.notListedWithoutVariant.length })}
                    </div>
                  )}
                </div>
              )}
              {affected.length > 0 && (
                <div className="alert alert-danger small">
                  {t('These booths are sold, held or optioned and change their plan code: {{list}}. Bookings and reservations keep their booth; the booth may move to another offer.', {
                    list: shortList(affected),
                  })}
                  <MDBCheckbox
                    id="hallplan-assign-confirm"
                    className="mt-2"
                    label={t('Change them anyway')}
                    checked={confirmAffected}
                    onChange={(e) => setConfirmAffected(e.target.checked)}
                  />
                </div>
              )}
              {preview.codes.length > 0 && (
                <div className="mb-3" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <MDBTable small hover className="mb-0">
                    <MDBTableHead>
                      <tr>
                        <th>{t('Plan code')}</th>
                        <th>{t('Count column')}</th>
                        <th>{t('Listed numbers')}</th>
                        <th>{t('Found on the plan')}</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {preview.codes.map((entry) => (
                        <tr key={entry.code}>
                          <td className="fw-bold">{entry.code}</td>
                          <td>{entry.count ?? '-'}</td>
                          <td className={entry.count !== null && entry.count !== entry.listed ? 'text-warning fw-bold' : ''}>{entry.listed}</td>
                          <td className={entry.found !== entry.listed ? 'text-warning fw-bold' : ''}>{entry.found}</td>
                        </tr>
                      ))}
                    </MDBTableBody>
                  </MDBTable>
                </div>
              )}
              {preview.changes.length > 0 && (
                <div className="border rounded p-2 small" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {preview.changes.slice(0, 300).map((change) => (
                    <div key={change.area.id}>
                      <strong>{change.area.boothNumber}</strong>
                      {`: ${change.current || '-'} → ${change.next}`}
                    </div>
                  ))}
                  {preview.changes.length > 300 && <div className="text-muted">…</div>}
                </div>
              )}
              {preview.featureChanges.length > 0 && (
                <div className="border rounded p-2 small mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {preview.featureChanges.slice(0, 300).map((change) => (
                    <div key={`${change.area.id}:${change.field}`}>
                      <strong>{change.area.boothNumber}</strong>
                      {` · ${t(FEATURE_FIELD_LABELS[change.field] || change.field)}: ${change.current || '-'} → ${change.next}`}
                    </div>
                  ))}
                  {preview.featureChanges.length > 300 && <div className="text-muted">…</div>}
                </div>
              )}
            </>
          )}
        </>
      )}
    </SimpleModal>
  );
}

function AreaSourceBadge({ source }) {
  const { t } = useTranslation();
  if (source === 'svg') return <MDBBadge color="info" light>{t('SVG version')}</MDBBadge>;
  if (source === 'manual') return <MDBBadge color="warning" light>{t('Manual')}</MDBBadge>;
  if (source === 'import') return <MDBBadge color="secondary" light>{t('JSON import')}</MDBBadge>;
  return <span className="small text-muted">{source || '-'}</span>;
}

function AreasCard({
  eventId, areas, activeVersionId, isLoading, loadError, boothKeys, plan: boothPlan, planImageUrl, busy, actionError, clearActionError, runAction,
  boothMap, eventBoothProducts, ...actions
}) {
  const { t, i18n } = useTranslation();
  const fileRef = useRef(null);
  const [importState, setImportState] = useState({ open: false, parsed: null, fileName: '' });
  const [addOpen, setAddOpen] = useState(false);
  const [deleteArea, setDeleteArea] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  // '' = all areas, '-' = areas without plan code, else a normalized plan code
  const [variantFilter, setVariantFilter] = useState('');
  // '' = all, '-' = without, else featureKey of the position type / zone
  const [positionFilter, setPositionFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [groupByVariant, setGroupByVariant] = useState(false);
  // change of a sold / held / optioned booth that needs a confirmation: { message, run, resolve }
  const [pendingChange, setPendingChange] = useState(null);
  const [pendingBusy, setPendingBusy] = useState(false);

  const statusByKey = useMemo(() => boothsByNumber(boothMap), [boothMap]);
  const matchProducts = useMemo(() => toMatchProducts(eventBoothProducts), [eventBoothProducts]);
  // which booth product each area belongs to (number first, then variant / alias), computed locally so
  // a changed variant shows its product before the map is reloaded
  const areaMatches = useMemo(() => matchAreasToProducts(areas, matchProducts).matches, [areas, matchProducts]);
  const productsByCode = useMemo(() => variantsOf(matchProducts), [matchProducts]);
  const eventProductById = useMemo(
    () => new Map((eventBoothProducts || []).map((ep) => [ep.id, ep])),
    [eventBoothProducts],
  );
  const variantGroups = useMemo(() => groupAreasByVariant(areas), [areas]);
  const withoutVariantCount = areas.filter((a) => !isUnnamedBoothNumber(a.boothNumber) && !normalizeVariant(a.variant)).length;
  // distinct position types / zones of the named areas (first spelling wins) with counts
  const featureOptions = useMemo(() => {
    const collect = (field) => {
      const byKey = new Map();
      let without = 0;
      areas.forEach((area) => {
        if (isUnnamedBoothNumber(area.boothNumber)) return;
        const key = featureKey(area[field]);
        if (!key) {
          without += 1;
          return;
        }
        if (!byKey.has(key)) byKey.set(key, { key, label: String(area[field]).trim(), count: 0 });
        byKey.get(key).count += 1;
      });
      return { options: [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label, 'de', { numeric: true })), without };
    };
    return { position: collect('positionType'), zone: collect('zone') };
  }, [areas]);
  const hasFeatures = featureOptions.position.options.length > 0 || featureOptions.zone.options.length > 0;
  // suggestions of the position editor: the standard keys plus the keys of this plan
  const positionSuggestions = useMemo(() => [...new Set([
    ...STANDARD_POSITION_TYPES,
    ...featureOptions.position.options.map((option) => option.label),
  ])], [featureOptions]);
  const visibleAreas = useMemo(() => {
    const matchesFilter = (value, filter) => {
      if (!filter) return true;
      if (filter === '-') return !featureKey(value);
      return featureKey(value) === filter;
    };
    return areas.filter((a) => {
      if (variantFilter === '-' && normalizeVariant(a.variant)) return false;
      if (variantFilter && variantFilter !== '-' && normalizeVariant(a.variant) !== variantFilter) return false;
      return matchesFilter(a.positionType, positionFilter) && matchesFilter(a.zone, zoneFilter);
    });
  }, [areas, variantFilter, positionFilter, zoneFilter]);

  const isAffected = (area) => AFFECTED_STATUSES.includes(statusByKey.get(normalizeBoothNumber(area.boothNumber))?.status);

  /** Runs `run` directly, or after a confirmation when the booth of the area is sold / held / optioned. */
  const guarded = (area, message, run) => {
    if (!isAffected(area)) return run();
    return new Promise((resolve) => {
      setPendingChange({ message, run, resolve });
    });
  };

  const closePendingChange = () => {
    if (pendingBusy || !pendingChange) return;
    pendingChange.resolve(false);
    setPendingChange(null);
  };

  const confirmPendingChange = async () => {
    const pending = pendingChange;
    if (!pending) return;
    setPendingBusy(true);
    const ok = await pending.run();
    setPendingBusy(false);
    setPendingChange(null);
    pending.resolve(ok);
  };

  const saveVariant = (area, value) => {
    const variant = cleanVariantCode(value);
    if (variant && !isValidVariantCode(variant)) {
      setParseError(t('"{{code}}" is not a valid plan code (letters, digits, ".", "_" and "-", max. 16 characters).', { code: variant }));
      return Promise.resolve(false);
    }
    setParseError(null);
    const run = async () => {
      const outcome = await runAction('update-area-variant', () => actions.updateAreaVariant(area, variant), {
        success: 'Plan code saved.',
        context: { areaId: area.id, boothNumber: area.boothNumber, variant },
        trackBusy: false,
      });
      return outcome !== undefined;
    };
    return guarded(area, t('Booth {{boothNumber}} is sold, held or optioned. Bookings and reservations keep the booth; with another plan code it may belong to another offer. Change the plan code anyway?', {
      boothNumber: area.boothNumber,
    }), run);
  };

  /** Position type / zone of one area ('' removes it). Sold / held / optioned booths ask first (offer and price may change). */
  const saveFeature = (area, field, value) => {
    const next = field === 'positionType' ? cleanPositionType(value) : cleanZone(value);
    setParseError(null);
    const run = async () => {
      const outcome = await runAction('update-area-features', () => actions.updateAreaFeatures(area, { [field]: next }), {
        success: field === 'positionType' ? 'Position type saved.' : 'Zone saved.',
        context: {
          areaId: area.id, boothNumber: area.boothNumber, field, value: next,
        },
        trackBusy: false,
      });
      return outcome !== undefined;
    };
    return guarded(area, t('Booth {{boothNumber}} is sold, held or optioned. Bookings and reservations keep the booth and their price; with other features it may belong to another offer or price. Change it anyway?', {
      boothNumber: area.boothNumber,
    }), run);
  };

  const saveBookable = (area, bookable) => {
    const run = async () => {
      const outcome = await runAction('update-area-bookable', () => actions.updateAreaBookable(area, bookable), {
        success: bookable ? 'The booth is bookable again.' : 'The booth was taken off sale.',
        context: { areaId: area.id, boothNumber: area.boothNumber, bookable },
        trackBusy: false,
      });
      return outcome !== undefined;
    };
    if (bookable) return run();
    return guarded(area, t('Booth {{boothNumber}} is sold, held or optioned. Taking it off sale does not cancel bookings or reservations. Continue?', {
      boothNumber: area.boothNumber,
    }), run);
  };

  const productOf = (area) => {
    const matched = areaMatches.get(normalizeBoothNumber(area.boothNumber));
    if (!matched) return null;
    const product = eventProductById.get(matched.eventProductId)?.product || null;
    return { ...matched, product };
  };

  const existingKeys = useMemo(() => new Set(areas.map((a) => normalizeBoothNumber(a.boothNumber))), [areas]);
  const assignedCount = areas.filter((a) => boothKeys.has(normalizeBoothNumber(a.boothNumber))).length;
  // placeholder areas of shapes without booth number ('#<n>'): shown, never assigned, not actionable
  const unnamedAreasCount = areas.filter((a) => isUnnamedBoothNumber(a.boothNumber)).length;

  const handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setParseError(null);
    if (/\.svg$/i.test(file.name || '')) {
      setParseError(t('Hall plan SVGs are imported as a hall plan version: use the section "Hall plan versions (SVG import)".'));
      return;
    }
    try {
      const text = await readFileText(file);
      const fromFile = parseAreasFromJson(text);
      // '#…' is reserved for placeholder areas of unnamed shapes: such rows are listed as errors, not imported
      const reserved = fromFile.areas.filter((area) => isUnnamedBoothNumber(area.boothNumber));
      const parsed = reserved.length === 0 ? fromFile : {
        ...fromFile,
        areas: fromFile.areas.filter((area) => !isUnnamedBoothNumber(area.boothNumber)),
        errors: [
          ...fromFile.errors,
          ...reserved.map((area) => `${area.boothNumber}: ${t('Booth numbers must not start with "#" (reserved for unnamed shapes).')}`),
        ],
      };
      clearActionError(); // the modal shows actionError: start without a stale one
      setImportState({ open: true, parsed, fileName: file.name });
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'parse-area-import-file',
        consoleMessage: 'Error reading the area import file',
        context: { fileName: file.name },
      });
      setParseError(errorMessage(error));
    }
  };

  const handleExport = () => {
    const json = actions.buildAreasExport();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hall-plan-areas-${eventId}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <MDBCard className="mb-4">
      <MDBCardBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <MDBCardTitle className="mb-1">{t('Hall plan areas')}</MDBCardTitle>
            <div className="small text-muted">
              {isLoading
                ? <MDBSpinner size="sm" />
                : t('{{count}} areas, {{assigned}} assigned to a booth of this event', { count: areas.length - unnamedAreasCount, assigned: assignedCount })}
              {!isLoading && unnamedAreasCount > 0 && (
                <>
                  {' · '}
                  {t('{{count}} unnamed shapes (not bookable)', { count: unnamedAreasCount })}
                </>
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <MDBFile
              size="sm"
              label={t('Import legacy areas (JSON)')}
              accept=".json"
              ref={fileRef}
              onChange={handleImportFile}
              disabled={busy}
            />
            <MDBBtn size="sm" outline type="button" onClick={handleExport} disabled={busy || areas.length === 0}>
              <MDBIcon fas icon="download" className="me-2" />
              {t('Export areas (JSON)')}
            </MDBBtn>
            <MDBBtn
              size="sm"
              outline
              type="button"
              onClick={() => {
                clearActionError();
                setAddOpen(true);
              }}
              disabled={busy}
            >
              <MDBIcon fas icon="draw-polygon" className="me-2" />
              {t('Add area manually')}
            </MDBBtn>
            <MDBBtn
              size="sm"
              outline
              type="button"
              onClick={() => {
                clearActionError();
                setAssignOpen(true);
              }}
              disabled={busy || areas.length === 0}
            >
              <MDBIcon fas icon="tags" className="me-2" />
              {t('Assign from list')}
            </MDBBtn>
          </div>
        </div>

        {loadError && <MessageList messages={[loadError]} />}
        {parseError && <MessageList messages={[parseError]} />}

        <div className="alert alert-info small">
          {activeVersionId
            ? t('Shown are the areas of the active hall plan version. A JSON import writes legacy areas without a version and deactivates the version.')
            : t('Shown are the legacy areas (JSON import or manual). Hall plan SVGs (Illustrator export) are imported in the section "Hall plan versions (SVG import)".')}
        </div>

        <AreasPreview areas={areas} assignedKeys={boothKeys} plan={boothPlan} planImageUrl={planImageUrl} />

        {areas.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <div style={{ minWidth: '200px' }}>
              <select
                className="form-select form-select-sm"
                value={variantFilter}
                aria-label={t('Filter by plan code')}
                onChange={(e) => setVariantFilter(e.target.value)}
              >
                <option value="">{t('All plan codes')}</option>
                <option value="-">{t('Without plan code ({{count}})', { count: withoutVariantCount })}</option>
                {variantGroups.map((group) => (
                  <option key={group.variant} value={group.variant}>{`${group.variant} (${group.count})`}</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '180px' }}>
              <select
                className="form-select form-select-sm"
                value={positionFilter}
                aria-label={t('Filter by position type')}
                onChange={(e) => setPositionFilter(e.target.value)}
              >
                <option value="">{t('All position types')}</option>
                <option value="-">{t('Without position type ({{count}})', { count: featureOptions.position.without })}</option>
                {featureOptions.position.options.map((option) => (
                  <option key={option.key} value={option.key}>{`${positionLabel(option.label, t)} (${option.count})`}</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '160px' }}>
              <select
                className="form-select form-select-sm"
                value={zoneFilter}
                aria-label={t('Filter by zone')}
                onChange={(e) => setZoneFilter(e.target.value)}
              >
                <option value="">{t('All zones')}</option>
                <option value="-">{t('Without zone ({{count}})', { count: featureOptions.zone.without })}</option>
                {featureOptions.zone.options.map((option) => (
                  <option key={option.key} value={option.key}>{`${option.label} (${option.count})`}</option>
                ))}
              </select>
            </div>
            <MDBCheckbox
              id="hallplan-areas-group-variant"
              label={t('Group by plan code')}
              checked={groupByVariant}
              onChange={(e) => setGroupByVariant(e.target.checked)}
            />
            {variantGroups.length > 0 && (
              <span className="small text-muted">{t('{{count}} plan codes', { count: variantGroups.length })}</span>
            )}
            {!hasFeatures && (
              <span className="small text-muted">{t('No position types or zones on this plan yet.')}</span>
            )}
          </div>
        )}

        {groupByVariant && variantGroups.length > 0 && (
          <div className="mb-3" style={{ maxHeight: '260px', overflowY: 'auto' }}>
            <MDBTable small hover className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>{t('Plan code')}</th>
                  <th>{t('Areas')}</th>
                  <th>{t('m²')}</th>
                  <th>{t('Offer in this event')}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {variantGroups.map((group) => {
                  const products = productsByCode.get(group.variant) || [];
                  const product = products.length > 0 ? eventProductById.get(products[0].eventProductId)?.product : null;
                  return (
                    <tr key={group.variant} style={{ cursor: 'pointer' }} onClick={() => setVariantFilter(group.variant)}>
                      <td className="fw-bold">{group.variant}</td>
                      <td title={group.boothNumbers.join(', ')}>{group.count}</td>
                      <td>{group.sizes.map((size) => formatNumber(size, i18n.language)).join(' / ') || '-'}</td>
                      <td className="small">
                        {product
                          ? [getLocalizedText(product.name), product.sku].filter(Boolean).join(' · ')
                          : <MDBBadge color="secondary" light>{t('No offer')}</MDBBadge>}
                        {products.length > 1 && (
                          <MDBBadge color="danger" light className="ms-1">{t('{{count}} offers with this code', { count: products.length })}</MDBBadge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </MDBTableBody>
            </MDBTable>
          </div>
        )}

        {visibleAreas.length > 0 && (
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            <MDBTable small hover className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>{t('Booth number')}</th>
                  <th>{t('Plan code')}</th>
                  <th>{t('Position')}</th>
                  <th>{t('Zone')}</th>
                  <th>{t('Points')}</th>
                  <th>{t('Offer in this event')}</th>
                  <th>{t('Bookable')}</th>
                  <th>{t('Source')}</th>
                  <th>{t('Last changed')}</th>
                  <th aria-label={t('Actions')} />
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {visibleAreas.map((area) => {
                  const unnamed = isUnnamedBoothNumber(area.boothNumber);
                  const matched = unnamed ? null : productOf(area);
                  const assigned = !unnamed && (Boolean(matched) || boothKeys.has(normalizeBoothNumber(area.boothNumber)));
                  return (
                    <tr key={area.id} className={unnamed ? 'text-muted' : ''}>
                      <td className={unnamed ? '' : 'fw-bold'}>
                        {unnamed ? <UnnamedShapeBadge /> : area.boothNumber}
                      </td>
                      <td className="text-nowrap">
                        {!unnamed && (
                          <BoothNumberEditor
                            value={cleanVariantCode(area.variant) || ''}
                            disabled={busy}
                            editLabel={t('Edit plan code')}
                            inputLabel={t('Plan code')}
                            onSave={(value) => saveVariant(area, value)}
                          />
                        )}
                        {!unnamed && positiveOrNull(area.size) !== null && (
                          <div className="small text-muted">{`${formatNumber(area.size, i18n.language)} m²`}</div>
                        )}
                      </td>
                      <td className="text-nowrap">
                        {!unnamed && (
                          <BoothNumberEditor
                            value={cleanPositionType(area.positionType) || ''}
                            displayValue={positionLabel(area.positionType, t)}
                            disabled={busy}
                            editLabel={t('Edit position type')}
                            inputLabel={t('Position type')}
                            suggestions={positionSuggestions}
                            onSave={(value) => saveFeature(area, 'positionType', value)}
                          />
                        )}
                        {!unnamed && area.openSides ? (
                          <div className="small text-muted">{t('{{count}} open sides', { count: area.openSides })}</div>
                        ) : null}
                      </td>
                      <td className="text-nowrap">
                        {!unnamed && (
                          <BoothNumberEditor
                            value={cleanZone(area.zone) || ''}
                            disabled={busy}
                            editLabel={t('Edit zone')}
                            inputLabel={t('Zone')}
                            suggestions={featureOptions.zone.options.map((option) => option.label)}
                            onSave={(value) => saveFeature(area, 'zone', value)}
                          />
                        )}
                      </td>
                      <td>{parsePoints(area.points).length}</td>
                      <td>
                        {unnamed && <span className="small text-muted">{t('Not bookable')}</span>}
                        {!unnamed && (assigned
                          ? <MDBIcon fas icon="check-circle" className="text-success" title={t('Yes')} />
                          : <MDBIcon far icon="circle" className="text-muted" title={t('No')} />)}
                        {matched?.product && (
                          <div className="small text-muted">
                            {matched.via === 'number' && (matched.product.sku || '')}
                            {(matched.via === 'variant' || matched.via === 'code')
                              && t('Offer {{code}}', { code: matched.product.boothVariant || '' })}
                            {matched.via === 'rule'
                              && t('Rule: {{offer}}', { offer: matched.product.sku || getLocalizedText(matched.product.name) })}
                          </div>
                        )}
                      </td>
                      <td>
                        {!unnamed && (
                          <MDBSwitch
                            id={`hallplan-area-bookable-${area.id}`}
                            checked={area.bookable !== false}
                            disabled={busy}
                            aria-label={t('Bookable')}
                            onChange={(e) => saveBookable(area, e.target.checked)}
                          />
                        )}
                      </td>
                      <td><AreaSourceBadge source={area.source} /></td>
                      <td className="small text-muted">{formatDateTime(area.updatedAt, i18n.language)}</td>
                      <td className="text-end">
                        <MDBBtn
                          color="link"
                          size="sm"
                          type="button"
                          className="text-danger"
                          title={t('Delete area')}
                          aria-label={t('Delete area')}
                          disabled={busy}
                          onClick={() => setDeleteArea(area)}
                        >
                          <MDBIcon fas icon="trash" />
                        </MDBBtn>
                      </td>
                    </tr>
                  );
                })}
              </MDBTableBody>
            </MDBTable>
          </div>
        )}
      </MDBCardBody>

      <ImportAreasModal
        open={importState.open}
        parsed={importState.parsed}
        fileName={importState.fileName}
        plan={boothPlan}
        boothKeys={boothKeys}
        activeVersionId={activeVersionId}
        actionError={actionError}
        onClose={() => setImportState({ open: false, parsed: null, fileName: '' })}
        onImport={(parsed, options) => runAction('import-areas', () => actions.importAreas(parsed, options), {
          context: { count: parsed.areas.length, replace: options.replace },
        })}
      />
      <AddAreaModal
        open={addOpen}
        existingKeys={existingKeys}
        actionError={actionError}
        onClose={() => setAddOpen(false)}
        onCreate={async (input) => {
          const outcome = await runAction('create-area', () => actions.createArea(input), {
            success: 'Area saved.',
            context: { boothNumber: input.boothNumber },
          });
          return outcome !== undefined;
        }}
      />
      <AssignVariantsModal
        open={assignOpen}
        areas={areas}
        statusByKey={statusByKey}
        actionError={actionError}
        onClose={() => setAssignOpen(false)}
        onApply={(updates) => runAction(
          'assign-areas-from-list',
          () => actions.writeAreaVariants(updates, { live: true }),
          { context: { count: updates.length } },
        )}
      />
      <ConfirmModal
        open={Boolean(pendingChange)}
        title={t('Booth is sold or reserved')}
        message={pendingChange ? pendingChange.message : ''}
        confirmLabel={t('Change anyway')}
        busy={pendingBusy}
        onClose={closePendingChange}
        onConfirm={confirmPendingChange}
      />
      <ConfirmModal
        open={Boolean(deleteArea)}
        title={t('Delete area')}
        message={deleteArea
          ? t('Delete the area of booth {{boothNumber}}?', {
            boothNumber: isUnnamedBoothNumber(deleteArea.boothNumber) ? t('unnamed shape') : deleteArea.boothNumber,
          })
          : ''}
        busy={busy}
        onClose={() => setDeleteArea(null)}
        onConfirm={async () => {
          const area = deleteArea;
          await runAction('delete-area', () => actions.deleteArea(area.id), {
            success: 'Area deleted.',
            context: { areaId: area.id },
          });
          setDeleteArea(null);
        }}
      />
    </MDBCard>
  );
}

// ---------------------------------------------------------------------------
// 4) Booths in this event - modals
// ---------------------------------------------------------------------------

function CompanyOptionModal({ booth, actionError, onClose, searchCompanies, onCreate }) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  const debounced = useDebounce(term, 400);

  useEffect(() => {
    setTerm('');
    setResults([]);
    setCompany(null);
    setBusy(false);
  }, [booth]);

  useEffect(() => {
    let cancelled = false;
    if (!booth || debounced.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    setSearching(true);
    searchCompanies(debounced.trim())
      .then((items) => {
        if (!cancelled) setResults(items);
      })
      .catch((error) => {
        captureError(error, {
          feature: FEATURE,
          operation: 'search-companies',
          consoleMessage: 'Error searching companies',
          context: { term: debounced },
        });
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, booth, searchCompanies]);

  const handleCreate = async () => {
    setBusy(true);
    const ok = await onCreate(booth, company);
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <SimpleModal
      open={Boolean(booth)}
      onClose={onClose}
      title={booth ? t('Create option for booth {{boothNumber}}', { boothNumber: booth.boothNumber }) : ''}
      size="md"
      busy={busy}
      footer={(
        <>
          <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
          <MDBBtn type="button" onClick={handleCreate} disabled={busy || !company}>
            {busy && <MDBSpinner size="sm" className="me-2" />}
            {t('Create option')}
          </MDBBtn>
        </>
      )}
    >
      {actionError && !busy && <MessageList messages={[actionError]} />}
      <p className="small text-muted">
        {t('The booth is reserved for the company without an end date until the option is released. The company can then book it from the hall plan.')}
      </p>
      <div className="d-flex align-items-center gap-2 mb-2">
        <div className="flex-grow-1">
          <MDBInput label={t('Search for company')} value={term} onChange={(e) => setTerm(e.target.value)} />
        </div>
        {searching && <MDBSpinner size="sm" />}
      </div>
      {results.length > 0 && (
        <div className="list-group mb-3" style={{ maxHeight: '220px', overflowY: 'auto' }}>
          {results.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`list-group-item list-group-item-action${company?.id === item.id ? ' active' : ''}`}
              onClick={() => setCompany(item)}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}
      {company && (
        <div className="d-flex align-items-center">
          <MDBIcon fas icon="check-circle" className="text-success me-2" />
          <span>{t('Selected: {{name}}', { name: company.name })}</span>
        </div>
      )}
    </SimpleModal>
  );
}

/**
 * Reservations of a booth. Variant booths (booth.isVariantBooth) list only the rows of their booth number;
 * single booths every row of the EventProduct (also legacy rows without number). Order claims ('pending' with
 * an order) are read-only: they disappear when the booth is created for the order or the order is cancelled.
 */
function ReservationsModal({ booth, actionError, onClose, listReservations, onDelete }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!booth) return;
    setLoading(true);
    setLoadError(null);
    try {
      setItems(await listReservations(booth.eventProductId, { boothNumber: booth.isVariantBooth ? booth.boothNumber : null }));
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation: 'list-booth-reservations',
        consoleMessage: 'Error loading booth reservations',
        context: { eventProductId: booth.eventProductId, boothNumber: booth.boothNumber },
      });
      setLoadError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [booth, listReservations]);

  useEffect(() => {
    setItems([]);
    setPendingId(null);
    load();
  }, [load]);

  const handleDelete = async (reservation) => {
    setBusyId(reservation.id);
    const ok = await onDelete(booth, reservation);
    setBusyId(null);
    setPendingId(null);
    if (ok) load();
  };

  const now = Date.now();
  const isOrderClaim = (reservation) => reservation.status === 'pending' && Boolean(reservation.orderId);

  return (
    <SimpleModal
      open={Boolean(booth)}
      onClose={onClose}
      title={booth ? t('Reservations of booth {{boothNumber}}', { boothNumber: booth.boothNumber }) : ''}
      size="lg"
      busy={Boolean(busyId)}
      footer={<MDBBtn type="button" onClick={onClose} disabled={Boolean(busyId)}>{t('Close')}</MDBBtn>}
    >
      {loading && <MDBSpinner size="sm" />}
      {loadError && <MessageList messages={[loadError]} />}
      {actionError && !busyId && <MessageList messages={[actionError]} />}
      {!loading && !loadError && items.length === 0 && <p className="text-muted">{t('No reservations for this booth.')}</p>}
      {items.length > 0 && (
        <MDBTable small hover className="mb-0">
          <MDBTableHead>
            <tr>
              <th>{t('Company')}</th>
              <th>{t('Booth')}</th>
              <th>{t('Status')}</th>
              <th>{t('From')}</th>
              <th>{t('Until')}</th>
              <th aria-label={t('Actions')} />
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {items.map((reservation) => {
              const expired = reservation.reservationEndDate && new Date(reservation.reservationEndDate).getTime() <= now;
              const claim = isOrderClaim(reservation);
              return (
                <tr key={reservation.id} className={expired || reservation.status === 'expired' ? 'text-muted' : ''}>
                  <td>{reservation.company?.name || '-'}</td>
                  <td className="text-nowrap">{reservation.boothNumber || <span className="text-muted">{t('whole product')}</span>}</td>
                  <td>
                    {claim ? (
                      <MDBBadge
                        color="info"
                        light
                        title={t('Removed automatically after the booth is created, or by cancelling the order.')}
                      >
                        {t('Order claim')}
                      </MDBBadge>
                    ) : (reservation.status || '-')}
                  </td>
                  <td className="small">{formatDateTime(reservation.reservationDate, i18n.language)}</td>
                  <td className="small">{reservation.reservationEndDate ? formatDateTime(reservation.reservationEndDate, i18n.language) : t('No end date')}</td>
                  <td className="text-end text-nowrap">
                    {!claim && pendingId === reservation.id && (
                      <>
                        <MDBBtn size="sm" color="danger" type="button" className="me-1" disabled={Boolean(busyId)} onClick={() => handleDelete(reservation)}>
                          {busyId === reservation.id && <MDBSpinner size="sm" className="me-1" />}
                          {t('Release')}
                        </MDBBtn>
                        <MDBBtn size="sm" color="secondary" type="button" disabled={Boolean(busyId)} onClick={() => setPendingId(null)}>
                          {t('Cancel')}
                        </MDBBtn>
                      </>
                    )}
                    {!claim && pendingId !== reservation.id && (
                      <MDBBtn
                        color="link"
                        size="sm"
                        type="button"
                        className="text-danger"
                        title={t('Release reservation')}
                        aria-label={t('Release reservation')}
                        disabled={Boolean(busyId)}
                        onClick={() => setPendingId(reservation.id)}
                      >
                        <MDBIcon fas icon="unlock" />
                      </MDBBtn>
                    )}
                  </td>
                </tr>
              );
            })}
          </MDBTableBody>
        </MDBTable>
      )}
    </SimpleModal>
  );
}

/**
 * Adds existing booth products to the event: single booths (booth number) and variant products (variant code).
 * A variant product gets the number of live areas with its code as (informational) stock; a variant whose
 * code is already used by a product of this event cannot be selected.
 */
function AddProductsModal({
  open, onClose, eventProductIds, eventBoothProducts, areas, loadBoothProducts, productsLoading, productsError, actionError, onAdd,
}) {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected({});
    setResult(null);
    loadBoothProducts().then((list) => setProducts(list || []));
  }, [open, loadBoothProducts]);

  // live areas per normalized variant code (stock of a linked variant product)
  const areaCountByCode = useMemo(() => {
    const counts = new Map();
    groupAreasByVariant(areas).forEach((group) => counts.set(group.variant, group.count));
    return counts;
  }, [areas]);
  const usedCodes = useMemo(() => variantsOf(toMatchProducts(eventBoothProducts)), [eventBoothProducts]);
  const codesOf = (p) => [p.boothVariant, ...(Array.isArray(p.boothVariantAliases) ? p.boothVariantAliases : [])]
    .map(normalizeVariant)
    .filter(Boolean);
  const variantAreaCount = (p) => [...new Set(codesOf(p))].reduce((sum, code) => sum + (areaCountByCode.get(code) || 0), 0);
  const conflictOf = (p) => codesOf(p).find((code) => usedCodes.has(code)) || null;

  const candidates = useMemo(() => products.filter((p) => !eventProductIds.has(p.id)
    && (cleanBoothNumber(p.boothNumber) || cleanVariantCode(p.boothVariant) || isRuleOfferProduct(p))), [products, eventProductIds]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((p) => String(p.boothNumber || '').toLowerCase().includes(q)
      || String(p.boothVariant || '').toLowerCase().includes(q)
      || String(p.sku || '').toLowerCase().includes(q)
      || getLocalizedText(p.name).toLowerCase().includes(q));
  }, [candidates, query]);
  const isSelectable = (p) => !(isVariantCandidate(p) && conflictOf(p));
  const selectedProducts = candidates.filter((p) => selected[p.id] && isSelectable(p));
  const selectableVisible = visible.filter(isSelectable);
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((p) => selected[p.id]);

  const toggleAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      selectableVisible.forEach((p) => {
        next[p.id] = !allVisibleSelected;
      });
      return next;
    });
  };

  const handleAdd = async () => {
    setBusy(true);
    const outcome = await onAdd(selectedProducts);
    setBusy(false);
    if (outcome && outcome.errors.length === 0) {
      onClose();
      return;
    }
    if (outcome) setResult(outcome);
  };

  return (
    <SimpleModal
      open={open}
      onClose={onClose}
      title={t('Add existing booth products')}
      size="lg"
      busy={busy}
      footer={(
        <>
          <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
          <MDBBtn type="button" onClick={handleAdd} disabled={busy || selectedProducts.length === 0}>
            {busy && <MDBSpinner size="sm" className="me-2" />}
            {t('Add {{count}} booths', { count: selectedProducts.length })}
          </MDBBtn>
        </>
      )}
    >
      <p className="small text-muted">
        {t('Booth products with a booth number, an offer code or a match rule that are not part of this event yet. Each one is added with its product price; a single booth with stock 1, an offer with a code with the number of its booths on the hall plan (informational only).')}
      </p>
      {productsError && <MessageList messages={[productsError]} />}
      {actionError && !busy && <MessageList messages={[actionError]} />}
      {result && (
        <>
          <div className="alert alert-warning small">{t('{{count}} booths added.', { count: result.created })}</div>
          <MessageList messages={result.errors} />
        </>
      )}
      <MDBInput label={t('Search')} value={query} onChange={(e) => setQuery(e.target.value)} className="mb-3" />
      {productsLoading && <MDBSpinner size="sm" />}
      {!productsLoading && candidates.length === 0 && <p className="text-muted">{t('No booth products available.')}</p>}
      {visible.length > 0 && (
        <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
          <MDBTable small hover className="mb-0">
            <MDBTableHead>
              <tr>
                <th>
                  <MDBCheckbox id="hallplan-add-all" checked={allVisibleSelected} onChange={toggleAll} aria-label={t('Select all')} />
                </th>
                <th>{t('Booth number / offer code')}</th>
                <th>{t('Name')}</th>
                <th>{t('SKU')}</th>
                <th>{t('m²')}</th>
                <th>{t('Price')}</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {visible.map((product) => {
                const isVariant = isVariantCandidate(product);
                const conflict = isVariant ? conflictOf(product) : null;
                let codeCell = product.boothNumber;
                if (isVariant) {
                  codeCell = (
                    <>
                      {product.boothVariant}
                      <MDBBadge color="info" light className="ms-1">{t('Offer')}</MDBBadge>
                      <div className="small fw-normal text-muted">
                        {t('{{count}} booths on the hall plan', { count: variantAreaCount(product) })}
                      </div>
                      {conflict && (
                        <div className="small fw-normal text-danger">
                          {t('Offer code {{code}} is already used by a product of this event.', { code: conflict })}
                        </div>
                      )}
                    </>
                  );
                } else if (isRuleOfferProduct(product)) {
                  codeCell = <MDBBadge color="info" light>{t('Rule offer')}</MDBBadge>;
                }
                return (
                  <tr key={product.id} className={conflict ? 'text-muted' : ''}>
                    <td>
                      <MDBCheckbox
                        id={`hallplan-add-${product.id}`}
                        checked={Boolean(selected[product.id]) && !conflict}
                        disabled={Boolean(conflict)}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [product.id]: e.target.checked }))}
                        aria-label={product.boothNumber || product.boothVariant || product.sku}
                      />
                    </td>
                    <td className="fw-bold">{codeCell}</td>
                    <td>{getLocalizedText(product.name)}</td>
                    <td className="small text-muted">{product.sku}</td>
                    <td>{formatNumber(product.boothArea, i18n.language)}</td>
                    <td>{product.price !== null && product.price !== undefined ? transformMoney(product.price) : '-'}</td>
                  </tr>
                );
              })}
            </MDBTableBody>
          </MDBTable>
        </div>
      )}
    </SimpleModal>
  );
}

/** Variant product among the booth products (variant code without booth number: rule "number XOR variant"). */
function isVariantCandidate(product) {
  return Boolean(cleanVariantCode(product?.boothVariant)) && !cleanBoothNumber(product?.boothNumber);
}

/** Number rows of the create form from hall plan areas (m² / width / depth from the last JSON import of this session). */
const rowsFromAreas = (areas, areaMeta) => (areas || []).map((area) => {
  const meta = areaMeta[normalizeBoothNumber(area.boothNumber)] || {};
  return {
    key: `n:${normalizeBoothNumber(area.boothNumber)}`,
    boothNumber: area.boothNumber,
    area: meta.area ?? null,
    width: meta.width ?? null,
    depth: meta.depth ?? null,
  };
});

/**
 * Variant rows of the create form from unassigned areas with a variant code: one row per code with the booth
 * count, the m² (data-size; else estimated from the area and the scale) and width / depth (short / long side
 * of the median area's bounding box; null without a scale).
 */
const variantRowsFromAreas = (areas, unitsPerMeter) => variantCreateRows(groupAreasByVariant(areas).map((group) => {
  const upm = positiveOrNull(unitsPerMeter);
  const sorted = [...group.areas].sort((a, b) => (Number(a.areaUnits) || 0) - (Number(b.areaUnits) || 0));
  const median = sorted[Math.floor((sorted.length - 1) / 2)] || null;
  const bounds = median && median.points ? polygonBounds(median.points) : null;
  const w = bounds ? bounds.maxX - bounds.minX : 0;
  const h = bounds ? bounds.maxY - bounds.minY : 0;
  const units = Number(median?.areaUnits);
  return {
    ...group,
    size: group.size ?? (upm && units > 0 ? round1(units / (upm * upm)) : null),
    width: upm && w > 0 && h > 0 ? round1(Math.min(w, h) / upm) : null,
    depth: upm && w > 0 && h > 0 ? round1(Math.max(w, h) / upm) : null,
  };
}));

/**
 * Form "create offers" (useEventBoothPlan.createBoothProducts) for rows of two kinds:
 * - single booths { key, boothNumber, area, width, depth } (unassigned areas or stands of a hall plan report):
 *   per-row m² and linking of an existing booth product with the same number (default: link);
 * - offers per plan code { key, variant, svgCode, count, area, width, depth }: ONE offer per code; the code is
 *   editable (a changed code stores the SVG code as alias, e.g. '9' -> offer 'Z9' with alias '9'); an offer
 *   of another event can be linked on request (default: create new); an offer with the code in THIS event
 *   refuses the row.
 * Template and price are set PER ROW (Phase 0 of the booth offering model): the template of a row donates its
 * price (prefilled, editable), files (copied) and texts; "Template for all rows" sets every row at once. Price
 * calculation: fixed price per booth (default) or price per m² × m² of the row; a template priced per m²
 * (boothPriceMode 'perSqm') keeps its rate. Nothing is preselected unless the caller passes rows with
 * `selected: true` (rows the admin picked in the hall plan report). Name pattern and product type are shared.
 * The rows are taken over when `active` becomes true.
 * renderFrame({ body, footer, busy }) places the form (modal or inline); onBusyChange(busy) reports a running creation.
 */
function CreateBoothsForm({
  active, sourceRows, description, hint, eventProductIds, loadBoothProducts, productsLoading, actionError, onCreate, onClose,
  onBusyChange, renderFrame,
}) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [allTemplateId, setAllTemplateId] = useState('');
  const [type, setType] = useState('event_booth');
  const [priceMode, setPriceMode] = useState('fixed');
  // commercial product name (order / invoice); the booth number / offer code is stored separately
  const [namePattern, setNamePattern] = useState('');
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!active) return;
    // empty = per-row default (buildBoothProductNames): offers 'Ausstellungsstand {{size}} m² ({{variant}})',
    // single booths 'Ausstellungsstand ({{area}})' with m², otherwise 'Stand {{number}}'
    setNamePattern('');
    setRows((sourceRows || []).map((row) => {
      const isVariant = Boolean(cleanVariantCode(row.variant));
      // offer with a match rule (features) instead of an offer code: "Create offer from features"
      if (!isVariant && row.rule) {
        return {
          key: row.key,
          kind: 'rule',
          boothNumber: '',
          variant: '',
          svgCode: '',
          rule: row.rule,
          label: row.label || '',
          nameCode: row.nameCode ?? '',
          skuCode: row.skuCode || row.label || '',
          priority: row.rule.priority ? String(row.rule.priority) : '',
          count: Number(row.count) || 0,
          selected: row.selected === true,
          area: row.area !== null && row.area !== undefined ? String(row.area) : '',
          width: null,
          depth: null,
          createNew: true,
          linkProductId: '',
          templateId: '',
          price: '',
          priceAuto: true,
        };
      }
      return {
        key: row.key || (isVariant ? `v:${row.variant}` : `n:${row.boothNumber}`),
        kind: isVariant ? 'variant' : 'number',
        boothNumber: isVariant ? '' : row.boothNumber,
        // editable code of an offer row; svgCode = the code in the plan (stored as alias when changed)
        variant: isVariant ? String(row.variant) : '',
        svgCode: isVariant ? String(row.svgCode || row.variant) : '',
        count: isVariant ? Number(row.count) || 0 : 1,
        // no automatic preselection: only rows the admin already picked (hall plan report) start selected
        selected: row.selected === true,
        area: row.area !== null && row.area !== undefined ? String(row.area) : '',
        width: row.width ?? null,
        depth: row.depth ?? null,
        // single booth: a booth product with this number already exists: link it unless the admin opts out
        createNew: false,
        // offer row: '' = create a new product (default), else the id of the product to link (opt-in)
        linkProductId: '',
        // template + price per row; priceAuto = the price was filled from the template (follows a template change)
        templateId: '',
        price: '',
        priceAuto: true,
      };
    }));
    setAllTemplateId('');
    setPriceMode('fixed');
    setProgress(null);
    setResult(null);
    loadBoothProducts().then((list) => setProducts(list || []));
    // only when the form opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const busy = progress !== null && !result;

  useEffect(() => {
    if (typeof onBusyChange === 'function') onBusyChange(busy);
  }, [busy, onBusyChange]);

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const templateOf = (row) => (row.templateId ? productById.get(row.templateId) || null : null);

  // ----- single booth rows: existing booth products with the row's number, not yet in this event
  const existingFor = (row) => (row.kind === 'number'
    ? findBoothProductsByNumber(products, row.boothNumber).filter((p) => !eventProductIds.has(p.id))
    : []);
  // ----- offer rows: codes of the row (edited code + SVG code), products carrying one of them
  const rowCodes = (row) => [...new Set([row.variant, row.svgCode].map(normalizeVariant).filter(Boolean))];
  const variantMatchesFor = (row) => (row.kind === 'variant' ? findBoothProductsByVariant(products, rowCodes(row)) : []);
  const variantInEvent = (row) => variantMatchesFor(row).filter((p) => eventProductIds.has(p.id));
  const variantLinkCandidates = (row) => variantMatchesFor(row).filter((p) => !eventProductIds.has(p.id));
  const variantCodeValid = (row) => row.kind !== 'variant' || isValidVariantCode(row.variant);
  const refusal = (row) => {
    if (row.kind !== 'variant') return null;
    if (!variantCodeValid(row)) return 'invalid';
    return variantInEvent(row).length > 0 ? 'in-event' : null;
  };

  const linkProductFor = (row) => {
    if (row.kind === 'variant') return row.linkProductId ? variantLinkCandidates(row).find((p) => p.id === row.linkProductId) || null : null;
    return row.createNew ? null : existingFor(row)[0] || null;
  };
  // same function as the creation in useEventBoothPlan: the preview is the price that gets saved
  const rowPrice = (row) => {
    const price = boothRowPrice({
      price: row.price, area: row.area, priceMode, template: templateOf(row),
    });
    if (price !== null) return price;
    const linkProduct = linkProductFor(row);
    const productPrice = linkProduct && linkProduct.price !== null && linkProduct.price !== undefined ? Number(linkProduct.price) : null;
    return productPrice !== null && productPrice > 0 ? productPrice : null;
  };
  // rule rows: offers of this event whose match rule already covers the features of the row (hint only)
  const ruleCoveredBy = (row) => {
    if (row.kind !== 'rule') return [];
    const sample = {
      size: row.rule.sizeMin ?? row.rule.sizeMax ?? null,
      positionType: (row.rule.positionTypes || [])[0] || null,
      zone: (row.rule.zones || [])[0] || null,
    };
    return products.filter((p) => eventProductIds.has(p.id) && boothMatchRuleResult(p.boothMatchRule, sample) !== null);
  };
  const selectedRows = rows.filter((r) => r.selected && !refusal(r));
  const selectedBooths = selectedRows.reduce((sum, r) => sum + (r.kind === 'number' ? 1 : Number(r.count) || 0), 0);
  const missingPrice = selectedRows.filter((r) => rowPrice(r) === null);
  const selectableRows = rows.filter((r) => !refusal(r));
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => r.selected);
  const rowLabel = (row) => {
    if (row.kind === 'rule') return row.label;
    return row.kind === 'variant' ? row.variant : row.boothNumber;
  };
  const invalidPriority = (row) => row.kind === 'rule' && row.priority !== '' && !Number.isFinite(Number(String(row.priority).replace(',', '.')));
  // the SVG code is kept as alias when the admin changed the code of an offer row
  const aliasesFor = (row) => (row.kind === 'variant' && row.svgCode && normalizeVariant(row.svgCode) !== normalizeVariant(row.variant)
    ? [row.svgCode]
    : null);

  const eventTemplates = products.filter((p) => eventProductIds.has(p.id));
  const otherTemplates = products.filter((p) => !eventProductIds.has(p.id));
  const templateLabel = (p) => [p.boothNumber || p.boothVariant, getLocalizedText(p.name), p.sku].filter(Boolean).join(' · ');
  const templateOptions = (
    <>
      {eventTemplates.length > 0 && (
        <optgroup label={t('Offers in this event')}>
          {eventTemplates.map((p) => <option key={p.id} value={p.id}>{templateLabel(p)}</option>)}
        </optgroup>
      )}
      {otherTemplates.length > 0 && (
        <optgroup label={t('Other booth products')}>
          {otherTemplates.map((p) => <option key={p.id} value={p.id}>{templateLabel(p)}</option>)}
        </optgroup>
      )}
    </>
  );
  const linkLabel = (p) => {
    const events = productEventNames(p);
    return [
      [getLocalizedText(p.name), p.sku].filter(Boolean).join(' · '),
      events.length > 0 ? t('events: {{list}}', { list: shortList(events, 3) }) : t('no event'),
    ].join(' – ');
  };
  // live preview of the name of the first (selected) row, built like the creation does
  const previewRow = selectedRows[0] || rows[0] || null;
  /** Names as the creation builds them: the pattern, or without a pattern the template's name retargeted to the row. */
  const rowNames = (row) => {
    const template = templateOf(row);
    const defaults = buildBoothProductNames(namePattern, {
      boothNumber: row.kind === 'number' ? row.boothNumber : '',
      variant: (() => {
        if (row.kind === 'rule') return row.nameCode;
        return row.kind === 'variant' ? row.variant.trim() : '';
      })(),
      area: row.area,
      count: row.count,
      templateName: germanText(template?.name),
    });
    // like createBoothProducts: rule offers keep the default name (no code to put in place of the template's)
    if (!template || row.kind === 'rule' || namePattern.trim()) return defaults;
    return retargetTemplateNames(template.name, templateRetargetValues(template, {
      variant: row.kind === 'variant' ? row.variant.trim() : '',
      boothNumber: row.kind === 'number' ? row.boothNumber : '',
      area: row.area,
    }), defaults);
  };
  const previewNames = previewRow ? rowNames(previewRow) : null;
  // template with another m² than the row: component quantities equal to the template's m² are scaled (N4)
  const sizeChangeOf = (row) => templateSizeChange(templateOf(row)?.boothArea, row.area);
  const usesArea = /\{\{\s*(area|size)\s*\}\}/.test(namePattern);
  const rowsWithoutArea = usesArea ? selectedRows.filter((r) => !(Number(r.area) > 0)) : [];
  const hasVariantRows = rows.some((r) => r.kind === 'variant' || r.kind === 'rule');
  const hasRuleRows = rows.some((r) => r.kind === 'rule');
  const hasNumberRows = rows.some((r) => r.kind === 'number');

  const updateRow = (index, patch) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  /** Row patch for a new template: the price follows the template while it was not typed by hand. */
  const templatePatch = (row, templateId, mode = priceMode) => {
    const template = templateId ? productById.get(templateId) || null : null;
    const patch = { templateId };
    if (row.priceAuto || row.price === '') {
      const templatePrice = template && template.price !== null && template.price !== undefined ? Number(template.price) : null;
      // a fixed template price is no rate per m²: in that mode it stays the fallback (empty field)
      const prefill = templatePrice !== null && templatePrice > 0 && (mode !== 'perSqm' || isPerSqmProduct(template));
      patch.price = prefill ? String(templatePrice) : '';
      patch.priceAuto = true;
    }
    return patch;
  };

  const setRowTemplate = (index, templateId) => setRows((prev) => prev.map((row, i) => (
    i === index ? { ...row, ...templatePatch(row, templateId) } : row
  )));

  /** Price calculation mode; prices filled from a template follow (a fixed template price is no rate per m²). */
  const changePriceMode = (mode) => {
    setPriceMode(mode);
    setRows((prev) => prev.map((row) => (row.priceAuto ? { ...row, ...templatePatch(row, row.templateId, mode) } : row)));
  };

  const setTemplateForAll = (templateId) => {
    setAllTemplateId(templateId);
    setRows((prev) => prev.map((row) => ({ ...row, ...templatePatch(row, templateId) })));
  };

  const handleCreate = async () => {
    setProgress({ current: 0, total: selectedRows.length });
    const price = (r) => (r.price === '' ? null : Number(r.price));
    const outcome = await onCreate({
      rows: selectedRows.map((r) => (r.kind === 'rule' ? {
        key: r.key,
        rule: { ...r.rule, priority: r.priority === '' ? 0 : Number(String(r.priority).replace(',', '.')) },
        label: r.label,
        nameCode: r.nameCode,
        skuCode: r.skuCode,
        area: r.area === '' ? null : Number(r.area),
        count: r.count,
        templateId: r.templateId || null,
        price: price(r),
      } : null) || (r.kind === 'variant' ? {
        key: r.key,
        variant: r.variant.trim(),
        aliases: aliasesFor(r),
        area: r.area === '' ? null : Number(r.area),
        count: r.count,
        width: r.width,
        depth: r.depth,
        linkProductId: linkProductFor(r)?.id || null,
        templateId: r.templateId || null,
        price: price(r),
      } : {
        key: r.key,
        boothNumber: r.boothNumber,
        area: r.area === '' ? null : Number(r.area),
        width: r.width,
        depth: r.depth,
        linkProductId: linkProductFor(r)?.id || null,
        createNew: Boolean(r.createNew),
        templateId: r.templateId || null,
        price: price(r),
      })),
      templateId: null,
      type,
      priceMode,
      namePattern: namePattern.trim(),
      onProgress: (current, total) => setProgress({ current, total }),
    });
    if (outcome) setResult(outcome);
    else setProgress(null);
  };

  const footer = result ? (
    <MDBBtn type="button" onClick={onClose}>{t('Close')}</MDBBtn>
  ) : (
    <>
      <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>{t('Cancel')}</MDBBtn>
      <MDBBtn type="button" onClick={handleCreate} disabled={busy || productsLoading || selectedRows.length === 0 || missingPrice.length > 0 || selectedRows.some(invalidPriority)}>
        {busy && <MDBSpinner size="sm" className="me-2" />}
        {t('Create {{count}} offers ({{booths}} booths)', plural(
          selectedRows.length,
          'Create {{count}} offer ({{booths}} booths)',
          'Create {{count}} offers ({{booths}} booths)',
          { booths: selectedBooths },
        ))}
      </MDBBtn>
    </>
  );

  const placeholderPatterns = [
    hasVariantRows ? BOOTH_NAME_PATTERN_VARIANT : null,
    hasNumberRows ? `${BOOTH_NAME_PATTERN_AREA} / ${BOOTH_NAME_PATTERN_NUMBER}` : null,
  ].filter(Boolean).join(' / ');

  const body = (
    <>
      <p className="small text-muted">
        {description || t('Creates one booth product (not visible in the shop) per selected row and adds it to this event. The template of a row donates image, PDF and 3D model (copied), included package, description, invoice name, price model, buyer groups, category, industries and - when the row has none - its price.')}
      </p>
      {actionError && !busy && !result && <MessageList messages={[actionError]} />}
      {result && (
        <>
          <div className={`alert small ${result.errors.length ? 'alert-warning' : 'alert-success'}`}>
            {t('{{count}} offers created ({{booths}} booths).', plural(
              result.created,
              '{{count}} offer created ({{booths}} booths).',
              '{{count}} offers created ({{booths}} booths).',
              { booths: result.createdBooths ?? result.created },
            ))}
            {result.linked > 0 && (
              <>
                {' '}
                {t('{{count}} existing offers linked ({{booths}} booths).', { count: result.linked, booths: result.linkedBooths ?? result.linked })}
              </>
            )}
          </div>
          <MessageList messages={result.errors} />
          <MessageList
            color="warning"
            messages={(result.warnings || []).map((warning) => `${warning.label}: ${t(SIZE_CHANGE_WARNING, {
              from: formatNumber(warning.from, i18n.language),
              to: formatNumber(warning.to, i18n.language),
            })}`)}
          />
        </>
      )}
      {busy && (
        <div className="small mb-3">
          <MDBSpinner size="sm" className="me-2" />
          {t('Creating offer {{current}} of {{total}}…', { current: Math.min(progress.current + 1, progress.total), total: progress.total })}
        </div>
      )}
      {!result && (
        <>
          <MDBRow className="g-3 mb-3">
            <MDBCol md="6">
              <label className="form-label small" htmlFor="hallplan-template">{t('Template for all rows (optional)')}</label>
              <select
                id="hallplan-template"
                className="form-select"
                value={allTemplateId}
                disabled={busy || productsLoading}
                onChange={(e) => setTemplateForAll(e.target.value)}
              >
                <option value="">{productsLoading ? t('Loading') : t('No template')}</option>
                {templateOptions}
              </select>
              <div className="small text-muted mt-1">
                {t('Each row can have its own template below; its price is prefilled and can be changed.')}
              </div>
            </MDBCol>
            <MDBCol md="3">
              <label className="form-label small" htmlFor="hallplan-type">{t('Product type')}</label>
              <select id="hallplan-type" className="form-select" value={type} disabled={busy} onChange={(e) => setType(e.target.value)}>
                {BOOTH_PRODUCT_TYPES.map((value) => (
                  <option key={value} value={value}>{value === 'event_space' ? t('Space only') : t('Booth')}</option>
                ))}
              </select>
            </MDBCol>
            <MDBCol md="3">
              <div className="form-label small">{t('SKU')}</div>
              <div className="small text-muted">
                {t('{{prefix}} + booth number or offer code (letters, digits and dots); -2, -3 … is appended when the SKU is taken', { prefix: BOOTH_SKU_PREFIX })}
              </div>
            </MDBCol>
          </MDBRow>
          <MDBRow className="g-3 mb-3 align-items-end">
            <MDBCol md="12">
              <MDBRadio
                name="hallplan-price-mode"
                id="hallplan-price-fixed"
                label={t('Fixed price per booth (the price of the row)')}
                checked={priceMode === 'fixed'}
                disabled={busy}
                onChange={() => changePriceMode('fixed')}
              />
              <MDBRadio
                name="hallplan-price-mode"
                id="hallplan-price-sqm"
                label={t('Price per m² × m² of the row (the price of the row is the rate per m²)')}
                checked={priceMode === 'perSqm'}
                disabled={busy}
                onChange={() => changePriceMode('perSqm')}
              />
            </MDBCol>
          </MDBRow>
          {hasVariantRows && (
            <div className="small text-muted mb-3">
              {t('Offers: the price applies to every booth with the plan code (m² of one booth). The stock of an offer is the number of its booths and is not used for booking.')}
            </div>
          )}
          {hasRuleRows && (
            <div className="small text-muted mb-3">
              {t('Offers from features get a match rule instead of an offer code: they cover every booth of the active plan with this size, position type and zone, unless a plan code or a single booth takes it. Refine the rule (size range, several position types or zones, priority) in the booth editor afterwards.')}
            </div>
          )}
          <MDBRow className="g-3 mb-3 align-items-start">
            <MDBCol md="6">
              <MDBInput
                label={t('Product name pattern')}
                value={namePattern}
                placeholder={placeholderPatterns}
                disabled={busy}
                onChange={(e) => setNamePattern(e.target.value)}
              />
              <div className="small text-muted mt-1">
                {t('Commercial name on order and invoice. Placeholders: {{placeholders}} (booth number, offer code, m² without unit, number of booths of the offer, German name of the template product). The booth number / offer code is stored separately.', {
                  // literal placeholder names (i18next does not interpolate inside values)
                  placeholders: '{{number}}, {{variant}}, {{area}} / {{size}}, {{count}}, {{template}}',
                })}
                {' '}
                {t('Leave empty for the default: "{{variantPattern}}" for an offer with a plan code, "Ausstellungsstand ({{area}})" for a booth with an area, otherwise "Stand {{number}}".', {
                  // keep the placeholders literal
                  variantPattern: BOOTH_NAME_PATTERN_VARIANT,
                  area: '{{area}}',
                  number: '{{number}}',
                })}
                {' '}
                {t('A row with a template and no pattern takes over the template\'s name. Code and m² of the template in name, description, included package and invoice name are replaced by those of the row.')}
              </div>
            </MDBCol>
            <MDBCol md="6">
              {previewNames && (
                <div className="small border rounded px-2 py-1">
                  <div className="text-muted">{t('Preview ({{boothNumber}})', { boothNumber: rowLabel(previewRow) })}</div>
                  <div className="fw-bold">{previewNames.de}</div>
                  <div className="text-muted">{previewNames.en}</div>
                </div>
              )}
            </MDBCol>
          </MDBRow>
          {rowsWithoutArea.length > 0 && (
            <div className="alert alert-info small">
              {t('{{count}} selected rows have no m²: their name is built without the area.', { count: rowsWithoutArea.length })}
            </div>
          )}
          <p className="small text-muted">
            {hint || t('The m² are taken from the last area import of this session (JSON with "area"); otherwise enter them per row where the name or the price needs them.')}
          </p>
          {missingPrice.length > 0 && (
            <div className="alert alert-warning small">
              {t('No price for: {{list}}. Enter a price for the row or choose a template with a price.', { list: missingPrice.map(rowLabel).join(', ') })}
            </div>
          )}
          {selectedRows.some((r) => existingFor(r).length > 0) && (
            <div className="alert alert-info small">
              {t('Booth products with these numbers already exist: {{list}}. They are linked to this event instead of creating duplicates (uncheck "Link" to create a new product anyway).', {
                list: selectedRows.filter((r) => existingFor(r).length > 0).map((r) => r.boothNumber).join(', '),
              })}
            </div>
          )}
          {rows.some((r) => refusal(r) === 'in-event') && (
            <div className="alert alert-warning small">
              {t('These plan codes already have an offer in this event and are skipped (one offer per code and event): {{list}}', {
                list: rows.filter((r) => refusal(r) === 'in-event').map(rowLabel).join(', '),
              })}
            </div>
          )}
          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <MDBTable small hover className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>
                    <MDBCheckbox
                      id="hallplan-create-all"
                      checked={allSelected}
                      disabled={busy}
                      aria-label={t('Select all')}
                      onChange={() => setRows((prev) => prev.map((r) => ({ ...r, selected: refusal(r) ? r.selected : !allSelected })))}
                    />
                  </th>
                  <th>{t('Booth number / offer code')}</th>
                  <th style={{ width: '110px' }}>{t('m²')}</th>
                  <th>{t('Dimensions (m)')}</th>
                  <th style={{ minWidth: '200px' }}>{t('Template')}</th>
                  <th style={{ minWidth: '150px' }}>{priceMode === 'perSqm' ? t('Price per m²') : t('Price')}</th>
                  <th>{t('Existing booth product')}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {rows.map((row, index) => {
                  const price = rowPrice(row);
                  const refused = refusal(row);
                  const existing = existingFor(row);
                  const linkCandidates = row.kind === 'variant' ? variantLinkCandidates(row) : [];
                  const inEvent = row.kind === 'variant' ? variantInEvent(row) : [];
                  const alias = aliasesFor(row);
                  const template = templateOf(row);
                  const templatePerSqm = isPerSqmProduct(template);
                  const sizeChange = sizeChangeOf(row);
                  return (
                    <tr key={row.key} className={refused ? 'text-muted' : ''}>
                      <td>
                        <MDBCheckbox
                          id={`hallplan-create-${index}`}
                          checked={row.selected && !refused}
                          disabled={busy || Boolean(refused)}
                          aria-label={rowLabel(row)}
                          onChange={(e) => updateRow(index, { selected: e.target.checked })}
                        />
                      </td>
                      {row.kind === 'rule' && (
                        <td style={{ minWidth: '180px' }}>
                          <div className="fw-bold">{row.label}</div>
                          <div className="small text-muted">
                            {t('Rule offer · {{count}} booths', { count: row.count })}
                          </div>
                          <div className="d-flex align-items-center gap-1 mt-1">
                            <label className="small text-muted mb-0" htmlFor={`hallplan-create-priority-${index}`}>{t('Priority')}</label>
                            <input
                              id={`hallplan-create-priority-${index}`}
                              type="number"
                              step="1"
                              className={`form-control form-control-sm${invalidPriority(row) ? ' is-invalid' : ''}`}
                              style={{ width: '80px' }}
                              value={row.priority}
                              placeholder="0"
                              disabled={busy}
                              onChange={(e) => updateRow(index, { priority: e.target.value })}
                            />
                          </div>
                          {ruleCoveredBy(row).length > 0 && (
                            <div className="small text-info">
                              {t('Already covered by a rule of: {{list}}', {
                                list: ruleCoveredBy(row).map((p) => p.sku || getLocalizedText(p.name)).join(', '),
                              })}
                            </div>
                          )}
                        </td>
                      )}
                      {row.kind === 'variant' && (
                        <td style={{ minWidth: '150px' }}>
                          <input
                            type="text"
                            className={`form-control form-control-sm fw-bold${refused === 'invalid' ? ' is-invalid' : ''}`}
                            value={row.variant}
                            disabled={busy}
                            aria-label={t('Offer code')}
                            onChange={(e) => updateRow(index, { variant: e.target.value, linkProductId: '' })}
                          />
                          <div className="small text-muted">
                            {t('Offer · {{count}} booths', { count: row.count })}
                          </div>
                          {alias && (
                            <div className="small text-info">{t('The plan code {{code}} is stored as alias.', { code: alias[0] })}</div>
                          )}
                          {refused === 'invalid' && (
                            <div className="small text-danger">{t('Invalid offer code.')}</div>
                          )}
                        </td>
                      )}
                      {row.kind === 'number' && (
                        <td className="fw-bold">{row.boothNumber}</td>
                      )}
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                          value={row.area}
                          disabled={busy}
                          aria-label={t('m²')}
                          onChange={(e) => updateRow(index, { area: e.target.value })}
                        />
                      </td>
                      <td className="small text-muted">
                        {row.width && row.depth
                          ? `${formatNumber(row.width, i18n.language)} × ${formatNumber(row.depth, i18n.language)}`
                          : ''}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.templateId}
                          disabled={busy || productsLoading}
                          aria-label={t('Template')}
                          onChange={(e) => setRowTemplate(index, e.target.value)}
                        >
                          <option value="">{t('No template')}</option>
                          {templateOptions}
                        </select>
                        {templatePerSqm && (
                          <div className="small text-info">{t('Priced per m² (the rate is kept)')}</div>
                        )}
                        {sizeChange && (
                          <div className="small text-warning">
                            {t(SIZE_CHANGE_WARNING, {
                              from: formatNumber(sizeChange.from, i18n.language),
                              to: formatNumber(sizeChange.to, i18n.language),
                            })}
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                          value={row.price}
                          disabled={busy}
                          placeholder={template && Number(template.price) > 0 ? String(template.price) : ''}
                          aria-label={priceMode === 'perSqm' ? t('Price per m²') : t('Price')}
                          onChange={(e) => updateRow(index, { price: e.target.value, priceAuto: false })}
                        />
                        <div className="small">
                          {price !== null
                            ? <span className="text-muted">{`= ${transformMoney(price)}${templatePerSqm ? ` ${t('per m²')}` : ''}`}</span>
                            : <span className="text-danger">{t('No price')}</span>}
                        </div>
                      </td>
                      <td className="small">
                        {row.kind === 'number' && existing.length > 0 && (
                          <MDBCheckbox
                            id={`hallplan-link-${index}`}
                            checked={!row.createNew}
                            disabled={busy}
                            label={t('Link {{sku}} instead of creating a new product', {
                              sku: existing[0].sku || existing[0].boothNumber,
                            })}
                            onChange={(e) => updateRow(index, { createNew: !e.target.checked })}
                          />
                        )}
                        {row.kind === 'variant' && refused === 'in-event' && (
                          <span className="text-danger">
                            {t('Already in this event: {{list}}', {
                              list: inEvent.map((p) => p.sku || getLocalizedText(p.name)).join(', '),
                            })}
                          </span>
                        )}
                        {row.kind === 'variant' && !refused && linkCandidates.length > 0 && (
                          <select
                            className="form-select form-select-sm"
                            value={row.linkProductId}
                            disabled={busy}
                            aria-label={t('Existing booth product')}
                            onChange={(e) => updateRow(index, { linkProductId: e.target.value })}
                          >
                            <option value="">{t('Create a new product')}</option>
                            {linkCandidates.map((p) => (
                              <option key={p.id} value={p.id}>{t('Link: {{product}}', { product: linkLabel(p) })}</option>
                            ))}
                          </select>
                        )}
                        {((row.kind === 'number' && existing.length === 0)
                          || (row.kind === 'variant' && !refused && linkCandidates.length === 0)) && (
                          <span className="text-muted">-</span>
                        )}
                        {row.kind === 'rule' && <span className="text-muted">{t('New product')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </MDBTableBody>
            </MDBTable>
          </div>
        </>
      )}
    </>
  );

  return renderFrame({ body, footer, busy });
}

/**
 * CreateBoothsForm in a modal, for the unassigned areas of the live area set: areas with a variant code become
 * one row per variant (count, size), the other named areas one row per booth number.
 */
function CreateBoothsModal({
  open, onClose, unassignedAreas, areaMeta, unitsPerMeter, ...formProps
}) {
  const { t } = useTranslation();
  const sourceRows = useMemo(() => [
    ...variantRowsFromAreas(unassignedAreas.filter((a) => normalizeVariant(a.variant)), unitsPerMeter),
    ...rowsFromAreas(unassignedAreas.filter((a) => !normalizeVariant(a.variant)), areaMeta),
  ], [unassignedAreas, areaMeta, unitsPerMeter]);
  return (
    <CreateBoothsForm
      {...formProps}
      active={open}
      sourceRows={sourceRows}
      onClose={onClose}
      description={t('Creates one offer (booth product, not visible in the shop) for every selected plan code, covering all booths with this code, or one single-booth product per selected area, and adds it to this event. The template of a row donates image, PDF and 3D model (copied), included package, description, invoice name, price model, buyer groups, category, industries and - when the row has none - its price.')}
      renderFrame={({ body, footer, busy }) => (
        <SimpleModal open={open} onClose={onClose} title={t('Create offers from unassigned areas')} size="xl" footer={footer} busy={busy}>
          {body}
        </SimpleModal>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// 4) Booths in this event - table
// ---------------------------------------------------------------------------

/**
 * Booth number (or, with editLabel / inputLabel, another short value such as the plan code, position type or
 * zone of an area) with a pencil button; edit mode: input, Enter or leaving the field saves, Esc cancels.
 * onSave(value) resolves to true when saved (edit mode closes) or false (input stays open).
 * suggestions: values offered in a datalist (free text stays possible); displayValue: shown instead of value.
 */
let editorIdCounter = 0;
function BoothNumberEditor({
  value, disabled, onSave, editLabel = '', inputLabel = '', suggestions = null, displayValue = null,
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [listId] = useState(() => {
    editorIdCounter += 1;
    return `hallplan-editor-list-${editorIdCounter}`;
  });
  // a save is running (the blur of a confirm dialog opening must not save twice) / Esc pressed
  const committing = useRef(false);
  const skipBlur = useRef(false);

  const start = () => {
    skipBlur.current = false;
    setDraft(value || '');
    setEditing(true);
  };

  const cancel = () => {
    skipBlur.current = true;
    setEditing(false);
  };

  const commit = async () => {
    if (committing.current) return;
    if (skipBlur.current) {
      skipBlur.current = false;
      return;
    }
    if (draft.trim() === String(value || '').trim()) {
      setEditing(false);
      return;
    }
    committing.current = true;
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    committing.current = false;
    if (ok) {
      // the input is removed: its blur must not save again (like cancel)
      skipBlur.current = true;
      setEditing(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    }
  };

  if (!editing) {
    return (
      <span className="d-inline-flex align-items-center">
        <span className="fw-bold">{displayValue || value || '-'}</span>
        <MDBBtn
          color="link"
          size="sm"
          type="button"
          className="px-2"
          title={editLabel || t('Edit booth number')}
          aria-label={editLabel || t('Edit booth number')}
          disabled={disabled}
          onClick={start}
        >
          <MDBIcon fas icon="pen" />
        </MDBBtn>
      </span>
    );
  }
  if (Array.isArray(suggestions)) {
    // plain input: the datalist offers the suggestions, free text stays possible
    return (
      <span className="d-inline-flex align-items-center gap-1" style={{ minWidth: '130px' }}>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ width: '120px' }}
          list={listId}
          value={draft}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          readOnly={saving}
          aria-label={inputLabel || t('Booth number')}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
        />
        <datalist id={listId}>
          {suggestions.map((option) => <option key={option} value={option} />)}
        </datalist>
        {saving && <MDBSpinner size="sm" />}
      </span>
    );
  }
  return (
    <span className="d-inline-flex align-items-center gap-1" style={{ minWidth: '130px' }}>
      <MDBInput
        size="sm"
        wrapperStyle={{ width: '110px' }}
        value={draft}
        autoFocus
        readOnly={saving}
        aria-label={inputLabel || t('Booth number')}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {saving && <MDBSpinner size="sm" />}
    </span>
  );
}

/**
 * Price of an EventProduct (single booth or variant): input saved on blur; on failure the stored value is shown
 * again. onSave(price) resolves to the result of runAction (undefined = failed).
 */
function EventProductPriceInput({ value, disabled, onSave }) {
  const { t } = useTranslation();
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    setPrice(value !== null && value !== undefined ? String(value) : '');
  }, [value]);
  useEffect(reset, [reset]);

  const commit = async () => {
    const parsed = parseFloat(price);
    if (price === '' || !Number.isFinite(parsed) || parsed < 0) {
      reset();
      return;
    }
    if (parsed === value) return;
    setSaving(true);
    const outcome = await onSave(parsed);
    setSaving(false);
    if (outcome === undefined) reset();
  };

  return (
    <input
      type="number"
      min="0"
      step="0.01"
      className="form-control form-control-sm"
      value={price}
      disabled={disabled}
      readOnly={saving}
      aria-label={t('Price')}
      onChange={(e) => setPrice(e.target.value)}
      onBlur={commit}
    />
  );
}

/**
 * Status chip + hold expiry and exhibitor of one physical booth.
 * The waiting list is switched off for now (product decision 2026-10-14): booth.waitlistCount still
 * arrives from getEventBoothMap but is deliberately not rendered.
 */
function BoothStatusCell({ booth, showExhibitor = true }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <StatusChip status={booth.status} />
      {HELD_STATUSES.includes(booth.status) && booth.expiresAt && (
        <div className="small text-muted">{t('until {{time}}', { time: formatDateTime(booth.expiresAt, i18n.language) })}</div>
      )}
      {showExhibitor && booth.status === BOOTH_STATUS.SOLD && booth.exhibitor?.name && (
        <div className="small text-muted">{booth.exhibitor.name}</div>
      )}
    </>
  );
}

/**
 * Links of an offer / single booth to its editors (review 2, B15): booth editor /admin/booth/<id> and product
 * editor /admin/product/<id>. They open in a new tab, so this tab (and unsaved event fields) stays as it is.
 */
function ProductEditLinks({ productId }) {
  const { t } = useTranslation();
  if (!productId) return null;
  return (
    <>
      <Link
        to={`/admin/booth/${productId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-link btn-sm"
        title={t('Edit booth')}
        aria-label={t('Edit booth')}
      >
        <MDBIcon fas icon="pen" />
      </Link>
      <Link
        to={`/admin/product/${productId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-link btn-sm"
        title={t('Edit as product')}
        aria-label={t('Edit as product')}
      >
        <MDBIcon fas icon="box" />
      </Link>
    </>
  );
}

/** Name of an offer / single booth as link to its booth editor (new tab); plain text without product id. */
function ProductNameLink({ productId, name }) {
  const { t } = useTranslation();
  if (!productId) return <div>{name}</div>;
  return (
    <div>
      <Link to={`/admin/booth/${productId}`} target="_blank" rel="noopener noreferrer" title={t('Edit booth')}>
        {name || productId}
      </Link>
    </div>
  );
}

/** Option creation is possible on a free / unavailable booth and over a hold (the Lambda replaces holds). */
const canCreateOption = (status) => status === BOOTH_STATUS.FREE || status === BOOTH_STATUS.UNAVAILABLE
  || HELD_STATUSES.includes(status);

function BoothRow({
  booth, eventProduct = null, busy, hasLivePlan = true, onUpdate, onSaveNumber, onOption, onReservations, onRemove,
}) {
  const { t, i18n } = useTranslation();
  // the inline price is the EventProduct (base) price; booth.price of the map may include surcharges
  const basePrice = eventProduct && eventProduct.price !== null && eventProduct.price !== undefined ? eventProduct.price : booth.price;
  const mapPrice = booth.price === null || booth.price === undefined ? null : Number(booth.price);
  const showMapPrice = mapPrice !== null && Number.isFinite(mapPrice) && basePrice !== null && basePrice !== undefined && mapPrice !== Number(basePrice);
  const [stock, setStock] = useState('');
  // field of this row whose save is running; only that control is locked (a global busy flag
  // flipped on blur would disable the control the user is just clicking and swallow the click)
  const [savingField, setSavingField] = useState(null);

  const resetStock = useCallback(() => {
    setStock(booth.stock !== null && booth.stock !== undefined ? String(booth.stock) : '');
  }, [booth.stock]);
  useEffect(resetStock, [resetStock]);

  /** Saves one field; on failure the input shows the stored value again. */
  const save = async (field, updates, revert) => {
    setSavingField(field);
    const outcome = await onUpdate(booth, updates);
    setSavingField(null);
    if (outcome === undefined && revert) revert();
    return outcome;
  };

  const commitStock = () => {
    const value = parseInt(stock, 10);
    if (stock === '' || !Number.isFinite(value) || value < 0) {
      resetStock();
      return;
    }
    if (value !== booth.stock) save('stock', { stock: value }, resetStock);
  };

  const isSold = booth.status === BOOTH_STATUS.SOLD;
  const isReserved = RESERVED_STATUSES.includes(booth.status);
  const canOption = canCreateOption(booth.status);
  const canRemove = booth.status === BOOTH_STATUS.FREE || booth.status === BOOTH_STATUS.UNAVAILABLE;

  return (
    <tr>
      <td className="text-nowrap">
        <BoothNumberEditor value={booth.boothNumber} disabled={busy} onSave={(value) => onSaveNumber(booth, value)} />
      </td>
      <td>
        <ProductNameLink productId={booth.productId} name={getLocalizedText(booth.name)} />
        <div className="small text-muted">{booth.type}</div>
      </td>
      <td className="text-nowrap">{formatNumber(booth.area, i18n.language)}</td>
      <td style={{ minWidth: '120px' }}>
        <EventProductPriceInput value={basePrice} disabled={busy} onSave={(price) => save('price', { price })} />
        {showMapPrice && (
          <div className="small text-muted text-nowrap">{t('Booth price {{price}}', { price: transformMoney(mapPrice) })}</div>
        )}
      </td>
      <td style={{ minWidth: '80px' }}>
        <input
          type="number"
          min="0"
          step="1"
          className="form-control form-control-sm"
          value={stock}
          disabled={busy}
          readOnly={savingField === 'stock'}
          aria-label={t('Stock')}
          onChange={(e) => setStock(e.target.value)}
          onBlur={commitStock}
        />
      </td>
      <td>
        <MDBSwitch
          id={`hallplan-available-${booth.eventProductId}`}
          checked={booth.isAvailable !== false}
          disabled={busy || isSold || savingField === 'isAvailable'}
          aria-label={t('Bookable')}
          onChange={(e) => save('isAvailable', { isAvailable: e.target.checked })}
        />
      </td>
      <td>
        <BoothStatusCell booth={booth} />
        {booth.bookable === false && <div className="small text-muted">{t('Taken off sale on the hall plan')}</div>}
      </td>
      <td className="text-center">
        {booth.points && <MDBIcon fas icon="check-circle" className="text-success" title={t('Area on the hall plan')} />}
        {/* without an active hall plan version the banner says it once instead of a warning per row (review 2, C-3) */}
        {!booth.points && hasLivePlan && <MDBIcon fas icon="exclamation-triangle" className="text-warning" title={t('No area on the hall plan')} />}
        {!booth.points && !hasLivePlan && <span className="text-muted">-</span>}
      </td>
      <td className="text-end text-nowrap">
        <ProductEditLinks productId={booth.productId} />
        {canOption && (
          <MDBBtn color="link" size="sm" type="button" title={t('Create option for company')} aria-label={t('Create option for company')} disabled={busy} onClick={() => onOption(booth)}>
            <MDBIcon fas icon="user-tag" />
          </MDBBtn>
        )}
        {(isReserved || booth.reservationId) && (
          <MDBBtn color="link" size="sm" type="button" title={t('Release reservation')} aria-label={t('Release reservation')} disabled={busy} onClick={() => onReservations(booth)}>
            <MDBIcon fas icon="unlock" />
          </MDBBtn>
        )}
        {canRemove && (
          <MDBBtn color="link" size="sm" type="button" className="text-danger" title={t('Remove from event')} aria-label={t('Remove from event')} disabled={busy} onClick={() => onRemove(booth)}>
            <MDBIcon fas icon="trash" />
          </MDBBtn>
        )}
      </td>
    </tr>
  );
}

/**
 * Row of a variant EventProduct (one product for all booths of a variant code): price and availability per
 * EventProduct, counts of map.variants[], read-only stock hint, and the expandable list of its physical booths
 * (status, exhibitor, hold expiry, bookable flag of the area, option / release / reservations per booth).
 * "Remove from event" exists only here (removes every booth of the variant).
 */
function VariantRow({
  row, busy, hasLivePlan = true, onUpdate, onBoothOption, onBoothRelease, onBoothReservations, onBoothBookable, onRemove,
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [savingAvailable, setSavingAvailable] = useState(false);
  const { variant, booths, product, eventProduct } = row;
  const aliases = (Array.isArray(variant.aliases) ? variant.aliases : product?.boothVariantAliases || []).filter(Boolean);
  // price model (booth offering model): the inline price is the BASE price of the EventProduct (fixed price per
  // booth or rate per m²); the booths' prices come from the map (priceForBooth incl. surcharges, time tiers)
  const priceMode = String(variant.priceMode || eventProduct?.product?.boothPriceMode || product?.boothPriceMode || product?.priceMode || '').trim() === 'perSqm'
    ? 'perSqm'
    : 'fixed';
  const hasPriceRule = Boolean(ruleObjectOrNull(variant.priceRule ?? eventProduct?.boothPriceRule ?? eventProduct?.product?.boothPriceRule ?? null));
  const basePrice = eventProduct && eventProduct.price !== null && eventProduct.price !== undefined ? eventProduct.price : variant.price;
  const boothPrices = numberRange(booths.map((b) => (b.price === null || b.price === undefined ? null : Number(b.price))));
  const pricesDiffer = boothPrices.count > 0 && (boothPrices.min !== boothPrices.max || (basePrice !== null && basePrice !== undefined && boothPrices.min !== Number(basePrice)));
  const total = variant.total ?? booths.length;
  const blocked = (variant.sold || 0) + (variant.held || 0) + (variant.reserved || 0);
  const canRemove = blocked === 0;
  const area = product?.area ?? booths.find((b) => b.variantSize)?.variantSize ?? null;

  const saveAvailable = async (isAvailable) => {
    setSavingAvailable(true);
    await onUpdate(row.entry, { isAvailable });
    setSavingAvailable(false);
  };

  return (
    <>
      <tr className="table-light">
        <td className="text-nowrap">
          <MDBBtn
            color="link"
            size="sm"
            type="button"
            className="px-1"
            title={expanded ? t('Hide booths') : t('Show booths')}
            aria-label={expanded ? t('Hide booths') : t('Show booths')}
            aria-expanded={expanded}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <MDBIcon fas icon={expanded ? 'chevron-down' : 'chevron-right'} />
          </MDBBtn>
          <span className="fw-bold">{variant.variant || product?.sku || ''}</span>
          <MDBBadge color="info" light className="ms-1">{variant.variant ? t('Offer') : t('Rule offer')}</MDBBadge>
          <MDBBadge color="secondary" light className="ms-1" title={t('Price mode')}>
            {priceMode === 'perSqm' ? t('per m²') : t('fixed price')}
          </MDBBadge>
          {aliases.length > 0 && (
            <div className="small text-muted">{t('Aliases: {{list}}', { list: aliases.join(', ') })}</div>
          )}
          {(variant.features?.positionTypes?.length > 0 || variant.features?.zones?.length > 0) && (
            <div className="small text-muted">
              {[
                ...(variant.features.positionTypes || []).map((key) => positionLabel(key, t)),
                ...(variant.features.zones || []).map((zone) => t('Zone {{zone}}', { zone })),
              ].join(' · ')}
            </div>
          )}
        </td>
        <td>
          <ProductNameLink productId={variant.productId || product?.id} name={getLocalizedText(variant.name || product?.name)} />
          <div className="small text-muted">{product?.type || ''}</div>
        </td>
        <td className="text-nowrap">{formatNumber(area, i18n.language)}</td>
        <td style={{ minWidth: '120px' }}>
          <EventProductPriceInput value={basePrice} disabled={busy} onSave={(price) => onUpdate(row.entry, { price })} />
          {(priceMode === 'perSqm' || hasPriceRule) && (
            <div className="small text-muted text-nowrap">
              {priceMode === 'perSqm' ? t('Base price per m²') : t('Base price')}
              {hasPriceRule && ` · ${t('with price rule')}`}
            </div>
          )}
          {pricesDiffer && (
            <div className="small text-nowrap" title={t('Prices of the booths of this offer (hall plan)')}>
              {boothPrices.min !== boothPrices.max
                ? t('from {{min}} to {{max}}', { min: transformMoney(boothPrices.min), max: transformMoney(boothPrices.max) })
                : t('per booth {{price}}', { price: transformMoney(boothPrices.min) })}
            </div>
          )}
        </td>
        <td className="small text-muted" style={{ minWidth: '80px' }}>
          {t('stock (unused for offers): {{stock}}', { stock: variant.stock ?? '-' })}
        </td>
        <td>
          <MDBSwitch
            id={`hallplan-available-${variant.eventProductId}`}
            checked={variant.isAvailable !== false}
            disabled={busy || savingAvailable}
            aria-label={t('Bookable')}
            onChange={(e) => saveAvailable(e.target.checked)}
          />
        </td>
        <td className="small">
          {/* one plural key per count (review 2, C-1: "1 booths · 1 free", "1 options") */}
          {[
            t('{{count}} booths', plural(total, '{{count}} booth', '{{count}} booths')),
            t('{{count}} free', plural(variant.free ?? 0, '{{count}} free', '{{count}} free')),
            t('{{count}} held', plural(variant.held ?? 0, '{{count}} held', '{{count}} held')),
            t('{{count}} options', plural(variant.reserved ?? 0, '{{count}} option', '{{count}} options')),
            t('{{count}} sold', plural(variant.sold ?? 0, '{{count}} sold', '{{count}} sold')),
          ].join(' · ')}
          {variant.unavailable > 0 && (
            <div className="text-muted">{t('{{count}} not available', { count: variant.unavailable })}</div>
          )}
        </td>
        <td className="text-center">
          {total > 0 && <MDBIcon fas icon="check-circle" className="text-success" title={t('{{count}} areas on the hall plan', { count: total })} />}
          {/* without an active hall plan version every offer lacks booths: the banner says so once (review 2, C-3) */}
          {total === 0 && hasLivePlan && (
            <MDBIcon fas icon="exclamation-triangle" className="text-warning" title={t('No booth on the hall plan belongs to this offer')} />
          )}
          {total === 0 && !hasLivePlan && <span className="text-muted">-</span>}
        </td>
        <td className="text-end text-nowrap">
          <ProductEditLinks productId={variant.productId || product?.id} />
          <MDBBtn
            color="link"
            size="sm"
            type="button"
            className="text-danger"
            title={canRemove ? t('Remove from event') : t('Booths of this offer are sold, held or optioned')}
            aria-label={t('Remove from event')}
            disabled={busy || !canRemove}
            onClick={() => onRemove(row)}
          >
            <MDBIcon fas icon="trash" />
          </MDBBtn>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} className="p-0">
            {booths.length === 0 ? (
              <div className="small text-muted px-4 py-2">{t('No booth on the hall plan belongs to this offer.')}</div>
            ) : (
              <div className="ps-4 pe-2 py-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <MDBTable small hover className="mb-0">
                  <MDBTableHead>
                    <tr>
                      <th>{t('Booth')}</th>
                      <th>{t('Features')}</th>
                      <th>{t('Price')}</th>
                      <th>{t('Status')}</th>
                      <th>{t('Exhibitor')}</th>
                      <th>{t('Booth bookable')}</th>
                      <th aria-label={t('Actions')} />
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {booths.map((booth) => {
                      const locked = HELD_STATUSES.includes(booth.status)
                        || booth.status === BOOTH_STATUS.RESERVED_FOR_OTHER || booth.status === BOOTH_STATUS.RESERVED_FOR_YOU;
                      return (
                        <tr key={booth.boothKey || `${booth.eventProductId}#${booth.boothNumber}`}>
                          <td className="fw-bold text-nowrap">{booth.boothNumber}</td>
                          <td className="small text-muted text-nowrap">
                            {[
                              positiveOrNull(booth.variantSize) !== null ? `${formatNumber(booth.variantSize, i18n.language)} m²` : '',
                              positionLabel(booth.positionType, t),
                              booth.zone ? t('Zone {{zone}}', { zone: booth.zone }) : '',
                            ].filter(Boolean).join(' · ') || '-'}
                          </td>
                          <td className="small text-nowrap">
                            {booth.price !== null && booth.price !== undefined ? transformMoney(booth.price) : '-'}
                            {Array.isArray(booth.priceDetails?.surcharges) && booth.priceDetails.surcharges.length > 0 && (
                              <div className="text-muted">{t('incl. surcharges')}</div>
                            )}
                          </td>
                          <td><BoothStatusCell booth={booth} showExhibitor={false} /></td>
                          <td className="small">{booth.exhibitor?.name || '-'}</td>
                          <td>
                            <MDBSwitch
                              id={`hallplan-booth-bookable-${booth.eventProductId}-${booth.boothNumber}`}
                              checked={booth.bookable !== false}
                              disabled={busy || !booth.areaId}
                              aria-label={t('Booth bookable')}
                              onChange={(e) => onBoothBookable(booth, e.target.checked)}
                            />
                          </td>
                          <td className="text-end text-nowrap">
                            {canCreateOption(booth.status) && (
                              <MDBBtn color="link" size="sm" type="button" title={t('Create option for company')} aria-label={t('Create option for company')} disabled={busy} onClick={() => onBoothOption(booth)}>
                                <MDBIcon fas icon="user-tag" />
                              </MDBBtn>
                            )}
                            {locked && (
                              <MDBBtn color="link" size="sm" type="button" title={t('Release hold or option')} aria-label={t('Release hold or option')} disabled={busy} onClick={() => onBoothRelease(booth)}>
                                <MDBIcon fas icon="unlock" />
                              </MDBBtn>
                            )}
                            <MDBBtn color="link" size="sm" type="button" title={t('Reservations of this booth')} aria-label={t('Reservations of this booth')} disabled={busy} onClick={() => onBoothReservations(booth)}>
                              <MDBIcon fas icon="list" />
                            </MDBBtn>
                          </td>
                        </tr>
                      );
                    })}
                  </MDBTableBody>
                </MDBTable>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Row of a booth EventProduct whose product has no booth number: booth number input + save. */
function NumberlessBoothRow({ eventProduct, busy, onSave }) {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const product = eventProduct.product || {};

  const save = async () => {
    if (!value.trim() || saving) return;
    setSaving(true);
    const ok = await onSave(value);
    setSaving(false);
    if (ok) setValue('');
  };

  return (
    <tr>
      <td>
        <div>{getLocalizedText(product.name) || '-'}</div>
        <div className="small text-muted">{product.type}</div>
      </td>
      <td className="small text-muted">{product.sku || '-'}</td>
      <td className="text-nowrap">{formatNumber(product.boothArea, i18n.language)}</td>
      <td className="text-nowrap">
        {eventProduct.price !== null && eventProduct.price !== undefined ? transformMoney(eventProduct.price) : '-'}
      </td>
      <td>{eventProduct.stock ?? '-'}</td>
      <td style={{ minWidth: '240px' }}>
        <div className="d-flex align-items-center gap-2">
          <MDBInput
            size="sm"
            label={t('Booth number')}
            wrapperClass="flex-grow-1"
            value={value}
            readOnly={saving}
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
            }}
          />
          <MDBBtn
            size="sm"
            type="button"
            title={t('Save booth number')}
            aria-label={t('Save booth number')}
            disabled={busy || saving || !value.trim()}
            onClick={save}
          >
            {saving ? <MDBSpinner size="sm" /> : <MDBIcon fas icon="save" />}
          </MDBBtn>
        </div>
      </td>
    </tr>
  );
}

/**
 * Warnings of the variant setup (design 3.11.5): variant products without areas, areas whose variant has no
 * product, duplicate variant products, single booths overriding a variant area, pool reservations.
 */
function VariantWarnings({
  boothMap, areas, eventBoothProducts, hasLivePlan = true,
}) {
  const { t } = useTranslation();
  const matchProducts = useMemo(() => toMatchProducts(eventBoothProducts), [eventBoothProducts]);
  // the same matching as the Lambda (utils/boothMap.matchAreasToProducts), on the live areas of this tab
  const warnings = useMemo(
    () => (eventBoothProducts ? matchAreasToProducts(areas, matchProducts).warnings : []),
    [areas, matchProducts, eventBoothProducts],
  );
  const labelByEp = useMemo(() => new Map((eventBoothProducts || []).map((ep) => [
    ep.id,
    ep.product ? (ep.product.sku || getLocalizedText(ep.product.name) || ep.product.id) : ep.id,
  ])), [eventBoothProducts]);

  const variants = Array.isArray(boothMap?.variants) ? boothMap.variants : [];
  const withoutAreas = variants.filter((v) => !(Number(v.total ?? v.boothCount) > 0));
  const missing = warnings.filter((w) => w.code === 'variant-without-product');
  const duplicates = warnings.filter((w) => w.code === 'duplicate-variant-product');
  const overrides = warnings.filter((w) => w.code === 'number-overrides-variant');
  const withoutOffer = warnings.filter((w) => w.code === 'area-without-offer');
  const ambiguous = warnings.filter((w) => w.code === 'ambiguous-offer-match');
  const pool = Array.isArray(boothMap?.poolReservations) ? boothMap.poolReservations : [];
  const codeByEp = new Map(variants.map((v) => [v.eventProductId, v.variant]));

  if (!withoutAreas.length && !missing.length && !duplicates.length && !overrides.length && !pool.length
    && !withoutOffer.length && !ambiguous.length) return null;
  return (
    <div className="alert alert-warning small">
      {/* no active version (e.g. only a draft): every offer lacks booths, that is no offer-specific problem (review 2, C-3) */}
      {withoutAreas.length > 0 && !hasLivePlan && (
        <div>{t('No active hall plan version - activate a version to assign booths to the offers.')}</div>
      )}
      {withoutAreas.length > 0 && hasLivePlan && (
        <div>{t('Offers without a booth on the hall plan: {{list}}', { list: withoutAreas.map((v) => v.variant || labelByEp.get(v.eventProductId) || v.eventProductId).join(', ') })}</div>
      )}
      {missing.length > 0 && (
        <div>
          {t('Areas whose plan code has no offer in this event: {{list}}', {
            list: missing.map((w) => `${w.ref} (${w.count})`).join(', '),
          })}
        </div>
      )}
      {withoutOffer.length > 0 && (
        <div>
          {t('Areas with features (size, position type, zone) but without a matching offer: {{list}}', {
            list: shortList(withoutOffer.map((w) => w.ref)),
          })}
        </div>
      )}
      {ambiguous.length > 0 && (
        <div>
          {t('Several offers match the same features with equal priority (the first one is used): {{list}}', {
            list: ambiguous.map((w) => `${w.ref}: ${(w.eventProductIds || []).map((id) => labelByEp.get(id) || id).join(' / ')}`).join('; '),
          })}
        </div>
      )}
      {duplicates.length > 0 && (
        <div>
          {t('Several offers of this event carry the same offer code (the first one is used): {{list}}', {
            list: duplicates.map((w) => `${w.ref}: ${(w.eventProductIds || []).map((id) => labelByEp.get(id) || id).join(' / ')}`).join('; '),
          })}
        </div>
      )}
      {overrides.length > 0 && (
        <div>
          {t('Single booth products take these areas with a plan code (the booth number wins over the code): {{list}}', {
            list: overrides.map((w) => `${w.ref} (${w.variant})`).join(', '),
          })}
        </div>
      )}
      {pool.length > 0 && (
        <div>
          {t('{{count}} reservations without a booth number on offers are ignored for booking: {{list}}. Give them a booth number or delete them under Stock Reservations.', {
            count: pool.length,
            list: [...new Set(pool.map((r) => codeByEp.get(r.eventProductId) || labelByEp.get(r.eventProductId) || r.eventProductId))].join(', '),
          })}
        </div>
      )}
    </div>
  );
}

/** Price range text: one amount, or "x – y" when the booths of a cell / offer differ. */
const priceRangeText = (min, max) => {
  if (min === null || min === undefined) return '';
  return max !== null && max !== undefined && max !== min ? `${transformMoney(min)} – ${transformMoney(max)}` : transformMoney(min);
};

/** Header label of an offer column / exception: offer code or rule, name, SKU. */
function OfferHeader({ eventProduct }) {
  const { t } = useTranslation();
  const product = eventProduct?.product;
  if (!product) return <span>{eventProduct?.id || '-'}</span>;
  const code = cleanVariantCode(product.boothVariant);
  const rule = ruleObjectOrNull(product.boothMatchRule);
  return (
    <div style={{ minWidth: '110px' }}>
      <div className="fw-bold text-nowrap">
        {code || (rule ? t('Rule offer') : product.sku)}
        {isPerSqmProduct(product) && <MDBBadge color="info" light className="ms-1">{t('per m²')}</MDBBadge>}
      </div>
      {rule && <div className="small text-muted text-nowrap">{featureRuleLabel(rule) || t('all booths')}</div>}
      <div className="small text-muted text-truncate" style={{ maxWidth: '180px' }} title={getLocalizedText(product.name)}>{product.sku}</div>
    </div>
  );
}

/**
 * Card "Assignment matrix" (booth offering model, phase 2): which offer sells which booths of the live plan.
 * Rows = feature combinations (size × position type × zone) or plan codes, columns = offers of this event (plus
 * single booths and "no offer"); a cell shows the booths whose primary offer the column is (bold), further offers
 * (+n alternative) and the price range of the primary booths (same matching and prices as the booking:
 * utils/boothMap via utils/boothOfferAdmin.assignmentMatrix). Below: exceptions (single booths, plan codes that beat
 * a rule) and the warnings area-without-offer / ambiguous-offer-match / offers without booths.
 * Rows of the feature view can be turned into offers with a match rule ("Create offer from features").
 */
function AssignmentMatrixCard({
  areas, eventBoothProducts, isLoading, busy, actionError, clearActionError, eventProductIds, loadBoothProducts, productsLoading,
  onCreate,
}) {
  const { t, i18n } = useTranslation();
  const [rowsBy, setRowsBy] = useState('features');
  const [selected, setSelected] = useState(() => new Set());
  const [createRows, setCreateRows] = useState(null);
  const matrix = useMemo(
    () => (eventBoothProducts ? assignmentMatrix(areas, eventBoothProducts, { rowsBy }) : null),
    [areas, eventBoothProducts, rowsBy],
  );
  const epById = useMemo(() => new Map((eventBoothProducts || []).map((ep) => [ep.id, ep])), [eventBoothProducts]);

  useEffect(() => {
    setSelected(new Set());
  }, [rowsBy, matrix]);

  if (!matrix || matrix.areaCount === 0) {
    return (
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBCardTitle>{t('Assignment matrix')}</MDBCardTitle>
          {isLoading ? <MDBSpinner size="sm" /> : <p className="small text-muted mb-0">{t('The active plan has no booths yet.')}</p>}
        </MDBCardBody>
      </MDBCard>
    );
  }

  const labelOf = (eventProductId) => offerLabelOf(epById.get(eventProductId)) || eventProductId;
  const ruleRows = rowsBy === 'features' ? featureRuleRows(matrix.rows.filter((row) => selected.has(row.key))) : [];
  const selectableKeys = rowsBy === 'features'
    ? new Set(featureRuleRows(matrix.rows).map((row) => row.key.replace(/^r:/, '')))
    : new Set();
  const isSelectable = (row) => selectableKeys.has(String(row.key).toLowerCase());
  const warnings = matrix.warnings || [];
  const withoutOffer = warnings.filter((w) => w.code === 'area-without-offer');
  const ambiguous = warnings.filter((w) => w.code === 'ambiguous-offer-match');
  const missingCodes = warnings.filter((w) => w.code === 'variant-without-product');
  const singleExceptions = matrix.exceptions.filter((e) => e.kind === 'single');
  const codeExceptions = matrix.exceptions.filter((e) => e.kind === 'code');

  const rowLabel = (row) => {
    if (rowsBy === 'codes') return row.variant || <span className="text-muted">{t('No plan code')}</span>;
    const parts = [
      row.size !== null && row.size !== undefined ? `${formatNumber(row.size, i18n.language)} m²` : '',
      positionLabel(row.positionType, t),
      row.zone ? t('Zone {{zone}}', { zone: row.zone }) : '',
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : <span className="text-muted">{t('No features')}</span>;
  };
  const columnHeader = (column) => {
    if (column.kind === 'single') return <span className="fw-bold">{t('Single booths')}</span>;
    if (column.kind === 'none') return <span className="fw-bold text-danger">{t('No offer')}</span>;
    return <OfferHeader eventProduct={epById.get(column.eventProductId)} />;
  };
  const toggle = (key, checked) => setSelected((prev) => {
    const next = new Set(prev);
    if (checked) next.add(key);
    else next.delete(key);
    return next;
  });
  const openCreate = () => {
    clearActionError();
    setCreateRows(ruleRows.map((row) => ({ ...row, selected: true })));
  };

  return (
    <MDBCard className="mb-4">
      <MDBCardBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
          <div>
            <MDBCardTitle className="mb-1">{t('Assignment matrix')}</MDBCardTitle>
            <div className="small text-muted">
              {t('Which offer sells which booths of the active plan: bold = primary offer (booking and price), +n = further matching offers. Order: single booth number, then plan code, then match rule (priority, then the more specific rule).')}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={rowsBy}
              aria-label={t('Rows')}
              onChange={(e) => setRowsBy(e.target.value)}
            >
              <option value="features">{t('Rows: features (size, position type, zone)')}</option>
              <option value="codes">{t('Rows: plan codes')}</option>
            </select>
            {rowsBy === 'features' && onCreate && (
              <MDBBtn size="sm" outline type="button" disabled={busy || ruleRows.length === 0} onClick={openCreate}>
                <MDBIcon fas icon="filter" className="me-2" />
                {t('Create offer from features')}
                {` (${ruleRows.length})`}
              </MDBBtn>
            )}
          </div>
        </div>

        <div className="table-responsive border rounded mb-3" style={{ maxHeight: '520px', overflowY: 'auto' }}>
          <MDBTable small hover className="mb-0" style={{ fontSize: '0.85rem' }}>
            <MDBTableHead>
              <tr>
                {rowsBy === 'features' && onCreate && <th aria-label={t('Select')} />}
                <th>{rowsBy === 'codes' ? t('Plan code') : t('Features')}</th>
                <th>{t('Booths')}</th>
                {matrix.columns.map((column) => (
                  <th key={column.key} className="align-top">{columnHeader(column)}</th>
                ))}
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {matrix.rows.map((row, index) => (
                <tr key={row.key}>
                  {rowsBy === 'features' && onCreate && (
                    <td>
                      {isSelectable(row) && (
                        <MDBCheckbox
                          id={`hallplan-matrix-${index}`}
                          checked={selected.has(row.key)}
                          disabled={busy}
                          aria-label={t('Select')}
                          onChange={(e) => toggle(row.key, e.target.checked)}
                        />
                      )}
                    </td>
                  )}
                  <td className="text-nowrap" title={shortList(row.boothNumbers || [], 40)}>{rowLabel(row)}</td>
                  <td>{row.count}</td>
                  {matrix.columns.map((column) => {
                    const cell = row.cells.get(column.key);
                    if (!cell) return <td key={column.key} className="text-muted text-center">·</td>;
                    return (
                      <td key={column.key} className={column.kind === 'none' ? 'table-danger' : ''}>
                        {cell.primary > 0 && <span className="fw-bold">{cell.primary}</span>}
                        {cell.alternative > 0 && (
                          <span className="text-muted" title={t('Further matching offer (not used for booking yet)')}>
                            {cell.primary > 0 ? ' ' : ''}
                            {`+${cell.alternative}`}
                          </span>
                        )}
                        {cell.min !== null && (
                          <div className="small text-muted text-nowrap">{priceRangeText(cell.min, cell.max)}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </MDBTableBody>
          </MDBTable>
        </div>

        {(withoutOffer.length > 0 || ambiguous.length > 0 || missingCodes.length > 0 || matrix.offersWithoutBooths.length > 0) && (
          <div className="alert alert-warning small">
            {withoutOffer.length > 0 && (
              <div>
                {t('Areas with features (size, position type, zone) but without a matching offer: {{list}}', {
                  list: shortList(withoutOffer.map((w) => w.ref)),
                })}
              </div>
            )}
            {missingCodes.length > 0 && (
              <div>
                {t('Areas whose plan code has no offer in this event: {{list}}', {
                  list: missingCodes.map((w) => `${w.ref} (${w.count})`).join(', '),
                })}
              </div>
            )}
            {ambiguous.length > 0 && (
              <div>
                {t('Several offers match the same features with equal priority (the first one is used): {{list}}', {
                  list: ambiguous.map((w) => `${w.ref}: ${(w.eventProductIds || []).map(labelOf).join(' / ')}`).join('; '),
                })}
              </div>
            )}
            {matrix.offersWithoutBooths.length > 0 && (
              <div>
                {t('Offers without a booth on the hall plan: {{list}}', { list: matrix.offersWithoutBooths.map(labelOf).join(', ') })}
              </div>
            )}
          </div>
        )}

        <h6 className="mb-1">{t('Exceptions')}</h6>
        {singleExceptions.length === 0 && codeExceptions.length === 0 && (
          <p className="small text-muted mb-0">{t('No exceptions: every booth is sold under the offer of its plan code or match rule.')}</p>
        )}
        {singleExceptions.length > 0 && (
          <div className="small mb-1">
            <strong>{t('Single booths (the booth number wins):')}</strong>
            {' '}
            {shortList(singleExceptions.map((e) => (e.variant ? `${e.boothNumber} (${e.variant})` : e.boothNumber)), 40)}
          </div>
        )}
        {codeExceptions.length > 0 && (
          <div className="small">
            <strong>{t('Plan codes that win over a match rule:')}</strong>
            {' '}
            {shortList(codeExceptions.map((e) => `${e.boothNumber}: ${e.variant || labelOf(e.eventProductId)} ${t('instead of')} ${labelOf(e.ruleEventProductId)}`), 20)}
          </div>
        )}
      </MDBCardBody>

      <CreateBoothsForm
        active={Boolean(createRows)}
        sourceRows={createRows || []}
        description={t('Creates one offer (booth product, not visible in the shop) with a match rule per selected feature combination and adds it to this event. The template of a row donates image, PDF and 3D model (copied), included package, description, invoice name, price model, buyer groups, category, industries and - when the row has none - its price.')}
        hint={t('The m² of a row is the size of its booths (data-size).')}
        eventProductIds={eventProductIds}
        loadBoothProducts={loadBoothProducts}
        productsLoading={productsLoading}
        actionError={actionError}
        onCreate={onCreate}
        onClose={() => setCreateRows(null)}
        renderFrame={({ body, footer, busy: formBusy }) => (
          <SimpleModal open={Boolean(createRows)} onClose={() => setCreateRows(null)} title={t('Create offer from features')} size="xl" footer={footer} busy={formBusy}>
            {body}
          </SimpleModal>
        )}
      />
    </MDBCard>
  );
}

function BoothsCard({
  boothMap, isLoading, loadError, busy, actionError, clearActionError, runAction, areaMeta, productsLoading, productsError,
  eventProductIds, eventBoothProducts, eventProductsLoading, eventProductsError, areas, unitsPerMeter, activeVersionId = null, ...actions
}) {
  const { t } = useTranslation();
  // live area set: the active hall plan version, or legacy areas without a version (review 2, C-3)
  const hasLivePlan = Boolean(activeVersionId) || (areas || []).length > 0;
  const [query, setQuery] = useState('');
  const [optionBooth, setOptionBooth] = useState(null);
  const [reservationsBooth, setReservationsBooth] = useState(null);
  const [removeBooth, setRemoveBooth] = useState(null);
  const [releaseBooth, setReleaseBooth] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // booth number change that needs a confirmation: { warnings, run, resolve }
  const [numberConfirm, setNumberConfirm] = useState(null);
  const [numberConfirmBusy, setNumberConfirmBusy] = useState(false);
  // bookable change of a sold / held / optioned booth that needs a confirmation: { booth, bookable }
  const [bookableConfirm, setBookableConfirm] = useState(null);
  // refused booth number input (leading '#')
  const [numberError, setNumberError] = useState(null);

  // the modals show actionError: open them without a stale error of an earlier action
  const openWithoutError = (open) => (value) => {
    clearActionError();
    open(value);
  };

  const booths = useMemo(() => boothMap?.booths || [], [boothMap]);
  const productsById = useMemo(() => (boothMap?.products && typeof boothMap.products === 'object' ? boothMap.products : {}), [boothMap]);
  // EventProducts of this event by id (base price, price model of the offer)
  const eventProductById = useMemo(() => new Map((eventBoothProducts || []).map((ep) => [ep.id, ep])), [eventBoothProducts]);
  /**
   * One row per EventProduct: single booths (one map booth each) first, then the variant products
   * (map.variants[]) with their physical booths; variants without areas are listed too.
   */
  const productRows = useMemo(() => {
    const variantByEp = new Map((Array.isArray(boothMap?.variants) ? boothMap.variants : []).map((v) => [v.eventProductId, v]));
    const singles = [];
    const variantRows = new Map();
    booths.forEach((booth) => {
      const variant = variantByEp.get(booth.eventProductId);
      if (!variant) {
        singles.push({ kind: 'single', key: booth.eventProductId, booth });
        return;
      }
      if (!variantRows.has(booth.eventProductId)) {
        variantRows.set(booth.eventProductId, {
          kind: 'variant', key: booth.eventProductId, variant, booths: [], product: productsById[variant.productId] || null,
        });
      }
      variantRows.get(booth.eventProductId).booths.push({ ...booth, isVariantBooth: true });
    });
    variantByEp.forEach((variant, eventProductId) => {
      if (!variantRows.has(eventProductId)) {
        variantRows.set(eventProductId, {
          kind: 'variant', key: eventProductId, variant, booths: [], product: productsById[variant.productId] || null,
        });
      }
    });
    const variants = [...variantRows.values()]
      .map((row) => ({
        ...row,
        // EventProduct-level record for updateBoothEventProduct / remove
        entry: { eventProductId: row.variant.eventProductId, productId: row.variant.productId, boothNumber: row.variant.variant },
        booths: row.booths.sort((a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber)),
      }))
      .sort((a, b) => compareBoothNumbers(a.variant.variant, b.variant.variant));
    return [...singles, ...variants];
  }, [booths, boothMap, productsById]);
  // unassigned areas that can become booths; placeholder areas of shapes without booth number
  // (Lambda: unnamed: true, boothNumber '#<n>') are drawn on the plan but are never actionable
  const unassignedAreas = useMemo(
    () => (boothMap?.unassignedAreas || []).filter((a) => a && !a.unnamed && !isUnnamedBoothNumber(a.boothNumber)),
    [boothMap],
  );
  // areas with a variant are reported per code (VariantWarnings), the others per number
  const unassignedNumbers = unassignedAreas.filter((a) => !normalizeVariant(a.variant));
  const unnamedAreasCount = (boothMap?.unassignedAreas || []).length - unassignedAreas.length;
  const boothsWithoutArea = boothMap?.boothsWithoutArea || [];
  const counts = boothMap?.counts || { total: 0, free: 0, reserved: 0, sold: 0, unavailable: 0 };
  // booth EventProducts that are neither a single booth (number) nor a variant product (code)
  const numberless = useMemo(
    () => (eventBoothProducts || []).filter((ep) => !cleanBoothNumber(ep.product?.boothNumber)
      && !cleanVariantCode(ep.product?.boothVariant)
      && !isRuleOfferProduct(ep.product)),
    [eventBoothProducts],
  );

  /**
   * Saves Product.boothNumber of a booth of this event. Asks first when the number is used by another
   * booth product of this event, when a sold / reserved booth gets another number, or when a number is removed.
   * Resolves to true when saved.
   */
  const saveBoothNumber = ({
    productId, currentNumber, status, label, stock = null,
  }, input) => {
    const value = cleanBoothNumber(input);
    // lenient booth numbers (any scheme from the SVG): only a leading '#' is refused (reserved for unnamed shapes)
    const check = value ? checkBoothNumber(value) : null;
    if (check && !check.ok) {
      setNumberError(t('Booth numbers must not start with "#" (reserved for unnamed shapes).'));
      return Promise.resolve(false);
    }
    setNumberError(null);
    // without the EventProduct list (load failed) the booths of the map are the best available check
    const conflictSource = eventBoothProducts || booths.map((b) => ({
      id: b.eventProductId,
      product: { id: b.productId, boothNumber: b.boothNumber, name: b.name, sku: null },
    }));
    const conflicts = findBoothNumberConflicts(conflictSource, productId, value);
    const warnings = [];
    if (check && check.warnings.includes('id-whitespace')) {
      warnings.push(t('The booth number {{boothNumber}} contains spaces; it is matched with collapsed spaces and must be written exactly like that in the SVG.', { boothNumber: value }));
    }
    if (check && check.warnings.includes('id-too-long')) {
      warnings.push(t('The booth number {{boothNumber}} is longer than 32 characters.', { boothNumber: value }));
    }
    if (conflicts.length > 0) {
      warnings.push(t('Booth number {{boothNumber}} is already used in this event by: {{list}}. Both products would share one area on the hall plan.', {
        boothNumber: value,
        list: conflicts.map((ep) => ep.product.sku || getLocalizedText(ep.product.name) || ep.product.id).join(', '),
      }));
    }
    if (status && status !== BOOTH_STATUS.FREE && status !== BOOTH_STATUS.UNAVAILABLE) {
      warnings.push(t('Booth {{boothNumber}} is sold or reserved. Bookings and reservations keep the old booth number, so the booth can appear free on the hall plan afterwards.', {
        boothNumber: currentNumber,
      }));
    }
    if (!value && currentNumber) {
      warnings.push(t('Without a booth number the booth is no longer shown on the hall plan and cannot be matched to a plan version.'));
    }
    if (value && !currentNumber && Number(stock) > 1) {
      warnings.push(t('This product has a stock of {{stock}} in this event. A booth on the hall plan stands for exactly one physical booth: set the stock to 1 afterwards if this product is meant to be a single booth.', { stock }));
    }
    const run = async () => {
      const outcome = await runAction(
        'update-product-booth-number',
        () => actions.updateProductBoothNumber(productId, value, { allowDuplicate: conflicts.length > 0 }),
        {
          success: 'Booth number saved.',
          context: {
            productId, boothNumber: value, previous: currentNumber || null, label: label || null,
          },
          trackBusy: false,
        },
      );
      return outcome !== undefined;
    };
    if (warnings.length === 0) return run();
    return new Promise((resolve) => {
      setNumberConfirm({ warnings, run, resolve });
    });
  };

  const closeNumberConfirm = () => {
    if (numberConfirmBusy || !numberConfirm) return;
    numberConfirm.resolve(false);
    setNumberConfirm(null);
  };

  const confirmNumberChange = async () => {
    const pending = numberConfirm;
    if (!pending) return;
    setNumberConfirmBusy(true);
    const ok = await pending.run();
    setNumberConfirmBusy(false);
    setNumberConfirm(null);
    pending.resolve(ok);
  };

  const visibleRows = useMemo(() => {
    const q = normalizeBoothNumber(query);
    if (!q) return productRows;
    const lower = query.trim().toLowerCase();
    const boothMatches = (b) => normalizeBoothNumber(b.boothNumber).includes(q)
      || (b.exhibitor?.name || '').toLowerCase().includes(lower);
    return productRows.filter((row) => {
      if (row.kind === 'single') {
        return boothMatches(row.booth) || getLocalizedText(row.booth.name).toLowerCase().includes(lower);
      }
      const codes = [row.variant.variant, ...(Array.isArray(row.variant.aliases) ? row.variant.aliases : [])];
      return codes.some((code) => normalizeVariant(code).includes(q))
        || getLocalizedText(row.variant.name || row.product?.name).toLowerCase().includes(lower)
        || row.booths.some(boothMatches);
    });
  }, [productRows, query]);

  // row-level save: rows lock only the saved field, the rest of the tab stays usable
  const handleUpdate = (booth, updates) => runAction('update-event-product', () => actions.updateBoothEventProduct(booth.eventProductId, updates), {
    success: 'Booth saved.',
    context: { eventProductId: booth.eventProductId, updates },
    trackBusy: false,
  });

  const saveBoothBookable = (booth, bookable) => runAction(
    'update-booth-bookable',
    () => actions.updateAreaBookable({ id: booth.areaId, boothNumber: booth.boothNumber }, bookable, { live: true }),
    {
      success: bookable ? 'The booth is bookable again.' : 'The booth was taken off sale.',
      context: { eventProductId: booth.eventProductId, boothNumber: booth.boothNumber, bookable },
      trackBusy: false,
    },
  );

  const handleBoothBookable = (booth, bookable) => {
    if (!bookable && AFFECTED_STATUSES.includes(booth.status)) {
      setBookableConfirm({ booth, bookable });
      return;
    }
    saveBoothBookable(booth, bookable);
  };

  const removeCount = removeBooth?.kind === 'variant' ? (removeBooth.variant.total ?? removeBooth.booths.length) : 0;

  return (
    <MDBCard className="mb-4">
      <MDBCardBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <MDBCardTitle className="mb-1">{t('Booths in this event')}</MDBCardTitle>
            <div className="d-flex flex-wrap gap-2 small">
              <span>{t('{{count}} booths', { count: counts.total })}</span>
              <span className="booth-status-chip booth-status-chip--free">{t('{{count}} free', { count: counts.free })}</span>
              <span className="booth-status-chip booth-status-chip--reserved">{t('{{count}} reserved', { count: counts.reserved })}</span>
              <span className="booth-status-chip booth-status-chip--sold">{t('{{count}} sold', { count: counts.sold })}</span>
              <span className="booth-status-chip booth-status-chip--unavailable">{t('{{count}} not available', { count: counts.unavailable })}</span>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <MDBBtn size="sm" outline type="button" onClick={() => openWithoutError(setAddOpen)(true)} disabled={busy}>
              <MDBIcon fas icon="plus" className="me-2" />
              {t('Add existing booth products')}
            </MDBBtn>
            <MDBBtn size="sm" outline type="button" onClick={() => openWithoutError(setCreateOpen)(true)} disabled={busy || unassignedAreas.length === 0}>
              <MDBIcon fas icon="magic" className="me-2" />
              {t('Create offers from unassigned areas')}
            </MDBBtn>
            <MDBBtn
              size="sm"
              color="secondary"
              type="button"
              title={t('Refresh')}
              aria-label={t('Refresh')}
              onClick={() => {
                actions.loadMap();
                actions.loadEventBoothProducts();
              }}
              disabled={isLoading || eventProductsLoading}
            >
              {isLoading || eventProductsLoading ? <MDBSpinner size="sm" /> : <MDBIcon fas icon="sync-alt" />}
            </MDBBtn>
          </div>
        </div>

        {loadError && <MessageList messages={[loadError]} />}
        {numberError && <MessageList messages={[numberError]} />}

        <VariantWarnings boothMap={boothMap} areas={areas} eventBoothProducts={eventBoothProducts} hasLivePlan={hasLivePlan} />
        {boothsWithoutArea.length > 0 && (
          <div className="alert alert-warning small">
            {t('Booths without an area on the hall plan: {{list}}', { list: boothsWithoutArea.join(', ') })}
          </div>
        )}
        {unassignedNumbers.length > 0 && (
          <div className="alert alert-secondary small">
            {t('Areas without a booth in this event: {{list}}', { list: unassignedNumbers.map((a) => a.boothNumber).join(', ') })}
          </div>
        )}
        {unnamedAreasCount > 0 && (
          <div className="small text-muted mb-3">
            {t('{{count}} unnamed shapes on the hall plan are shown but not bookable (no booth number in the SVG).', { count: unnamedAreasCount })}
          </div>
        )}

        <div className="mb-3" style={{ maxWidth: '320px' }}>
          <MDBInput label={t('Search booth number, offer code, name or exhibitor')} size="sm" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>

        {isLoading && !boothMap && <MDBSpinner size="sm" />}
        {boothMap && productRows.length === 0 && (
          <p className="text-muted">{t('This event has no booth products with a booth number or offer code yet.')}</p>
        )}
        {visibleRows.length > 0 && (
          <div className="table-responsive">
            <MDBTable small hover align="middle" className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>{t('Booth')}</th>
                  <th>{t('Name')}</th>
                  <th>{t('m²')}</th>
                  <th>{t('Price')}</th>
                  <th>{t('Stock')}</th>
                  <th>{t('Bookable')}</th>
                  <th>{t('Status')}</th>
                  <th className="text-center">{t('Area')}</th>
                  <th aria-label={t('Actions')} />
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {visibleRows.map((row) => (row.kind === 'single' ? (
                  <BoothRow
                    key={row.key}
                    booth={row.booth}
                    eventProduct={eventProductById.get(row.booth.eventProductId) || null}
                    busy={busy}
                    hasLivePlan={hasLivePlan}
                    onUpdate={handleUpdate}
                    onSaveNumber={(booth, value) => saveBoothNumber({
                      productId: booth.productId,
                      currentNumber: booth.boothNumber,
                      status: booth.status,
                      label: getLocalizedText(booth.name),
                    }, value)}
                    onOption={openWithoutError(setOptionBooth)}
                    onReservations={openWithoutError(setReservationsBooth)}
                    onRemove={(booth) => setRemoveBooth({ kind: 'single', booth })}
                  />
                ) : (
                  <VariantRow
                    key={row.key}
                    row={{ ...row, eventProduct: eventProductById.get(row.variant.eventProductId) || null }}
                    busy={busy}
                    hasLivePlan={hasLivePlan}
                    onUpdate={handleUpdate}
                    onBoothOption={openWithoutError(setOptionBooth)}
                    onBoothRelease={openWithoutError(setReleaseBooth)}
                    onBoothReservations={openWithoutError(setReservationsBooth)}
                    onBoothBookable={handleBoothBookable}
                    onRemove={setRemoveBooth}
                  />
                )))}
              </MDBTableBody>
            </MDBTable>
          </div>
        )}

        <h6 className="mt-4">{t('Booth products of this event without a booth number')}</h6>
        <div className="small text-muted mb-2">
          {t('Only booths with a booth number appear on the hall plan and can be matched to a plan version.')}
          {' '}
          {t('The booth number is stored on the product and applies to every event that uses it.')}
          {' '}
          {t('Offers with a plan code or a match rule are not listed here: their booths come from the areas of the hall plan.')}
        </div>
        {eventProductsError && <MessageList messages={[eventProductsError]} />}
        {eventProductsLoading && !eventBoothProducts && <MDBSpinner size="sm" />}
        {eventBoothProducts && numberless.length === 0 && (
          <p className="small text-muted mb-0">{t('All booth products of this event have a booth number.')}</p>
        )}
        {numberless.length > 0 && (
          <div className="table-responsive">
            <MDBTable small hover align="middle" className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>{t('Name')}</th>
                  <th>{t('SKU')}</th>
                  <th>{t('m²')}</th>
                  <th>{t('Price')}</th>
                  <th>{t('Stock')}</th>
                  <th>{t('Booth number')}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {numberless.map((eventProduct) => (
                  <NumberlessBoothRow
                    key={eventProduct.id}
                    eventProduct={eventProduct}
                    busy={busy}
                    onSave={(value) => saveBoothNumber({
                      productId: eventProduct.product.id,
                      currentNumber: null,
                      status: null,
                      label: eventProduct.product.sku || getLocalizedText(eventProduct.product.name),
                      stock: eventProduct.stock,
                    }, value)}
                  />
                ))}
              </MDBTableBody>
            </MDBTable>
          </div>
        )}
      </MDBCardBody>

      <CompanyOptionModal
        booth={optionBooth}
        actionError={actionError}
        onClose={() => setOptionBooth(null)}
        searchCompanies={actions.searchCompanies}
        onCreate={async (booth, company) => {
          const outcome = await runAction('create-booth-option', () => actions.createOptionForCompany({ booth, companyId: company.id }), {
            success: 'Option created.',
            context: { eventProductId: booth.eventProductId, boothNumber: booth.boothNumber, companyId: company.id },
          });
          return outcome !== undefined;
        }}
      />
      <ReservationsModal
        booth={reservationsBooth}
        actionError={actionError}
        onClose={() => setReservationsBooth(null)}
        listReservations={actions.listBoothReservations}
        onDelete={async (booth, reservation) => {
          const outcome = await runAction('release-booth-reservation', () => actions.deleteReservation(reservation, booth), {
            success: 'Reservation released.',
            context: { eventProductId: booth.eventProductId, boothNumber: booth.boothNumber, reservationId: reservation.id },
          });
          return outcome !== undefined;
        }}
      />
      <AddProductsModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        eventProductIds={eventProductIds}
        eventBoothProducts={eventBoothProducts}
        areas={areas}
        loadBoothProducts={actions.loadBoothProducts}
        productsLoading={productsLoading}
        productsError={productsError}
        actionError={actionError}
        onAdd={(products) => runAction('add-booth-products', () => actions.addBoothProductsToEvent(products), {
          context: { count: products.length },
        })}
      />
      <CreateBoothsModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        unassignedAreas={unassignedAreas}
        areaMeta={areaMeta}
        unitsPerMeter={unitsPerMeter}
        eventProductIds={eventProductIds}
        loadBoothProducts={actions.loadBoothProducts}
        productsLoading={productsLoading}
        actionError={actionError}
        onCreate={(options) => runAction('create-booths-from-areas', () => actions.createBoothProducts(options), {
          context: { count: options.rows.length, templateId: options.templateId },
        })}
      />
      <ConfirmModal
        open={Boolean(releaseBooth)}
        title={t('Release hold or option')}
        message={releaseBooth
          ? t('Release the hold or option on booth {{boothNumber}}? The booth becomes free on the hall plan.', { boothNumber: releaseBooth.boothNumber })
          : ''}
        confirmLabel={t('Release')}
        busy={busy}
        onClose={() => setReleaseBooth(null)}
        onConfirm={async () => {
          const booth = releaseBooth;
          await runAction('release-booth-lock', () => actions.releaseBoothLock(booth), {
            success: 'Reservation released.',
            context: { eventProductId: booth.eventProductId, boothNumber: booth.boothNumber },
          });
          setReleaseBooth(null);
        }}
      />
      <ConfirmModal
        open={Boolean(bookableConfirm)}
        title={t('Booth is sold or reserved')}
        message={bookableConfirm
          ? t('Booth {{boothNumber}} is sold, held or optioned. Taking it off sale does not cancel bookings or reservations. Continue?', {
            boothNumber: bookableConfirm.booth.boothNumber,
          })
          : ''}
        confirmLabel={t('Change anyway')}
        busy={busy}
        onClose={() => setBookableConfirm(null)}
        onConfirm={async () => {
          const pending = bookableConfirm;
          setBookableConfirm(null);
          await saveBoothBookable(pending.booth, pending.bookable);
        }}
      />
      <ConfirmModal
        open={Boolean(removeBooth)}
        title={t('Remove from event')}
        message={removeBooth?.kind === 'variant' ? (
          <>
            <p className="mb-2">
              {t('Remove offer {{offer}} from this event? The booth product itself is kept.', { offer: removeBooth.variant.variant || removeBooth.product?.sku || '' })}
            </p>
            {removeCount > 0 && (
              <p className="mb-0 text-danger">
                {t('All {{count}} booths of this offer are removed from the booking with it.', { count: removeCount })}
              </p>
            )}
          </>
        ) : (removeBooth
          ? t('Remove booth {{boothNumber}} from this event? The booth product itself is kept.', { boothNumber: removeBooth.booth.boothNumber })
          : '')}
        confirmLabel={t('Remove')}
        busy={busy}
        onClose={() => setRemoveBooth(null)}
        onConfirm={async () => {
          const target = removeBooth;
          const eventProductId = target.kind === 'variant' ? target.variant.eventProductId : target.booth.eventProductId;
          await runAction('remove-booth-from-event', () => actions.removeBoothFromEvent(eventProductId), {
            success: 'Booth removed from the event.',
            context: { eventProductId, kind: target.kind },
          });
          setRemoveBooth(null);
        }}
      />
      <ConfirmModal
        open={Boolean(numberConfirm)}
        title={t('Change booth number')}
        message={numberConfirm ? (
          <>
            {numberConfirm.warnings.map((warning) => <p key={warning} className="mb-2">{warning}</p>)}
            <p className="mb-0">{t('Save the booth number anyway?')}</p>
          </>
        ) : ''}
        confirmLabel={t('Save anyway')}
        busy={numberConfirmBusy}
        onClose={closeNumberConfirm}
        onConfirm={confirmNumberChange}
      />
    </MDBCard>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

const HallPlanTab = ({ eventId, isActive }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const data = useEventBoothPlan(eventId, isActive);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [toast, setToast] = useState(null);

  /**
   * Runs an action of the hook: reports errors to Sentry and shows them above the tab.
   * Resolves to the action result, or undefined when it failed.
   * `success` must be a fixed English i18n key (shown via FlashToast).
   * `trackBusy: false` leaves the tab-wide busy flag alone (row-level saves track their own state).
   */
  const runAction = useCallback(async (operation, fn, { success, context = {}, trackBusy = true } = {}) => {
    if (trackBusy) setBusy(true);
    setActionError(null);
    try {
      const result = await fn();
      if (success) setToast(success);
      return result === undefined ? null : result;
    } catch (error) {
      captureError(error, {
        feature: FEATURE,
        operation,
        consoleMessage: `Hall plan action failed: ${operation}`,
        context: { eventId, ...context },
      });
      setActionError(errorMessage(error));
      return undefined;
    } finally {
      if (trackBusy) setBusy(false);
    }
  }, [eventId]);

  const clearActionError = useCallback(() => setActionError(null), []);

  const boothKeys = useMemo(
    () => new Set((data.boothMap?.booths || []).map((b) => normalizeBoothNumber(b.boothNumber))),
    [data.boothMap],
  );
  // products of this event's booth EventProducts: from the map (with number) and the EventProduct list (all)
  const eventProductIds = useMemo(() => new Set([
    ...(data.boothMap?.booths || []).map((b) => b.productId),
    ...(data.eventBoothProducts || []).map((ep) => ep.product?.id).filter(Boolean),
  ]), [data.boothMap, data.eventBoothProducts]);
  // scale of the live version (width / depth of variants created from unassigned areas)
  const liveUnitsPerMeter = useMemo(
    () => (data.versions || []).find((v) => v.id === data.activeVersionId)?.unitsPerMeter ?? null,
    [data.versions, data.activeVersionId],
  );

  if (!eventId) {
    return <div className="alert alert-info">{t('Save the event first to manage the hall plan.')}</div>;
  }
  if (!isActive) return null;

  const plan = data.event?.boothPlan || null;
  // plan and area writes need the loaded event (they rewrite Event.boothPlan)
  const eventReady = Boolean(data.event) && !data.loading.event;

  return (
    <>
      {actionError && (
        <div className="alert alert-danger d-flex justify-content-between align-items-start" role="alert">
          <span>{t('Action failed: {{message}}', { message: actionError })}</span>
          <MDBBtn className="btn-close" color="none" type="button" aria-label={t('Close')} onClick={() => setActionError(null)} />
        </div>
      )}

      <PlanImageCard
        plan={plan}
        planImageUrl={data.planImageUrl}
        isLoading={data.loading.event}
        loadError={data.errors.event}
        busy={busy || !eventReady}
        areasCount={data.areas.length}
        onReadDims={(file) => runAction('read-booth-plan-dimensions', () => data.readPlanFileDimensions(file), {
          context: { fileName: file.name },
        })}
        onUpload={(file, onProgress, options) => runAction('upload-booth-plan-image', () => data.uploadPlanImage(file, onProgress, options), {
          success: 'Hall plan image saved.',
          context: { fileName: file.name, keepDimensions: Boolean(options?.keepDimensions) },
        })}
        onRemove={() => runAction('remove-booth-plan', () => data.removePlanImage(), { success: 'Hall plan removed.' })}
        onSaveDims={(width, height) => runAction('save-booth-plan-dimensions', () => data.savePlanDimensions(width, height), {
          success: 'Coordinate space saved.',
          context: { width, height },
        })}
      />

      <VersionsCard
        eventId={eventId}
        versions={data.versions}
        activeVersionId={data.activeVersionId}
        liveAreas={data.loading.areas ? null : data.areas}
        boothMap={data.boothMap}
        isLoading={data.loading.versions}
        loadError={data.errors.versions}
        busy={busy || !eventReady}
        actionError={actionError}
        clearActionError={clearActionError}
        runAction={runAction}
        notify={setToast}
        uploadedBy={user?.email || user?.id || user?.username || null}
        loadVersions={data.loadVersions}
        loadVersionAreas={data.loadVersionAreas}
        analyzeHallPlanFile={data.analyzeHallPlanFile}
        saveHallPlanVersion={data.saveHallPlanVersion}
        previewCarryOver={data.previewCarryOver}
        activateVersion={data.activateVersion}
        deactivateVersion={data.deactivateVersion}
        deleteVersion={data.deleteVersion}
        previewVersionVariants={data.previewVersionVariants}
        applyVersionVariants={data.applyVersionVariants}
        eventProductIds={eventProductIds}
        eventBoothProducts={data.eventBoothProducts}
        productsLoading={data.loading.products}
        loadBoothProducts={data.loadBoothProducts}
        createBoothProducts={data.createBoothProducts}
        reloadBoothsDelayed={data.reloadBoothsDelayed}
      />

      <AreasCard
        eventId={eventId}
        areas={data.areas}
        activeVersionId={data.activeVersionId}
        isLoading={data.loading.areas}
        loadError={data.errors.areas}
        boothKeys={boothKeys}
        plan={plan}
        planImageUrl={data.planImageUrl}
        busy={busy || !eventReady}
        actionError={actionError}
        clearActionError={clearActionError}
        runAction={runAction}
        boothMap={data.boothMap}
        eventBoothProducts={data.eventBoothProducts}
        importAreas={data.importAreas}
        createArea={data.createArea}
        deleteArea={data.deleteArea}
        updateAreaVariant={data.updateAreaVariant}
        updateAreaFeatures={data.updateAreaFeatures}
        updateAreaBookable={data.updateAreaBookable}
        writeAreaVariants={data.writeAreaVariants}
        buildAreasExport={data.buildAreasExport}
      />

      <BoothsCard
        boothMap={data.boothMap}
        isLoading={data.loading.map}
        loadError={data.errors.map}
        busy={busy}
        actionError={actionError}
        clearActionError={clearActionError}
        runAction={runAction}
        areaMeta={data.areaMeta}
        areas={data.areas}
        unitsPerMeter={liveUnitsPerMeter}
        activeVersionId={data.activeVersionId}
        productsLoading={data.loading.products}
        productsError={data.errors.products}
        eventProductIds={eventProductIds}
        eventBoothProducts={data.eventBoothProducts}
        eventProductsLoading={data.loading.eventProducts}
        eventProductsError={data.errors.eventProducts}
        loadMap={data.loadMap}
        loadEventBoothProducts={data.loadEventBoothProducts}
        updateBoothEventProduct={data.updateBoothEventProduct}
        updateProductBoothNumber={data.updateProductBoothNumber}
        updateAreaBookable={data.updateAreaBookable}
        removeBoothFromEvent={data.removeBoothFromEvent}
        loadBoothProducts={data.loadBoothProducts}
        addBoothProductsToEvent={data.addBoothProductsToEvent}
        searchCompanies={data.searchCompanies}
        createOptionForCompany={data.createOptionForCompany}
        releaseBoothLock={data.releaseBoothLock}
        listBoothReservations={data.listBoothReservations}
        deleteReservation={data.deleteReservation}
        createBoothProducts={data.createBoothProducts}
      />

      <AssignmentMatrixCard
        areas={data.areas}
        eventBoothProducts={data.eventBoothProducts}
        isLoading={data.loading.areas || data.loading.eventProducts}
        busy={busy}
        actionError={actionError}
        clearActionError={clearActionError}
        eventProductIds={eventProductIds}
        loadBoothProducts={data.loadBoothProducts}
        productsLoading={data.loading.products}
        onCreate={(options) => runAction('create-rule-offers-from-features', () => data.createBoothProducts(options), {
          context: { count: options.rows.length },
        })}
      />

      <FlashToast message={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default HallPlanTab;
